import React, { useState, useEffect, useRef, useMemo, useDeferredValue, useCallback } from "react";
import { Container, Button, Modal, Form, Row, Col, Spinner, Badge, Alert } from "react-bootstrap";
import { collection, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc, setDoc, Timestamp, query, where, onSnapshot, writeBatch } from "firebase/firestore";
import { db, callFunction, callXeroFunction } from "../firebase.js";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";
import { uploadToCloudinary } from "../config/cloudinary.js";
import { useNavigate } from "react-router-dom";
import { format as fnsFormat, parseISO, startOfWeek, endOfWeek, format, addDays, addWeeks } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { Combobox } from "@headlessui/react";
import { useLoadScript } from "@react-google-maps/api";
import "./CalendarPage.css";
import {
  isFirestoreClient,
  getPaymentMethod,
  getDefaultPrice,
  getXeroContactId,
  getServiceDuration,
  parseClientChecklist
} from "../utils/clientHelpers.js";
import ChecklistGrouped from "../components/ChecklistGrouped.js";
import NoticeModal from "../components/NoticeModal.js";

// Google Maps libraries - defined outside component to avoid re-renders
const LIBRARIES = ["places"];

export default function CalendarPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const searchResultsRef = useRef(null);
  const datesSetTimeoutRef = useRef(null); // Debounce for calendar navigation

  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Separate team configurations for each team
  const [weeklyTeams, setWeeklyTeams] = useState({
    team1: {},
    team2: {},
    team3: {}
  }); // { team1: { Monday: [uid1, uid2], ... }, team2: { Monday: [...], ... }, team3: { Monday: [...], ... } }
  const [dailyTeamOverrides, setDailyTeamOverrides] = useState({
    team1: {},
    team2: {},
    team3: {}
  }); // { team1: { "2025-01-25": [uid1, uid2], ... }, team2: { ... }, team3: { ... } }
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTeam, setActiveTeam] = useState("team1"); // "team1" | "team2" | "team3"
  const [darkTheme, setDarkTheme] = useState(() => {
    // Load theme preference from localStorage, default to dark
    const saved = localStorage.getItem('calendarTheme');
    return saved === 'light' ? false : true;
  });

  // Dark mode props for all modals (Bootstrap 5.3+ native dark mode)
  const modalTheme = darkTheme ? "dark" : undefined;

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showDailyTeamModal, setShowDailyTeamModal] = useState(false);
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null); // For keyboard shortcuts
  const [selectedDate, setSelectedDate] = useState(null); // For daily team override
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit" | "view"
  const [pendingEventChange, setPendingEventChange] = useState(null); // Stores pending drag/resize/delete info

  // Team switching modal states
  const [showTeamSwitchModal, setShowTeamSwitchModal] = useState(false);
  const [teamSwitchData, setTeamSwitchData] = useState(null);
  const [showTeamPickerModal, setShowTeamPickerModal] = useState(false);
  const [pendingTeamSwitch, setPendingTeamSwitch] = useState(null); // Stores job and currentTeam while picking target

  // Lunch break custom times storage
  const [customLunchTimes, setCustomLunchTimes] = useState({}); // Stores job, oldTeam, newTeam, newDate, etc.

  // Invoice selection state
  const [selectedInvoices, setSelectedInvoices] = useState([]); // Array of job IDs
  const [sendingInvoices, setSendingInvoices] = useState(false);

  // Invoice review modal for batch sending
  const [showInvoiceReviewModal, setShowInvoiceReviewModal] = useState(false);
  const [reviewInvoices, setReviewInvoices] = useState([]); // Array of editable invoice data

  // Selection mode for invoices and confirmations
  const [selectionMode, setSelectionMode] = useState(null); // null | 'invoices' | 'confirmations'

  // Copy Week functionality
  const [showCopyWeekModal, setShowCopyWeekModal] = useState(false);
  const [copyWeekData, setCopyWeekData] = useState({ fromDate: "", toDate: "" });
  const [copyingWeek, setCopyingWeek] = useState(false);

  // Plan Full Day functionality
  const [showPlanDayModal, setShowPlanDayModal] = useState(false);
  const [planDayData, setPlanDayData] = useState({
    date: "",
    startTime: "",
    team: "team1",
    clients: [] // Array of { clientId, clientName, duration, intervalAfter }
  });
  const [creatingDayPlan, setCreatingDayPlan] = useState(false);

  // Track active checklist template for language switching
  const [activeChecklistTemplate, setActiveChecklistTemplate] = useState(null);

  // Restricted time warning modal
  const [showRestrictedTimeModal, setShowRestrictedTimeModal] = useState(false);
  const [restrictedTimeData, setRestrictedTimeData] = useState(null);

  // Calendar notices (avisos)
  const [notices, setNotices] = useState([]);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [noticeModalMode, setNoticeModalMode] = useState("create");
  const [selectedNotice, setSelectedNotice] = useState(null);

  // Edit client restricted times in job modal
  const [editingRestrictedTimes, setEditingRestrictedTimes] = useState(null); // Stores temp restrictedTimes being edited
  const [addingTimeWindow, setAddingTimeWindow] = useState(false); // Flag for adding new time window

  // Edit client data inline in job modal
  const [editingClientData, setEditingClientData] = useState(null); // Stores temp client data being edited
  const [savingClientData, setSavingClientData] = useState(false); // Loading state for saving client

  // Duplicate employee warning
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateEmployees, setDuplicateEmployees] = useState([]);
  const [pendingTeamSave, setPendingTeamSave] = useState(null); // Stores the save action to execute after confirmation

  // Plan Day - Client selection and new client
  const [planDayClientQuery, setPlanDayClientQuery] = useState("");
  const [showPlanDayNewClient, setShowPlanDayNewClient] = useState(false);
  const [planDayNewClient, setPlanDayNewClient] = useState({
    name: "",
    address: "",
    hours: "2:00",
    paymentMethod: "cash",
    defaultInvoiceAmount: ""
  });

  // Invoice amount change confirmation
  const [showAmountChangeModal, setShowAmountChangeModal] = useState(false);
  const [pendingAmountChange, setPendingAmountChange] = useState(null);

  // Password/Checklist update confirmation modal
  const [showPasswordChecklistModal, setShowPasswordChecklistModal] = useState(false);
  const [pendingPasswordChecklistChange, setPendingPasswordChecklistChange] = useState(null);

  // Individual invoice modal
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceFormData, setInvoiceFormData] = useState({
    description: "",
    amount: 0,
    notes: "",
    reference: ""
  });

  // Mobile list view states
  const [mobileViewMode, setMobileViewMode] = useState("list"); // "list" | "calendar"
  const [mobileViewType, setMobileViewType] = useState("day"); // "day" | "week"
  const [selectedMobileDate, setSelectedMobileDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });

  // Visible date range for dynamic lunch break generation
  const [visibleDateRange, setVisibleDateRange] = useState(() => {
    const today = new Date();
    const start = addDays(startOfWeek(today, { weekStartsOn: 1 }), -7); // 1 week before
    const end = addDays(startOfWeek(today, { weekStartsOn: 1 }), 42); // 6 weeks after
    return { start, end };
  });

  // Calculate dynamic max time based on events
  const calculateMaxTime = () => {
    const defaultMaxTime = "21:00:00"; // 9pm default

    if (!events || events.length === 0) {
      return defaultMaxTime;
    }

    // Find the latest end time among all events
    let latestHour = 21; // Default 9pm

    events.forEach(event => {
      if (event.end) {
        const endDate = new Date(event.end);
        const endHour = endDate.getHours();
        const endMinute = endDate.getMinutes();

        // If event ends after current latest, update
        if (endHour > latestHour || (endHour === latestHour && endMinute > 0)) {
          latestHour = endHour + (endMinute > 0 ? 1 : 0); // Round up to next hour
        }
      }
    });

    // Ensure minimum of 21:00 (9pm)
    latestHour = Math.max(latestHour, 21);

    // Cap at 26:00 (2am next day)
    latestHour = Math.min(latestHour, 26);

    return `${String(latestHour).padStart(2, '0')}:00:00`;
  };

  // Drag and drop states for mobile list
  const [draggedJob, setDraggedJob] = useState(null);
  const [showMoveJobModal, setShowMoveJobModal] = useState(false);
  const [moveJobData, setMoveJobData] = useState({
    job: null,
    newDate: "",
    newStartTime: "",
    newEndTime: ""
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragTimerRef = useRef(null);
  const swipeRef = useRef({ startX: 0, startY: 0 });
  // Refs for drag state (avoid stale closures in pointer event handlers)
  const draggedJobRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartPosRef = useRef({ x: 0, y: 0 });

  // Zoom states for mobile timeline
  const [zoomScale, setZoomScale] = useState(1.0); // 100% = default legivel
  const [lastPinchDistance, setLastPinchDistance] = useState(null);

  // WhatsApp Confirmation modal states
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsAppData, setWhatsAppData] = useState({
    startDate: "",
    endDate: "",
    messageType: "confirmation" // 'confirmation', 'end_of_year', 'last_minute_cancel'
  });
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [availableClients, setAvailableClients] = useState([]); // Clients with jobs in the period
  const [selectedClients, setSelectedClients] = useState([]); // Client phones to send to

  // Available message types for WhatsApp (default types)
  // Only 'confirmation' is truly fixed (isDefault: true)
  // end_of_year and last_minute_cancel can be edited from frontend
  const defaultWhatsAppMessageTypes = [
    { id: 'confirmation', name: language === "en" ? 'Standard Confirmation' : 'Confirmação Padrão', isDefault: true },
    {
      id: 'end_of_year',
      name: language === "en" ? 'End of Year Break' : 'Férias de Fim de Ano',
      isDefault: false,
      // Default prompt that can be edited
      promptText: `You are an assistant for It's So Clean, a professional cleaning company in New Zealand.

Client first name: {firstName}

Generate a friendly end-of-year message in English:
- Greet the client by their FIRST NAME ONLY (use "{firstName}")
- Inform them that the company will be taking a break for the holidays
- DO NOT mention any specific dates or times
- Ask them if they would like to PAUSE their service during the holiday period
- Ask when they would like to RESUME service after the break
- Wish them happy holidays
- Keep it professional but warm
- Maximum 4-5 sentences
- DO NOT use excessive emojis (max 2-3 total)

Example style: "Hi {firstName}! As the year comes to a close, we wanted to let you know that It's So Clean will be taking a short break for the holidays. Would you like to pause your service during this period? Just let us know when you'd like us to resume! Wishing you a wonderful holiday season!"`,
      mockMessage: `Hi {firstName}!\n\nAs the year comes to a close, we wanted to let you know that It's So Clean will be taking a short break for the holidays.\n\nWould you like to pause your service during this period? Just let us know when you'd like us to resume!\n\nWishing you a wonderful holiday season! 🎄`
    },
    {
      id: 'last_minute_cancel',
      name: language === "en" ? 'Last Minute Opening' : 'Horário Disponível (Cancelamento)',
      isDefault: false,
      // Default prompt that can be edited
      promptText: `You are an assistant for It's So Clean, a professional cleaning company in New Zealand.

Client first name: {firstName}
Available slot(s) due to another client's cancellation:
{appointmentList}

Generate a friendly message offering a last-minute opening in English:
- Greet the client by their FIRST NAME ONLY (use "{firstName}")
- Explain that another client had to cancel last minute
- Offer the now-available time slot(s) listed above
- Ask if they would like to take this slot for their cleaning
- Keep it casual and not pushy - it's an opportunity, not pressure
- Keep it professional but warm
- Maximum 3-4 sentences
- DO NOT use excessive emojis (max 1-2 total)

Example style: "Hi {firstName}! We just had a last-minute cancellation and have an opening on [date/time]. Would you be interested in scheduling your cleaning for this slot? Let us know if it works for you!"`,
      mockMessage: `Hi {firstName}!\n\nWe just had a last-minute cancellation and have an opening:\n{appointmentList}\n\nWould you be interested in taking this slot for your cleaning? Let us know if it works for you!`
    }
  ];

  // Custom prompts loaded from PostgreSQL
  const [customWhatsAppPrompts, setCustomWhatsAppPrompts] = useState([]);
  const [archivedWhatsAppPrompts, setArchivedWhatsAppPrompts] = useState([]);
  const [showArchivedPrompts, setShowArchivedPrompts] = useState(false);
  const [showPromptEditorModal, setShowPromptEditorModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [savingPrompt, setSavingPrompt] = useState(false);

  // Combined message types (defaults + custom, avoiding duplicates and archived)
  // If a default prompt (end_of_year, last_minute_cancel) has been customized in DB, use the DB version
  const activeCustomPrompts = customWhatsAppPrompts.filter(p => !p.archived);
  const whatsAppMessageTypes = [
    // First, add defaults - but replace with custom version if exists (and not archived)
    ...defaultWhatsAppMessageTypes.map(defaultPrompt => {
      const customVersion = activeCustomPrompts.find(c => c.id === defaultPrompt.id);
      if (customVersion) {
        // Use custom version but keep the default's isDefault flag for UI purposes
        return { ...customVersion, isDefault: defaultPrompt.isDefault };
      }
      return defaultPrompt;
    }),
    // Then add truly custom prompts (not overrides of defaults, and not archived)
    ...activeCustomPrompts.filter(p => !defaultWhatsAppMessageTypes.some(d => d.id === p.id))
  ];

  // Form states
  const [formData, setFormData] = useState({
    clientId: "",
    date: "",
    startTime: "",
    endTime: "",
    notes: "",
    password: "", // Entry password for the client location
    checklist: [],
    status: "scheduled",
    visitType: "regular", // "regular" | "first_clean" | "touchup"
    firstCleanExtra: 60, // extra minutes for first clean (editable)
    recurrence: "none", // "none" | "weekly" | "fortnightly" | "3weekly" | "monthly"
    repeatUntil: "indefinite", // "indefinite" | "date"
    repeatUntilDate: "", // YYYY-MM-DD if repeatUntil === "date"
    uploadedFiles: [],
    invoiceAmount: 0,
    timeModified: false,
    timeModificationReason: "",
    originalEndTime: "" // Store the original end time for comparison
  });

  // State para alerta de agendamentos expirando
  const [expiringAlertDismissed, setExpiringAlertDismissed] = useState(false);
  const [extendingSeriesLoading, setExtendingSeriesLoading] = useState(false);

  // Upload states
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadError, setUploadError] = useState("");

  // Team configuration state
  const [teamConfig, setTeamConfig] = useState({
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: []
  });

  const [newChecklistItem, setNewChecklistItem] = useState("");
  const [tempDailyTeam, setTempDailyTeam] = useState([]); // For daily team modal
  const [customPersonName, setCustomPersonName] = useState(""); // For adding custom person names
  const [savedCustomMembers, setSavedCustomMembers] = useState([]); // Saved custom members for quick selection
  const [savedCustomMembersLoaded, setSavedCustomMembersLoaded] = useState(false); // Track if custom members have loaded

  // Google Maps loader configuration
  const loaderOpts = useMemo(
    () => ({
      id: "google-map-script",
      googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY,
      libraries: LIBRARIES
    }),
    []
  );
  const { isLoaded, loadError } = useLoadScript(loaderOpts);

  // Client search and new client states
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [globalClientFilter, setGlobalClientFilter] = useState(""); // Global calendar filter (input value)
  const deferredFilter = useDeferredValue(globalClientFilter); // Deferred value for smooth UI
  const [showSearchResults, setShowSearchResults] = useState(false); // Show search results dropdown
  const [newClient, setNewClient] = useState({
    name: "",
    address: "",
    hours: "",
    email: "",
    phone: "",
    paymentMethod: "cash",
    defaultInvoiceAmount: "",
    addressValid: false,
    visible: false
  });
  const [addressService, setAddressService] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const addressDebounceRef = useRef(null);
  const addressSessionTokenRef = useRef(null);

  // Initialize Google Maps address service
  useEffect(() => {
    if (isLoaded && window.google && window.google.maps && window.google.maps.places) {
      setAddressService(new window.google.maps.places.AutocompleteService());
    }
  }, [isLoaded]);

  // Close search results dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearchResults) {
        // Check if click is outside the search area
        const searchContainer = event.target.closest('[data-search-container]');
        if (!searchContainer) {
          setShowSearchResults(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchResults]);

  // Real-time data listeners - accessible to all authenticated users
  useEffect(() => {
    if (!user) return;

    setLoading(true);
    const unsubscribers = [];
    let clientsLoaded = false;
    let jobsLoaded = false;

    const checkAndSetLoadingComplete = () => {
      if (clientsLoaded && jobsLoaded) {
        setLoading(false);
        console.log("✅ Both clients and jobs loaded");
      }
    };

    // Load clients from PostgreSQL via REST API
    const loadClients = async () => {
      try {
        const result = await callXeroFunction('getClients', {}, { method: 'GET' });
        const clientsData = result.clients || [];
        setClients(clientsData);
        console.log("📥 Clients loaded from PostgreSQL:", clientsData.length);
        console.log("📋 Sample client IDs:", clientsData.slice(0, 3).map(c => ({ id: c.id, type: typeof c.id, name: c.name })));
        clientsLoaded = true;
        checkAndSetLoadingComplete();
      } catch (error) {
        console.error("Error loading clients:", error);
        clientsLoaded = true; // Still mark as loaded to not block forever
        checkAndSetLoadingComplete();
      }
    };
    loadClients();

    // Real-time listener for employees
    const employeesUnsubscribe = onSnapshot(
      collection(db, "users"),
      (snapshot) => {
        const employeesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setEmployees(employeesData);
        console.log("📥 Employees updated in real-time");
      },
      (error) => {
        console.error("Error listening to employees:", error);
      }
    );
    unsubscribers.push(employeesUnsubscribe);

    // Real-time listener for weekly teams
    const weeklyTeamsUnsubscribe = onSnapshot(
      collection(db, "weeklyTeams"),
      (snapshot) => {
        if (!snapshot.empty) {
          const teamDoc = snapshot.docs[0];
          const loadedData = teamDoc.data();

          // Check if data is in new nested format or old flat format
          let weeklyTeamsData;
          if (loadedData.team1 || loadedData.team2 || loadedData.team3) {
            // New nested format
            weeklyTeamsData = {
              team1: loadedData.team1 || {
                Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
                Friday: [], Saturday: [], Sunday: []
              },
              team2: loadedData.team2 || {
                Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
                Friday: [], Saturday: [], Sunday: []
              },
              team3: loadedData.team3 || {
                Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
                Friday: [], Saturday: [], Sunday: []
              }
            };
          } else {
            // Old flat format - migrate to team1
            const completeConfig = {
              Monday: loadedData.Monday || [],
              Tuesday: loadedData.Tuesday || [],
              Wednesday: loadedData.Wednesday || [],
              Thursday: loadedData.Thursday || [],
              Friday: loadedData.Friday || [],
              Saturday: loadedData.Saturday || [],
              Sunday: loadedData.Sunday || []
            };
            weeklyTeamsData = {
              team1: completeConfig,
              team2: {
                Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
                Friday: [], Saturday: [], Sunday: []
              },
              team3: {
                Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
                Friday: [], Saturday: [], Sunday: []
              }
            };
          }

          setTeamConfig(weeklyTeamsData.team1);
          setWeeklyTeams(weeklyTeamsData);
          console.log("📥 Weekly teams updated in real-time");
        }
      },
      (error) => {
        console.error("Error listening to weekly teams:", error);
      }
    );
    unsubscribers.push(weeklyTeamsUnsubscribe);

    // Real-time listener for daily team overrides
    const dailyTeamUnsubscribe = onSnapshot(
      collection(db, "dailyTeamOverrides"),
      (snapshot) => {
        const dailyOverridesData = {
          team1: {},
          team2: {},
          team3: {}
        };

        snapshot.docs.forEach(doc => {
          const dateStr = doc.id;
          const data = doc.data();

          // Check if data is in new format (has team1/team2/team3 fields)
          if (data.team1 !== undefined || data.team2 !== undefined || data.team3 !== undefined) {
            // New format
            if (data.team1) {
              dailyOverridesData.team1[dateStr] = data.team1;
            }
            if (data.team2) {
              dailyOverridesData.team2[dateStr] = data.team2;
            }
            if (data.team3) {
              dailyOverridesData.team3[dateStr] = data.team3;
            }
          } else {
            // Old format - migrate to team1
            const teamMembers = data.teamMembers || [];
            if (teamMembers.length > 0) {
              dailyOverridesData.team1[dateStr] = teamMembers;
            }
          }
        });

        setDailyTeamOverrides(dailyOverridesData);
        console.log("📥 Daily team overrides updated in real-time");
      },
      (error) => {
        console.error("Error listening to daily team overrides:", error);
      }
    );
    unsubscribers.push(dailyTeamUnsubscribe);

    // Real-time listener for jobs
    const jobsUnsubscribe = onSnapshot(
      collection(db, "jobs"),
      (snapshot) => {
        const jobsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setJobs(jobsData);
        jobsLoaded = true;
        checkAndSetLoadingComplete();
        console.log("📥 Jobs updated in real-time");
      },
      (error) => {
        console.error("Error listening to jobs:", error);
        jobsLoaded = true; // Still mark as loaded to not block forever
        checkAndSetLoadingComplete();
      }
    );
    unsubscribers.push(jobsUnsubscribe);

    // Real-time listener for calendar notices
    const noticesUnsubscribe = onSnapshot(
      collection(db, "calendarNotices"),
      (snapshot) => {
        const noticesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setNotices(noticesData);
        console.log("📥 Calendar notices updated in real-time:", noticesData.length);
      },
      (error) => {
        console.error("Error listening to calendar notices:", error);
      }
    );
    unsubscribers.push(noticesUnsubscribe);

    // Real-time listener for saved custom members
    const customMembersUnsubscribe = onSnapshot(
      collection(db, "savedCustomMembers"),
      (snapshot) => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          const data = doc.data();
          setSavedCustomMembers(data.members || []);
          console.log("📥 Saved custom members updated in real-time");
        } else {
          setSavedCustomMembers([]);
        }
        setSavedCustomMembersLoaded(true); // Mark as loaded after first fetch
      },
      (error) => {
        console.error("Error listening to saved custom members:", error);
        setSavedCustomMembersLoaded(true); // Mark as loaded even on error to prevent blocking
      }
    );
    unsubscribers.push(customMembersUnsubscribe);

    // Load custom WhatsApp prompts from PostgreSQL via API
    const loadCustomPrompts = async () => {
      try {
        const result = await callXeroFunction('getWhatsappPrompts', {}, { method: 'GET' });
        if (result.prompts) {
          setCustomWhatsAppPrompts(result.prompts);
          console.log("📥 Custom WhatsApp prompts loaded from PostgreSQL:", result.prompts.length);
        }
      } catch (error) {
        console.error("Error loading custom WhatsApp prompts:", error);
      }
    };
    loadCustomPrompts();

    // Cleanup function to unsubscribe from all listeners
    return () => {
      console.log("🔌 Unsubscribing from real-time listeners");
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [user]);

  // Load custom lunch times from localStorage (migrate old clientId-team keys to date-team format)
  useEffect(() => {
    const saved = localStorage.getItem('customLunchTimes');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Remove old-format keys (clientId-team) that don't start with a date pattern (YYYY-MM-DD)
        const cleaned = {};
        Object.entries(parsed).forEach(([key, value]) => {
          if (/^\d{4}-\d{2}-\d{2}-/.test(key)) {
            cleaned[key] = value; // new format: date-team
          }
          // old format (clientId-team) is discarded
        });
        setCustomLunchTimes(cleaned);
        if (Object.keys(cleaned).length !== Object.keys(parsed).length) {
          localStorage.setItem('customLunchTimes', JSON.stringify(cleaned));
        }
      } catch (error) {
        console.error("Error loading custom lunch times:", error);
      }
    }
  }, []);

  // Handle window resize for responsive view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize tempDailyTeam when daily modal opens - clean invalid UIDs
  useEffect(() => {
    if (showDailyTeamModal && selectedDate) {
      const validIds = new Set(employees.map(e => e.id));
      const cleanUIDs = (arr) => (arr || []).filter(uid => uid.startsWith("custom:") || validIds.has(uid));
      const dayOfWeek = getDayOfWeek(selectedDate);
      const teamWeeklyConfig = weeklyTeams[activeTeam] || {};
      const teamDailyConfig = dailyTeamOverrides[activeTeam] || {};
      const weeklyDefault = cleanUIDs(teamWeeklyConfig[dayOfWeek]);
      const currentOverride = cleanUIDs(teamDailyConfig[selectedDate]);
      setTempDailyTeam(currentOverride.length > 0 ? currentOverride : weeklyDefault);
    }
  }, [showDailyTeamModal, selectedDate, weeklyTeams, dailyTeamOverrides, activeTeam, employees]);

  // Initialize teamConfig when team modal opens - clean invalid UIDs
  useEffect(() => {
    if (showTeamModal) {
      const validIds = new Set(employees.map(e => e.id));
      const raw = weeklyTeams[activeTeam] || {};
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      const cleaned = {};
      days.forEach(day => {
        cleaned[day] = (raw[day] || []).filter(uid => uid.startsWith("custom:") || validIds.has(uid));
      });
      setTeamConfig(cleaned);
    }
  }, [showTeamModal, activeTeam, weeklyTeams, employees]);

  // Keyboard shortcut for Delete key - DISABLED
  // Deletion now only happens through the modal to prevent accidental deletions
  // useEffect(() => {
  //   const handleKeyDown = (e) => {
  //     if ((e.key === "Delete" || e.key === "Backspace") && selectedEvent && !showModal) {
  //       e.preventDefault();
  //       handleDeleteEvent();
  //     }
  //   };
  //   window.addEventListener("keydown", handleKeyDown);
  //   return () => window.removeEventListener("keydown", handleKeyDown);
  // }, [selectedEvent, showModal]);

  // Auto-recalculate end time when date or start time changes
  useEffect(() => {
    if (formData.clientId && formData.date && formData.startTime) {
      const selectedClient = clients.find(c => String(c.id) === String(formData.clientId));
      const teamForDay = getTeamForDate(formData.date);

      const clientDuration = getServiceDuration(selectedClient);
      if (clientDuration && teamForDay.length > 0) {
        // Parse client hours (format: "HH:MM")
        const [hours, minutes] = clientDuration.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes;

        // Divide by number of people
        const minutesPerPerson = Math.round(totalMinutes / teamForDay.length);

        // Calculate end time from start time
        const [startHour, startMin] = formData.startTime.split(':').map(Number);
        const endTotalMinutes = startHour * 60 + startMin + minutesPerPerson;
        const endHour = Math.floor(endTotalMinutes / 60);
        const endMin = endTotalMinutes % 60;

        const newEndTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;

        // Only update if different to avoid infinite loop
        if (formData.endTime !== newEndTime) {
          setFormData(prev => ({
            ...prev,
            endTime: newEndTime
          }));
        }
      }
    }
  }, [formData.clientId, formData.date, formData.startTime, clients]);

  // Handle client selection from Combobox
  const handleClientSelect = (selectedOption) => {
    if (!selectedOption) return;

    // If user clicked "Adicionar", show new client form
    if (selectedOption.id === null) {
      setNewClient({
        name: clientSearchQuery,
        address: "",
        hours: "",
        email: "",
        phone: "",
        addressValid: false,
        visible: true
      });
      return;
    }

    // Regular client selection - auto-calculate time and set invoice amount
    const clientId = selectedOption.id;
    const client = findClientById(clientId);
    // Use default_price (PostgreSQL) or defaultInvoiceAmount (Firestore fallback)
    const invoiceAmount = getDefaultPrice(client);
    const password = client?.password || "";
    // Use default_checklist (PostgreSQL) or checklist (Firestore fallback)
    let checklist = [];
    try {
      // PostgreSQL returns JSONB as already-parsed array
      if (Array.isArray(client?.default_checklist)) {
        checklist = client.default_checklist;
      } else if (client?.default_checklist && typeof client.default_checklist === 'string') {
        checklist = JSON.parse(client.default_checklist);
      } else if (client?.checklist) {
        checklist = client.checklist;
      }
    } catch (e) {
      console.warn('Failed to parse checklist:', e);
      checklist = [];
    }
    const notes = client?.notes || "";

    // Touch-up: zero invoice amount
    const finalInvoiceAmount = formData.visitType === "touchup" ? 0 : invoiceAmount;
    setFormData({ ...formData, clientId, invoiceAmount: finalInvoiceAmount, password, checklist, notes });

    // Touch-up: don't auto-calculate end time (manual duration)
    if (formData.visitType === "touchup") return;

    if (clientId && formData.date) {
      const selectedClient = clients.find(c => String(c.id) === String(clientId));
      const teamForDay = getTeamForDate(formData.date);

      const clientDuration = getServiceDuration(selectedClient);
      if (clientDuration && teamForDay.length > 0) {
        // Parse client hours (format: "HH:MM")
        const [hours, minutes] = clientDuration.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes;

        // First clean: add extra buffer to total before dividing by team
        const adjustedMinutes = formData.visitType === "first_clean" ? totalMinutes + (parseInt(formData.firstCleanExtra) || 60) : totalMinutes;

        // Divide by number of people
        const minutesPerPerson = Math.round(adjustedMinutes / teamForDay.length);

        // Calculate end time from start time
        if (formData.startTime) {
          const [startHour, startMin] = formData.startTime.split(':').map(Number);
          const endTotalMinutes = startHour * 60 + startMin + minutesPerPerson;
          const endHour = Math.floor(endTotalMinutes / 60);
          const endMin = endTotalMinutes % 60;

          setFormData(prev => ({
            ...prev,
            clientId,
            endTime: `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`
          }));
        }
      }
    }
  };

  // Handle address autocomplete suggestions (com debounce 300ms + session token)
  const handleAddressChange = (val) => {
    setNewClient(n => ({
      ...n,
      address: val,
      addressValid: false
    }));

    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);

    if (addressService && val && val.length >= 3 && window.google && window.google.maps) {
      // Session token agrupa chamadas numa cobranca so
      if (!addressSessionTokenRef.current) {
        addressSessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }

      addressDebounceRef.current = setTimeout(() => {
        const aklBounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(-37.1458, 174.4976),
          new window.google.maps.LatLng(-36.6322, 175.2871)
        );

        addressService.getPlacePredictions(
          {
            input: val,
            componentRestrictions: { country: "nz" },
            locationRestriction: aklBounds,
            sessionToken: addressSessionTokenRef.current
          },
          (preds, status) => {
            setAddressSuggestions(
              status === "OK" ? preds : []
            );
          }
        );
      }, 300);
    } else {
      setAddressSuggestions([]);
    }
  };

  // Save new client to Firebase
  const handleSaveNewClient = async () => {
    // Validation
    if (!newClient.name.trim()) {
      alert(language === "en" ? "Please enter a client name" : "Por favor, insira o nome do cliente");
      return;
    }
    if (!newClient.address.trim() || !newClient.addressValid) {
      alert(language === "en" ? "Please select a valid address from suggestions" : "Por favor, selecione um endereço válido das sugestões");
      return;
    }
    if (!newClient.hours.trim()) {
      alert(language === "en" ? "Please enter hours (HH:MM)" : "Por favor, insira as horas (HH:MM)");
      return;
    }

    // Validate hours format
    const hoursPattern = /^\d{1,2}:\d{2}$/;
    if (!hoursPattern.test(newClient.hours)) {
      alert(language === "en" ? "Hours must be in HH:MM format" : "Horas devem estar no formato HH:MM");
      return;
    }

    try {
      // Save to PostgreSQL via REST API
      const paymentMethod = newClient.paymentMethod || "cash";
      const clientData = {
        name: newClient.name,
        address: newClient.address,
        service_duration: newClient.hours,
        email: newClient.email || "",
        phone: newClient.phone || "",
        payment_method: paymentMethod,
        default_price: paymentMethod === "xero" ? parseFloat(newClient.defaultInvoiceAmount) || 0 : 0,
        default_checklist: JSON.stringify([]),
        restricted_times: null,
        notes: null
      };

      const result = await callXeroFunction('createClient', clientData, { method: 'POST' });
      const savedClient = result.client;

      // Add to local clients list
      setClients([...clients, savedClient]);

      // Select the new client and calculate end time
      let updatedFormData = { ...formData, clientId: savedClient.id };

      // Auto-calculate time based on client hours and team size
      if (formData.date && formData.startTime) {
        const teamForDay = getTeamForDate(formData.date);

        const clientHours = getServiceDuration(savedClient);
        if (clientHours && teamForDay.length > 0) {
          // Parse client hours (format: "HH:MM")
          const [hours, minutes] = clientHours.split(':').map(Number);
          const totalMinutes = hours * 60 + minutes;

          // Divide by number of people
          const minutesPerPerson = Math.round(totalMinutes / teamForDay.length);

          // Calculate end time from start time
          const [startHour, startMin] = formData.startTime.split(':').map(Number);
          const endTotalMinutes = startHour * 60 + startMin + minutesPerPerson;
          const endHour = Math.floor(endTotalMinutes / 60);
          const endMin = endTotalMinutes % 60;

          updatedFormData = {
            ...updatedFormData,
            endTime: `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`
          };
        }
      }

      setFormData(updatedFormData);

      // Reset form
      setNewClient({
        name: "",
        address: "",
        hours: "",
        email: "",
        phone: "",
        paymentMethod: "cash",
        defaultInvoiceAmount: "",
        addressValid: false,
        visible: false
      });
      setClientSearchQuery("");

      alert(language === "en" ? "Client added successfully!" : "Cliente adicionado com sucesso!");
    } catch (error) {
      console.error("Error saving new client:", error);
      alert(language === "en" ? "Error saving client" : "Erro ao salvar cliente");
    }
  };

  // Save edited client data from job modal
  const handleSaveClientData = async () => {
    if (!editingClientData) return;

    // Validation
    if (!editingClientData.name?.trim()) {
      alert(language === "en" ? "Please enter a client name" : "Por favor, insira o nome do cliente");
      return;
    }
    if (!editingClientData.address?.trim()) {
      alert(language === "en" ? "Please enter an address" : "Por favor, insira um endereço");
      return;
    }
    if (!editingClientData.service_duration?.trim()) {
      alert(language === "en" ? "Please enter hours (HH:MM)" : "Por favor, insira as horas (HH:MM)");
      return;
    }

    // Validate hours format
    const hoursPattern = /^\d{1,2}:\d{2}$/;
    if (!hoursPattern.test(editingClientData.service_duration)) {
      alert(language === "en" ? "Hours must be in HH:MM format" : "Horas devem estar no formato HH:MM");
      return;
    }

    setSavingClientData(true);
    try {
      const clientData = {
        name: editingClientData.name.trim(),
        address: editingClientData.address.trim(),
        service_duration: editingClientData.service_duration,
        email: editingClientData.email?.trim() || "",
        phone: editingClientData.phone?.trim() || "",
        payment_method: editingClientData.payment_method || "cash",
        default_price: editingClientData.payment_method === "xero"
          ? parseFloat(editingClientData.default_price) || 0
          : 0
      };

      await callXeroFunction('updateClient', clientData, {
        method: 'PUT',
        id: editingClientData.id
      });

      // Update local clients list
      setClients(prev => prev.map(c =>
        c.id === editingClientData.id
          ? { ...c, ...clientData }
          : c
      ));

      setEditingClientData(null);
      alert(language === "en" ? "Client updated successfully!" : "Cliente atualizado com sucesso!");
    } catch (error) {
      console.error("Error updating client:", error);
      alert(language === "en" ? "Error updating client" : "Erro ao atualizar cliente");
    } finally {
      setSavingClientData(false);
    }
  };

  // NOTE: This function is now deprecated as real-time listeners handle data loading automatically
  // Kept for backwards compatibility but should not be called anymore
  const loadData = async () => {
    console.log("⚠️ loadData called but real-time listeners are active");
    return;

    /* Old implementation - no longer needed
    try {
      setLoading(true);

      // Load clients
      const clientsSnap = await getDocs(collection(db, "clients"));
      const clientsData = clientsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClients(clientsData);

      // Load employees (users)
      const usersSnap = await getDocs(collection(db, "users"));
      const employeesData = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEmployees(employeesData);

      // Load weekly team configuration
      const teamSnap = await getDocs(collection(db, "weeklyTeams"));
      if (!teamSnap.empty) {
        const teamDoc = teamSnap.docs[0];
        const loadedData = teamDoc.data();

        // Check if data is in new nested format or old flat format
        let weeklyTeamsData;
        if (loadedData.team1 || loadedData.team2 || loadedData.team3) {
          // New nested format
          weeklyTeamsData = {
            team1: loadedData.team1 || {
              Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
              Friday: [], Saturday: [], Sunday: []
            },
            team2: loadedData.team2 || {
              Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
              Friday: [], Saturday: [], Sunday: []
            },
            team3: loadedData.team3 || {
              Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
              Friday: [], Saturday: [], Sunday: []
            }
          };
        } else {
          // Old flat format - migrate to team1
          const completeConfig = {
            Monday: loadedData.Monday || [],
            Tuesday: loadedData.Tuesday || [],
            Wednesday: loadedData.Wednesday || [],
            Thursday: loadedData.Thursday || [],
            Friday: loadedData.Friday || [],
            Saturday: loadedData.Saturday || [],
            Sunday: loadedData.Sunday || []
          };
          weeklyTeamsData = {
            team1: completeConfig,
            team2: {
              Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
              Friday: [], Saturday: [], Sunday: []
            },
            team3: {
              Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
              Friday: [], Saturday: [], Sunday: []
            }
          };
        }

        setTeamConfig(weeklyTeamsData.team1); // teamConfig is for the modal, use team1 by default
        setWeeklyTeams(weeklyTeamsData);
      }

      // Load daily team overrides
      const dailyTeamSnap = await getDocs(collection(db, "dailyTeamOverrides"));
      const dailyOverridesData = {
        team1: {},
        team2: {},
        team3: {}
      };

      dailyTeamSnap.docs.forEach(doc => {
        const dateStr = doc.id;
        const data = doc.data();

        // Check if data is in new format (has team1/team2/team3 fields)
        if (data.team1 !== undefined || data.team2 !== undefined || data.team3 !== undefined) {
          // New format
          if (data.team1) {
            dailyOverridesData.team1[dateStr] = data.team1;
          }
          if (data.team2) {
            dailyOverridesData.team2[dateStr] = data.team2;
          }
          if (data.team3) {
            dailyOverridesData.team3[dateStr] = data.team3;
          }
        } else {
          // Old format - migrate to team1
          const teamMembers = data.teamMembers || [];
          if (teamMembers.length > 0) {
            dailyOverridesData.team1[dateStr] = teamMembers;
          }
        }
      });

      setDailyTeamOverrides(dailyOverridesData);

      // Load jobs
      const jobsSnap = await getDocs(collection(db, "jobs"));
      const jobsData = jobsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setJobs(jobsData);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
    */
  };

  // Reload only jobs (without loading spinner)
  // NOTE: This function is now deprecated as real-time listeners handle updates automatically
  // Kept for backwards compatibility but no longer needs to be called
  const reloadJobs = async () => {
    // Real-time listeners handle this automatically now
    console.log("⚠️ reloadJobs called but real-time listeners are active");
    return;

    /* Old implementation - no longer needed
    try {
      const jobsSnap = await getDocs(collection(db, "jobs"));
      const jobsData = jobsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setJobs(jobsData);
    } catch (error) {
      console.error("Error reloading jobs:", error);
    }
    */
  };

  // Get day of week from date
  const getDayOfWeek = (dateString) => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    // Parse date in local timezone to avoid UTC conversion issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return days[date.getDay()];
  };

  // Get team for a specific date (checks override first, then falls back to weekly default)
  const getTeamForDate = (dateString, team = activeTeam) => {
    // Get the team-specific configurations
    const teamDailyOverrides = dailyTeamOverrides[team] || {};
    const teamWeeklyTeams = weeklyTeams[team] || {};

    let teamMembers = [];

    // Check if there's a daily override for this specific date
    if (teamDailyOverrides[dateString]) {
      teamMembers = teamDailyOverrides[dateString];
    } else {
      // Otherwise, use weekly default
      const dayOfWeek = getDayOfWeek(dateString);
      teamMembers = teamWeeklyTeams[dayOfWeek] || [];
    }

    // Filter out members that no longer exist in employees list
    // Custom members must exist in savedCustomMembers to be kept
    const validEmployeeIds = employees.map(e => e.id);
    const validCustomNames = savedCustomMembers.map(m => m.name || m);
    const filteredTeam = teamMembers.filter(uid => {
      if (uid.startsWith("custom:")) {
        return validCustomNames.includes(uid.replace("custom:", ""));
      }
      return validEmployeeIds.includes(uid);
    });

    return filteredTeam;
  };

  // Lunch break configuration (12:00 - 12:20)
  const LUNCH_START = "12:00";
  const LUNCH_END = "12:20";

  // Check if a time range conflicts with lunch break
  const conflictsWithLunch = (startTime, endTime) => {
    const start = parseTime(startTime);
    const end = parseTime(endTime);
    const lunchStart = parseTime(LUNCH_START);
    const lunchEnd = parseTime(LUNCH_END);

    // Check if job overlaps with lunch break
    return (start < lunchEnd && end > lunchStart);
  };

  // Adjust lunch break to avoid job conflict
  // Returns the adjusted lunch time based on which side is closer to original 12:00
  const adjustForLunchBreak = (startTime, endTime) => {
    if (!conflictsWithLunch(startTime, endTime)) {
      return { startTime, endTime }; // No conflict
    }

    const start = parseTime(startTime);
    const end = parseTime(endTime);
    const lunchDuration = 20; // 20 minutes
    const originalLunchStart = parseTime(LUNCH_START); // 12:00 = 720 minutes

    // Option 1: Move lunch to END before job starts
    const lunchBeforeJob = start - lunchDuration;

    // Option 2: Move lunch to START after job ends
    const lunchAfterJob = end;

    // Calculate distance from original lunch time (12:00)
    const distanceBefore = Math.abs(lunchBeforeJob - originalLunchStart);
    const distanceAfter = Math.abs(lunchAfterJob - originalLunchStart);

    // Choose the option closest to original 12:00
    if (distanceBefore <= distanceAfter) {
      // Move lunch before the job
      const newLunchStart = lunchBeforeJob;
      const newLunchEnd = start;
      return {
        startTime: formatTime(newLunchStart),
        endTime: formatTime(newLunchEnd)
      };
    } else {
      // Move lunch after the job
      const newLunchStart = end;
      const newLunchEnd = end + lunchDuration;
      return {
        startTime: formatTime(newLunchStart),
        endTime: formatTime(newLunchEnd)
      };
    }
  };

  // Helper: parse time string to minutes
  const parseTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Indexed client Maps for O(1) lookup (performance optimization)
  const clientMaps = useMemo(() => {
    const byId = new Map();
    const byFirebaseId = new Map();
    const byName = new Map();
    const byFirstName = new Map();

    clients.forEach(c => {
      if (c.id) byId.set(String(c.id), c);
      if (c.firebase_id) byFirebaseId.set(c.firebase_id, c);
      if (c.name) {
        byName.set(c.name, c);
        const firstName = c.name.split(' ')[0].toLowerCase();
        // Only store first occurrence to avoid overwriting
        if (!byFirstName.has(firstName)) byFirstName.set(firstName, c);
      }
    });

    return { byId, byFirebaseId, byName, byFirstName };
  }, [clients]);

  // ========== SISTEMA DE RECORRÊNCIA MELHORADO ==========

  // Helper: calcular quantas ocorrências baseado na frequência e data limite
  const calcularOcorrencias = (startDate, recurrence, repeatUntil, repeatUntilDate) => {
    if (!startDate || recurrence === "none") return 0;

    const start = new Date(startDate + "T00:00:00");
    let endDate;

    if (repeatUntil === "indefinite" || !repeatUntil) {
      // 2 anos a partir da data inicial
      endDate = new Date(start);
      endDate.setFullYear(endDate.getFullYear() + 2);
    } else if (repeatUntilDate) {
      endDate = new Date(repeatUntilDate + "T23:59:59");
    } else {
      return 0;
    }

    const diffDays = Math.ceil((endDate - start) / (1000 * 60 * 60 * 24));

    switch (recurrence) {
      case "weekly":      return Math.floor(diffDays / 7);
      case "fortnightly": return Math.floor(diffDays / 14);
      case "3weekly":     return Math.floor(diffDays / 21);
      case "monthly":
        const months = (endDate.getFullYear() - start.getFullYear()) * 12
                     + (endDate.getMonth() - start.getMonth());
        return Math.max(0, months);
      default: return 0;
    }
  };

  // Helper: calcular a última data da série
  const calcularUltimaData = (startDate, recurrence, repeatUntil, repeatUntilDate) => {
    const ocorrencias = calcularOcorrencias(startDate, recurrence, repeatUntil, repeatUntilDate);
    if (ocorrencias <= 0 || !startDate) return null;

    const start = new Date(startDate + "T00:00:00");
    const ultima = new Date(start);

    switch (recurrence) {
      case "weekly":      ultima.setDate(start.getDate() + (ocorrencias * 7)); break;
      case "fortnightly": ultima.setDate(start.getDate() + (ocorrencias * 14)); break;
      case "3weekly":     ultima.setDate(start.getDate() + (ocorrencias * 21)); break;
      case "monthly":     ultima.setMonth(start.getMonth() + ocorrencias); break;
      default: return null;
    }

    return ultima;
  };

  // Helper: find client with backwards compatibility for legacy formats (O(1) lookup)
  const findClientById = (clientId, clientName = null) => {
    if (!clientId) return null;

    const { byId, byFirebaseId, byName, byFirstName } = clientMaps;

    // 1. Try matching by numeric/string ID (new format)
    let client = byId.get(String(clientId));

    // 2. Try matching by firebase_id (old Firestore document ID format)
    if (!client && typeof clientId === 'string' && clientId.length > 10) {
      client = byFirebaseId.get(clientId);
    }

    // 3. Try matching by exact name (legacy format where clientId was actually the name)
    if (!client && typeof clientId === 'string') {
      client = byName.get(clientId);
    }

    // 4. PRIORITY: Try clientName if provided (most reliable for legacy data)
    if (!client && clientName) {
      client = byName.get(clientName);
    }

    // 5. FALLBACK: Try matching by first name only as last resort (may be ambiguous)
    if (!client && typeof clientId === 'string' && clientId.length < 20) {
      const searchName = clientId.toLowerCase().trim();
      client = byFirstName.get(searchName);
    }

    return client;
  };

  // ========== SISTEMA DE RECORRÊNCIA (movido após findClientById) ==========

  // Hook: detectar clientes com jobs recorrentes acabando (< 30 dias até o último)
  // Agrupa por cliente (usando clientId OU clientName) - considera TODAS as séries
  const expiringRecurrences = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const limite = new Date(hoje);
    limite.setDate(limite.getDate() + 30); // 30 dias à frente

    // Primeiro, criar um mapa de clientId -> nome do cliente para normalização
    const clientIdToName = {};
    jobs.forEach(job => {
      if (job.clientId) {
        const client = findClientById(job.clientId, job.clientName);
        if (client?.name) {
          clientIdToName[String(job.clientId)] = client.name.toLowerCase().trim();
        } else if (job.clientName) {
          clientIdToName[String(job.clientId)] = job.clientName.toLowerCase().trim();
        }
      }
    });

    // Agrupar jobs por nome do cliente (mais confiável que clientId)
    const clientMap = {};
    jobs.forEach(job => {
      if (job.recurrence && job.recurrence !== "none") {
        // Tentar pegar o nome do cliente para agrupar
        let clientKey = null;

        if (job.clientId) {
          clientKey = clientIdToName[String(job.clientId)];
        }
        if (!clientKey && job.clientName) {
          clientKey = job.clientName.toLowerCase().trim();
        }
        if (!clientKey) return; // Sem identificação do cliente

        if (!clientMap[clientKey]) {
          clientMap[clientKey] = [];
        }
        clientMap[clientKey].push(job);
      }
    });

    // Encontrar clientes com último job dentro de 30 dias
    const expirando = [];

    Object.entries(clientMap).forEach(([clientId, clientJobs]) => {
      // Filtrar apenas jobs futuros ou de hoje
      const futureJobs = clientJobs.filter(job => {
        const jobDate = new Date(job.date + "T23:59:59");
        return jobDate >= hoje;
      });

      if (futureJobs.length === 0) return;

      // Ordenar por data e pegar o último (mais distante no futuro)
      const sortedJobs = [...futureJobs].sort((a, b) =>
        b.date.localeCompare(a.date)
      );
      const ultimoJob = sortedJobs[0];
      const ultimaData = new Date(ultimoJob.date + "T23:59:59");

      // Se último job está nos próximos 30 dias
      if (ultimaData <= limite) {
        const diasRestantes = Math.ceil((ultimaData - hoje) / (1000 * 60 * 60 * 24));
        const client = findClientById(ultimoJob.clientId, ultimoJob.clientName);

        expirando.push({
          seriesId: ultimoJob.seriesId || clientId,
          clientName: client?.name || ultimoJob.clientName || "Cliente",
          clientId: ultimoJob.clientId,
          recurrence: ultimoJob.recurrence,
          ultimaData: ultimoJob.date,
          diasRestantes,
          ultimoJob,
          totalJobs: futureJobs.length
        });
      }
    });

    // Ordenar por mais urgente primeiro
    return expirando.sort((a, b) => a.diasRestantes - b.diasRestantes);

  }, [jobs, clients, findClientById]);

  // Mapa de últimos jobs por cliente (para indicador visual ⚠️)
  // Agrupa por nome do cliente - considera TODAS as séries
  const lastJobBySeriesId = useMemo(() => {
    const map = new Map();

    // Primeiro, criar um mapa de clientId -> nome do cliente para normalização
    const clientIdToName = {};
    jobs.forEach(job => {
      if (job.clientId) {
        const client = findClientById(job.clientId, job.clientName);
        if (client?.name) {
          clientIdToName[String(job.clientId)] = client.name.toLowerCase().trim();
        } else if (job.clientName) {
          clientIdToName[String(job.clientId)] = job.clientName.toLowerCase().trim();
        }
      }
    });

    // Agrupar jobs por nome do cliente
    const clientMap = {};
    jobs.forEach(job => {
      if (job.recurrence && job.recurrence !== "none") {
        let clientKey = null;

        if (job.clientId) {
          clientKey = clientIdToName[String(job.clientId)];
        }
        if (!clientKey && job.clientName) {
          clientKey = job.clientName.toLowerCase().trim();
        }
        if (!clientKey) return;

        if (!clientMap[clientKey]) {
          clientMap[clientKey] = [];
        }
        clientMap[clientKey].push(job);
      }
    });

    Object.entries(clientMap).forEach(([clientName, clientJobs]) => {
      // Filtrar apenas jobs futuros ou de hoje
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      const futureJobs = clientJobs.filter(job => {
        const jobDate = new Date(job.date + "T23:59:59");
        return jobDate >= hoje;
      });

      if (futureJobs.length === 0) return;

      // Ordenar por data e pegar o último (mais distante no futuro)
      const sorted = [...futureJobs].sort((a, b) => b.date.localeCompare(a.date));
      if (sorted[0]) {
        map.set(sorted[0].id, true); // Marca o ID do último job
      }
    });

    return map;
  }, [jobs, findClientById]);

  // Função para estender série de jobs recorrentes
  const extendRecurringSeries = async () => {
    if (!formData.clientId || !formData.recurrence || formData.recurrence === "none") {
      alert(language === "en" ? "No recurring series to extend" : "Nenhuma série recorrente para estender");
      return;
    }

    setExtendingSeriesLoading(true);

    try {
      // Buscar todos os jobs recorrentes deste cliente com a mesma frequência
      const clientJobs = jobs.filter(j =>
        String(j.clientId) === String(formData.clientId) &&
        j.recurrence === formData.recurrence
      );

      if (clientJobs.length === 0) {
        alert(language === "en" ? "No existing jobs found" : "Nenhum job existente encontrado");
        setExtendingSeriesLoading(false);
        return;
      }

      // Filtrar apenas jobs futuros e ordenar para pegar o último
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const futureJobs = clientJobs.filter(j => {
        const jobDate = new Date(j.date + "T23:59:59");
        return jobDate >= hoje;
      });

      if (futureJobs.length === 0) {
        alert(language === "en" ? "No future jobs found to extend from" : "Nenhum job futuro encontrado para estender");
        setExtendingSeriesLoading(false);
        return;
      }

      // Pegar o último job da série
      const sorted = [...futureJobs].sort((a, b) => b.date.localeCompare(a.date));
      const ultimoJob = sorted[0];

      // Buscar o cliente para pegar o nome
      const client = findClientById(ultimoJob.clientId, ultimoJob.clientName);
      const clientName = client?.name || ultimoJob.clientName || "";

      // Calcular data limite baseada em repeatUntil
      let dataLimite;
      if (formData.repeatUntil === "date" && formData.repeatUntilDate) {
        dataLimite = new Date(formData.repeatUntilDate + "T23:59:59");
      } else {
        // Indefinido = 2 anos a partir de hoje
        dataLimite = new Date();
        dataLimite.setFullYear(dataLimite.getFullYear() + 2);
      }

      // Gerar novos jobs a partir do último
      const novosJobs = [];
      let currentDate = new Date(ultimoJob.date + "T00:00:00");
      let iteracao = 0;
      const maxIteracoes = 200;

      while (iteracao < maxIteracoes) {
        iteracao++;

        // Calcular próxima data baseada na recorrência
        const nextDate = new Date(currentDate);
        switch (formData.recurrence) {
          case "weekly":      nextDate.setDate(nextDate.getDate() + 7); break;
          case "fortnightly": nextDate.setDate(nextDate.getDate() + 14); break;
          case "3weekly":     nextDate.setDate(nextDate.getDate() + 21); break;
          case "monthly":     nextDate.setMonth(nextDate.getMonth() + 1); break;
          default: break;
        }

        currentDate = nextDate;

        if (currentDate > dataLimite) break;

        // Formatar data
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, "0");
        const day = String(currentDate.getDate()).padStart(2, "0");
        const formattedDate = `${year}-${month}-${day}`;

        // Verificar se já existe um job nessa data para esse cliente
        const jaExiste = jobs.some(j =>
          String(j.clientId) === String(formData.clientId) &&
          j.date === formattedDate
        );

        if (jaExiste) continue;

        novosJobs.push({
          clientId: ultimoJob.clientId,
          clientName: clientName,
          date: formattedDate,
          startTime: ultimoJob.startTime,
          endTime: ultimoJob.endTime,
          notes: ultimoJob.notes || "",
          password: ultimoJob.password || "",
          checklist: ultimoJob.checklist || [],
          status: "scheduled",
          recurrence: formData.recurrence,
          seriesId: ultimoJob.seriesId || `series_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          team: ultimoJob.team || activeTeam,
          invoiceAmount: ultimoJob.invoiceAmount || 0,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      }

      if (novosJobs.length === 0) {
        alert(language === "en"
          ? "No new jobs to create - series already extends to the selected date"
          : "Nenhum job novo para criar - série já vai até a data selecionada");
        setExtendingSeriesLoading(false);
        return;
      }

      // Salvar todos os novos jobs
      const promises = novosJobs.map(job => addDoc(collection(db, "jobs"), job));
      await Promise.all(promises);

      alert(language === "en"
        ? `✅ Extended! ${novosJobs.length} new jobs created for ${clientName || 'client'}`
        : `✅ Estendido! ${novosJobs.length} novos jobs criados para ${clientName || 'cliente'}`);

      // Recarregar jobs
      await reloadJobs();

    } catch (error) {
      console.error("Error extending series:", error);
      alert(language === "en" ? "Error extending series" : "Erro ao estender série");
    } finally {
      setExtendingSeriesLoading(false);
    }
  };

  // ========== FIM SISTEMA DE RECORRÊNCIA ==========

  // Helper: format minutes to time string
  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Calculate end time based on client hours, team size, and start time
  const calculateEndTime = (clientId, date, startTime, newWeeklyTeams = null, newDailyOverrides = null, team = null, clientName = null, visitType = "regular", firstCleanExtra = 60) => {
    // Use findClientById for robust client matching (supports legacy formats)
    const client = findClientById(clientId, clientName);
    // Use service_duration (PostgreSQL) or hours (Firestore fallback)
    const clientDuration = getServiceDuration(client);
    if (!clientDuration) return null;

    // Determine which team to use
    const teamToUse = team || activeTeam;

    // Use provided teams or fall back to current state
    const teamsToUse = newWeeklyTeams || weeklyTeams;
    const overridesToUse = newDailyOverrides || dailyTeamOverrides;

    // Get team-specific configurations
    const teamWeeklyConfig = teamsToUse[teamToUse] || {};
    const teamDailyConfig = overridesToUse[teamToUse] || {};

    // Get team for this date
    let teamForDay;
    if (teamDailyConfig[date]) {
      teamForDay = teamDailyConfig[date];
    } else {
      const dayOfWeek = getDayOfWeek(date);
      teamForDay = teamWeeklyConfig[dayOfWeek] || [];
    }

    // Filter out deleted members; custom members must exist in savedCustomMembers
    const validEmployeeIds = employees.map(e => e.id);
    const validCustomNames = savedCustomMembers.map(m => m.name || m);
    teamForDay = teamForDay.filter(uid => {
      if (uid.startsWith("custom:")) {
        return validCustomNames.includes(uid.replace("custom:", ""));
      }
      return validEmployeeIds.includes(uid);
    });

    if (teamForDay.length === 0) return null;

    // Parse client hours (format: "HH:MM")
    const [hours, minutes] = clientDuration.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;

    // First clean: add extra buffer to total before dividing by team
    const adjustedMinutes = visitType === "first_clean" ? totalMinutes + (parseInt(firstCleanExtra) || 60) : totalMinutes;

    // Divide by number of people
    const minutesPerPerson = Math.round(adjustedMinutes / teamForDay.length);

    // Calculate end time from start time
    const [startHour, startMin] = startTime.split(':').map(Number);
    const endTotalMinutes = startHour * 60 + startMin + minutesPerPerson;
    const endHour = Math.floor(endTotalMinutes / 60);
    const endMin = endTotalMinutes % 60;

    const endTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;

    console.log(`💡 calculateEndTime: ${date} | Team: ${teamForDay.length} people | Client hours: ${clientDuration} | Per person: ${minutesPerPerson}min | Result: ${startTime} → ${endTime}`);

    return endTime;
  };

  // Recalculate all jobs for specific dates when team changes
  const recalculateJobsForDates = async (dates, newWeeklyTeams = null, newDailyOverrides = null) => {
    console.log(`🔄 Recalculating jobs for dates:`, dates);

    // Find all jobs on these dates
    const jobsToUpdate = jobs.filter(job => dates.includes(job.date));

    if (jobsToUpdate.length === 0) {
      console.log("No jobs found for these dates");
      return;
    }

    console.log(`Found ${jobsToUpdate.length} jobs to recalculate`);

    // Recalculate each job - passing new team values directly!
    const updatePromises = jobsToUpdate.map(async (job) => {
      // Pass the job's team to calculateEndTime so it uses the correct team's configuration
      const jobTeam = job.team || "team1";
      // Pass clientName for robust client matching (supports legacy formats)
      const newEndTime = calculateEndTime(job.clientId, job.date, job.startTime, newWeeklyTeams, newDailyOverrides, jobTeam, job.clientName);

      if (newEndTime && newEndTime !== job.endTime) {
        console.log(`🧠 Updating job ${job.id} on ${job.date}: ${job.startTime} → ${newEndTime} (was ${job.endTime})`);

        await updateDoc(doc(db, "jobs", job.id), {
          endTime: newEndTime,
          updatedAt: Timestamp.now()
        });

        return { ...job, endTime: newEndTime };
      }

      return job;
    });

    const updatedJobs = await Promise.all(updatePromises);

    // Update local state
    setJobs(prevJobs =>
      prevJobs.map(j => {
        const updated = updatedJobs.find(uj => uj.id === j.id);
        return updated || j;
      })
    );

    console.log(`✅ Recalculation complete for ${jobsToUpdate.length} jobs`);
  };

  // Convert jobs to FullCalendar events - filter by active team
  const jobEvents = jobs
    .filter(job => (job.team || "team1") === activeTeam) // Default to team1 for old jobs
    .filter(job => {
      // Collaborators cannot see jobs starting after 6pm (18:00)
      if (!user?.isAdmin && !user?.isClientManager) {
        const startHour = parseInt(job.startTime.split(':')[0], 10);
        return startHour < 18;
      }
      return true; // Admins and managers see all jobs
    })
    .filter(job => {
      // Global client filter - use findClientById for legacy job compatibility
      if (globalClientFilter.trim()) {
        const client = findClientById(job.clientId, job.clientName);
        if (!client) return false;
        return client.name.toLowerCase().includes(globalClientFilter.toLowerCase());
      }
      return true;
    })
    .map(job => {
      // Use findClientById for backwards compatibility with legacy job formats
      const client = findClientById(job.clientId, job.clientName);

      const teamForDay = getTeamForDate(job.date);
      const teamNames = teamForDay.map(uid => {
        // Check if it's a custom name (starts with "custom:")
        if (uid.startsWith("custom:")) {
          return uid.substring(7); // Remove "custom:" prefix
        }
        // Otherwise, it's an employee UID
        const emp = employees.find(e => e.id === uid);
        return emp?.displayName?.split(" ")[0] || "";
      }).filter(n => n).join(", ");

    // Determine color based on status, time modification, and recurrence
    let backgroundColor, borderColor;
    if (job.status === "completed") {
      backgroundColor = "#22c55e"; // Green - completed
      borderColor = "#16a34a";
    } else if (job.status === "cancelled") {
      backgroundColor = "#ef4444"; // Red - cancelled
      borderColor = "#dc2626";
    } else {
      // Color by recurrence type (use originalRecurrence if recurrence is "none")
      const recurrenceType = job.recurrence === "none" && job.originalRecurrence
        ? job.originalRecurrence
        : job.recurrence;

      switch (recurrenceType) {
        case "weekly":
          backgroundColor = "#f59e0b"; // Amber
          borderColor = "#d97706";
          break;
        case "fortnightly":
          backgroundColor = "#8b5cf6"; // Purple
          borderColor = "#7c3aed";
          break;
        case "3weekly":
          backgroundColor = "#64748b"; // Slate
          borderColor = "#475569";
          break;
        case "monthly":
          backgroundColor = "#64748b"; // Slate
          borderColor = "#475569";
          break;
        default:
          backgroundColor = "#3b82f6"; // Blue (none/one-off)
          borderColor = "#2563eb";
      }
    }

    // Indicador visual ⚠️ para último job da série (agendamento acabando)
    const isLastOfSeries = lastJobBySeriesId.has(job.id);
    const lastIndicator = isLastOfSeries ? " ⚠️" : "";

    // Visit type indicator
    const visitTypePrefix = job.visitType === "first_clean" ? "🆕 "
      : job.visitType === "touchup" ? "↩️ "
      : "";

    // Touch-up: dashed border + reduced opacity
    if (job.visitType === "touchup") {
      borderColor = "#94a3b8"; // slate-400
    }

    return {
      id: job.id,
      title: `${visitTypePrefix}${client?.name || "Unknown"}${teamNames ? " (" + teamNames + ")" : ""}${lastIndicator}`,
      start: `${job.date}T${job.startTime}`,
      end: `${job.date}T${job.endTime}`,
      backgroundColor: job.visitType === "touchup" ? "#94a3b8" : backgroundColor,
      borderColor: borderColor,
      extendedProps: {
        ...job,
        clientName: client?.name,
        teamNames: teamNames,
        clientAddress: client?.address,
        clientPhone: client?.phone,
        isLastOfSeries: isLastOfSeries
      }
    };
  });

  // Generate lunch break events for calendar display (memoized for performance)
  const lunchBreakEvents = useMemo(() => {
    const lunchEvents = [];

    // Use visible date range
    const startDate = visibleDateRange.start;
    const endDate = visibleDateRange.end;

    // Calculate number of days between start and end
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

    // Pre-index jobs by date for O(1) lookup instead of O(n) filter per day
    const jobsByDate = {};
    jobs.forEach(job => {
      if ((job.team || "team1") === activeTeam) {
        if (!jobsByDate[job.date]) {
          jobsByDate[job.date] = [];
        }
        jobsByDate[job.date].push(job);
      }
    });

    // Generate lunch break for each day in the visible range
    for (let i = 0; i <= daysDiff; i++) {
      const date = addDays(startDate, i);
      const dateStr = format(date, 'yyyy-MM-dd');

      // O(1) lookup instead of filtering all jobs
      const dayJobs = jobsByDate[dateStr] || [];

      let lunchStart = LUNCH_START;
      let lunchEnd = LUNCH_END;

      // Check if this day+team has a custom lunch time
      const customKey = `${dateStr}-${activeTeam}`;
      const customTime = customLunchTimes[customKey];
      if (customTime) {
        lunchStart = customTime.startTime;
        lunchEnd = customTime.endTime;
      } else if (dayJobs.length > 0) {
        // No custom time - check for automatic adjustment based on conflicts
        const conflictingJob = dayJobs.find(job =>
          conflictsWithLunch(job.startTime, job.endTime)
        );
        if (conflictingJob) {
          const adjusted = adjustForLunchBreak(conflictingJob.startTime, conflictingJob.endTime);
          lunchStart = adjusted.startTime;
          lunchEnd = adjusted.endTime;
        }
      }

      lunchEvents.push({
        id: `lunch-${dateStr}`,
        title: language === "en" ? "🍽️ Lunch Break" : "🍽️ Almoço",
        start: `${dateStr}T${lunchStart}`,
        end: `${dateStr}T${lunchEnd}`,
        backgroundColor: "#f97316", // Orange
        borderColor: "#ea580c",
        textColor: "#fff",
        editable: true, // Allow dragging lunch breaks
        extendedProps: {
          isLunchBreak: true
        }
      });
    }

    return lunchEvents;
  }, [visibleDateRange, jobs, activeTeam, customLunchTimes, language]);

  // Calculate search results (Google Calendar style - timeline centered on present)
  // Past jobs on top (scroll UP to see older), future jobs below (scroll DOWN to see later)
  // Uses deferredFilter (React 18) for smooth UI - low priority updates don't block typing
  const { searchResults, firstFutureIndex } = useMemo(() => {
    if (!deferredFilter.trim()) return { searchResults: [], firstFutureIndex: 0 };

    const query = deferredFilter.toLowerCase();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Search across ALL teams (not just activeTeam)
    const filteredJobs = jobs
      .filter(job => {
        if (!job.date) return false;
        const parsedDate = new Date(job.date);
        if (isNaN(parsedDate.getTime())) return false;

        const client = findClientById(job.clientId, job.clientName);
        if (!client) return false;
        return client.name.toLowerCase().includes(query);
      })
      .map(job => {
        const client = findClientById(job.clientId, job.clientName);
        const jobDate = new Date(job.date);
        return {
          ...job,
          clientName: client?.name || "Unknown",
          isPast: jobDate < today,
          teamLabel: (job.team || "team1").replace("team", "T")
        };
      });

    // Separate future and past jobs
    const futureJobs = filteredJobs.filter(job => !job.isPast);
    const pastJobs = filteredJobs.filter(job => job.isPast);

    // Sort past jobs by date descending (most recent at bottom, oldest at top)
    // So when scrolling UP you see older jobs
    pastJobs.sort((a, b) => {
      const dateCompare = new Date(a.date) - new Date(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.startTime.localeCompare(b.startTime);
    });

    // Sort future jobs by date ascending (closest first, then further)
    // So when scrolling DOWN you see later jobs
    futureJobs.sort((a, b) => {
      const dateCompare = new Date(a.date) - new Date(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.startTime.localeCompare(b.startTime);
    });

    // Past jobs on top, future jobs below
    // firstFutureIndex marks where future jobs start (for auto-scroll)
    // Limit to 50 results for performance
    const allResults = [...pastJobs, ...futureJobs];
    const limitedResults = allResults.slice(0, 50);
    const adjustedFirstFutureIndex = Math.min(pastJobs.length, limitedResults.length);

    return {
      searchResults: limitedResults,
      firstFutureIndex: adjustedFirstFutureIndex
    };
  }, [jobs, clients, deferredFilter]);

  // Auto-scroll search results to first future job (timeline centered on present)
  useEffect(() => {
    if (showSearchResults && searchResults.length > 0 && firstFutureIndex > 0 && searchResultsRef.current) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        const container = searchResultsRef.current;
        const items = container?.querySelectorAll('[data-search-item]');
        if (items && items[firstFutureIndex]) {
          // Calculate position relative to container and scroll only the container
          const itemTop = items[firstFutureIndex].offsetTop;
          container.scrollTop = itemTop;
        }
      }, 50);
    }
  }, [showSearchResults, searchResults.length, firstFutureIndex]);

  // Convert notices to FullCalendar allDay events
  const NOTICE_COLORS = { red: "#dc3545", orange: "#fd7e14", blue: "#0d6efd", green: "#198754" };
  const noticeEvents = useMemo(() => {
    return notices
      .filter(n => n.team === "all" || n.team === activeTeam)
      .map(n => ({
        id: `notice-${n.id}`,
        title: n.title,
        start: n.date,
        allDay: true,
        backgroundColor: NOTICE_COLORS[n.color] || NOTICE_COLORS.blue,
        borderColor: NOTICE_COLORS[n.color] || NOTICE_COLORS.blue,
        textColor: "#fff",
        extendedProps: { isNotice: true, noticeId: n.id, description: n.description, color: n.color, team: n.team }
      }));
  }, [notices, activeTeam]);

  // Combine job events, lunch break events and notice events
  const events = [...jobEvents, ...lunchBreakEvents, ...noticeEvents];

  // Filter jobs by date range for mobile list view - includes lunch breaks
  const getFilteredJobs = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    let startDate, endDate;

    if (mobileViewType === "week") {
      // Week view - show entire week
      const selectedDate = new Date(selectedMobileDate + 'T00:00:00');
      startDate = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Monday
      endDate = endOfWeek(selectedDate, { weekStartsOn: 1 }); // Sunday
      endDate.setHours(23, 59, 59, 999); // Include entire last day
    } else {
      // Day view - show single day
      const selectedDate = new Date(selectedMobileDate + 'T00:00:00');
      startDate = selectedDate;
      endDate = new Date(selectedDate);
      endDate.setDate(endDate.getDate() + 1);
    }

    // Filter actual jobs
    const filteredJobs = jobs
      .filter(job => (job.team || "team1") === activeTeam)
      .filter(job => {
        // Collaborators cannot see jobs starting after 6pm (18:00)
        if (!user?.isAdmin && !user?.isClientManager) {
          const startHour = parseInt(job.startTime.split(':')[0], 10);
          if (startHour >= 18) return false;
        }

        // Filter by date range
        const jobDate = new Date(job.date);
        return jobDate >= startDate && jobDate <= endDate;
      })
      .filter(job => {
        // Only show jobs on days where there's a team working
        const teamForDay = getTeamForDate(job.date, activeTeam);
        return teamForDay.length > 0;
      })
      .filter(job => {
        // Global client filter - use findClientById for legacy job compatibility
        if (globalClientFilter.trim()) {
          const client = findClientById(job.clientId, job.clientName);
          if (!client) return false;
          return client.name.toLowerCase().includes(globalClientFilter.toLowerCase());
        }
        return true;
      });

    // Create lunch breaks for dates that have jobs
    const datesWithJobs = [...new Set(filteredJobs.map(j => j.date))];
    const lunchBreaks = [];

    datesWithJobs.forEach(date => {
      const dateStr = date;
      const teamForDay = getTeamForDate(dateStr, activeTeam);

      if (teamForDay.length > 0) {
        // Find all jobs on this date for this team (from filtered jobs)
        const dayJobs = filteredJobs.filter(job => job.date === dateStr);

        let lunchStart = LUNCH_START; // "12:00"
        let lunchEnd = LUNCH_END; // "12:20"

        // Check if this day+team has a custom lunch time
        const customKey = `${dateStr}-${activeTeam}`;
        const customTime = customLunchTimes[customKey];
        if (customTime) {
          lunchStart = customTime.startTime;
          lunchEnd = customTime.endTime;
        }

        // If no custom time found, check for automatic adjustment based on conflicts
        if (!customTime && dayJobs.length > 0) {
          // Check if any job conflicts with default lunch time
          const conflictingJob = dayJobs.find(job =>
            conflictsWithLunch(job.startTime, job.endTime)
          );

          if (conflictingJob) {
            // Adjust lunch to the closest available time
            const adjusted = adjustForLunchBreak(conflictingJob.startTime, conflictingJob.endTime);
            lunchStart = adjusted.startTime;
            lunchEnd = adjusted.endTime;
          }
        }

        lunchBreaks.push({
          id: `lunch-${date}-${activeTeam}`,
          date: date,
          startTime: lunchStart,
          endTime: lunchEnd,
          isLunchBreak: true,
          team: activeTeam
        });
      }
    });

    // Combine jobs and lunch breaks
    const allItems = [...filteredJobs, ...lunchBreaks].sort((a, b) => {
      // Sort by date, then by start time
      const dateCompare = new Date(a.date) - new Date(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.startTime.localeCompare(b.startTime);
    });

    return allItems;
  };

  // Handle invoice checkbox toggle
  const handleInvoiceToggle = (jobId, event) => {
    event.stopPropagation(); // Prevent opening the modal
    setSelectedInvoices(prev => {
      if (prev.includes(jobId)) {
        return prev.filter(id => id !== jobId);
      } else {
        return [...prev, jobId];
      }
    });
  };

  // Toggle theme between dark and light
  const toggleTheme = () => {
    setDarkTheme(prev => {
      const newTheme = !prev;
      localStorage.setItem('calendarTheme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
  };

  // Handle send invoices - show review modal first
  const handleSendInvoices = async () => {
    if (selectedInvoices.length === 0) {
      alert(language === "en" ? "Please select at least one invoice" : "Selecione pelo menos uma invoice");
      return;
    }

    // Filter only jobs that have Xero clients - use findClientById for legacy job compatibility
    const jobsToInvoice = jobs.filter(job => {
      const client = findClientById(job.clientId, job.clientName);
      return selectedInvoices.includes(job.id) && getPaymentMethod(client) === 'xero';
    });

    if (jobsToInvoice.length === 0) {
      alert(language === "en" ? "Selected clients are not configured for Xero invoicing" : "Os clientes selecionados não estão configurados para invoicing via Xero");
      return;
    }

    // Prepare invoice data for review
    const invoicesForReview = jobsToInvoice.map(job => {
      const client = findClientById(job.clientId, job.clientName);

      // Format date as DD/MM/YY for reference
      const jobDate = new Date(job.date);
      const day = String(jobDate.getDate()).padStart(2, '0');
      const month = String(jobDate.getMonth() + 1).padStart(2, '0');
      const year = String(jobDate.getFullYear()).slice(-2);
      const formattedDate = `${day}/${month}/${year}`;

      // Cliente novo = não tem xeroContactId (nunca recebeu invoice)
      const hasXeroContact = getXeroContactId(client);
      const isNewClient = !hasXeroContact;

      return {
        jobId: job.id,
        clientId: client.id,
        clientName: client.name,
        clientEmail: client.email || '',
        clientPhone: client.phone || '',
        date: job.date,
        reference: `It's So Clean - Cleaning done on ${formattedDate}`,
        description: client.invoice_description || client.invoiceDescription || "Cleaning Service",
        amount: job.invoiceAmount || client.default_price || client.defaultInvoiceAmount || 0,
        xeroContactId: hasXeroContact,
        requestFeedback: isNewClient // Novo cliente: marcado | Antigo: desmarcado (admin pode alterar)
      };
    });

    setReviewInvoices(invoicesForReview);
    setShowInvoiceReviewModal(true);
  };

  // Confirm and send invoices after review
  const handleConfirmSendInvoices = async () => {
    if (reviewInvoices.length === 0) return;

    setShowInvoiceReviewModal(false);
    setSendingInvoices(true);
    try {
      const results = [];

      for (const invoice of reviewInvoices) {
        const client = findClientById(invoice.clientId, invoice.clientName);

        const invoiceData = {
          contactId: invoice.xeroContactId, // Backend expects 'contactId'
          date: invoice.date,
          description: invoice.description,
          amount: invoice.amount,
          reference: invoice.reference, // Use the edited reference from the review modal
          clientEmail: invoice.clientEmail || client?.email || "",
          clientName: invoice.clientName || client?.name || "",
          clientPhone: invoice.clientPhone || client?.phone || "",
          clientAddress: client?.address || "",
          requestFeedback: invoice.requestFeedback !== false // Enviar pedido de feedback via WhatsApp
        };

        // Validate required fields before sending (contactId is optional, will be created if needed)
        if (!invoiceData.amount || !invoiceData.description || !invoiceData.date) {
          console.error(`❌ Missing required fields for ${invoiceData.clientName}:`, {
            amount: invoiceData.amount,
            description: invoiceData.description,
            date: invoiceData.date
          });
          results.push({
            ...invoiceData,
            jobId: invoice.jobId,
            clientName: invoice.clientName,
            success: false,
            error: `Missing required fields: ${!invoiceData.amount ? 'Amount' : ''} ${!invoiceData.description ? 'Description' : ''} ${!invoiceData.date ? 'Date' : ''}`.trim()
          });
          continue; // Skip this invoice
        }

        // If no contactId, backend will create the contact automatically
        if (!invoiceData.contactId) {
          console.log(`📝 Client "${invoiceData.clientName}" will be created in Xero automatically`);
        }

        try {
          // Send invoice
          const data = await callXeroFunction('xeroSendInvoice', invoiceData);

          if (data.success) {
            results.push({ ...invoiceData, jobId: invoice.jobId, clientName: invoice.clientName, success: true, invoiceId: data.invoiceId, invoiceNumber: data.invoiceNumber });

            // Save xeroContactId to client if it was newly created or missing
            if (data.xeroContactId && !client?.xeroContactId && !client?.xero_contact_id) {
              try {
                const isFirestoreId = isFirestoreClient(client.id);

                if (isFirestoreId) {
                  const clientRef = doc(db, "clients", client.id);
                  await updateDoc(clientRef, {
                    xeroContactId: data.xeroContactId
                  });
                  console.log(`✅ Saved xeroContactId to Firestore client ${client.name}`);
                  client.xeroContactId = data.xeroContactId;
                } else {
                  await callXeroFunction('updateClient', {
                    xero_contact_id: data.xeroContactId
                  }, { method: 'PUT', id: client.id });
                  console.log(`✅ Saved xero_contact_id to PostgreSQL client ${client.name}`);
                  client.xero_contact_id = data.xeroContactId;
                }
              } catch (updateError) {
                console.error("Error updating client with xeroContactId:", updateError);
              }
            }

            // Save to Firestore
            try {
              await addDoc(collection(db, "xeroInvoices"), {
                xeroInvoiceId: data.invoiceId,
                xeroInvoiceNumber: data.invoiceNumber,
                jobId: invoice.jobId,
                clientId: invoice.clientId,
                clientName: invoice.clientName,
                xeroContactId: data.xeroContactId || invoice.xeroContactId,
                date: Timestamp.fromDate(new Date(invoice.date)),
                description: invoiceData.description,
                amount: invoiceData.amount,
                status: data.status,
                total: data.total || invoiceData.amount, // Use amount as fallback
                sentAt: Timestamp.now(),
                sentBy: user.email
              });

              // Mark job as invoiced
              await updateDoc(doc(db, "jobs", invoice.jobId), {
                invoiceSent: true,
                invoiceSentAt: Timestamp.now(),
                invoiceSentBy: user.email
              });
            } catch (firestoreError) {
              console.error("Error saving to Firestore:", firestoreError);
            }
          } else {
            results.push({ ...invoiceData, jobId: invoice.jobId, clientName: invoice.clientName, success: false, error: data.message });
          }
        } catch (error) {
          results.push({ ...invoiceData, jobId: invoice.jobId, clientName: invoice.clientName, success: false, error: error.message });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      alert(`${language === "en" ? "Invoices sent!" : "Invoices enviadas!"}\n\n✅ ${successCount} ${language === "en" ? "successful" : "com sucesso"}\n${failCount > 0 ? `❌ ${failCount} ${language === "en" ? "failed" : "falharam"}` : ''}`);

      setSelectedInvoices([]);

      // Reload jobs to show invoice sent indicators
      await reloadJobs();
    } catch (error) {
      console.error("Error sending invoices:", error);
      alert(language === "en" ? "Error sending invoices" : "Erro ao enviar invoices");
    } finally {
      setSendingInvoices(false);
    }
  };

  // Open invoice modal for individual job
  const handleOpenInvoiceModal = () => {
    if (!selectedJob) return;

    const client = findClientById(selectedJob.clientId, selectedJob.clientName);

    if (!client) {
      alert(language === "en" ? "Client not found" : "Cliente não encontrado");
      return;
    }

    if (getPaymentMethod(client) !== 'xero') {
      alert(language === "en" ? "This client is not configured for Xero invoicing" : "Este cliente não está configurado para invoicing via Xero");
      return;
    }

    // Format date as DD/MM/YY
    const jobDate = new Date(selectedJob.date);
    const day = String(jobDate.getDate()).padStart(2, '0');
    const month = String(jobDate.getMonth() + 1).padStart(2, '0');
    const year = String(jobDate.getFullYear()).slice(-2);
    const formattedDate = `${day}/${month}/${year}`;

    // Generate reference
    const reference = `It's So Clean - Cleaning done on ${formattedDate}`;

    // Pre-fill invoice data
    setInvoiceFormData({
      description: client.invoice_description || client.invoiceDescription || "Cleaning Service",
      amount: selectedJob.invoiceAmount || client.default_price || client.defaultInvoiceAmount || 0,
      notes: selectedJob.notes || "",
      reference: reference
    });

    setShowInvoiceModal(true);
  };

  // Send individual invoice
  const handleSendIndividualInvoice = async () => {
    if (!selectedJob) return;

    const client = findClientById(selectedJob.clientId, selectedJob.clientName);
    if (!client) {
      alert(language === "en" ? "Client not found" : "Cliente não encontrado");
      return;
    }

    setSendingInvoices(true);
    try {
      const invoiceData = {
        xeroContactId: getXeroContactId(client),
        date: selectedJob.date,
        description: invoiceFormData.description,
        amount: invoiceFormData.amount,
        reference: invoiceFormData.reference,
        clientEmail: client.email || "",
        clientName: client.name || "",
        clientPhone: client.phone || "",
        clientAddress: client.address || ""
      };

      // Send invoice (using backend Easypanel instead of Firebase Functions)
      const data = await callXeroFunction('xeroSendInvoice', invoiceData);

      if (data.success) {
        // Save xeroContactId to client if it was newly created (check both PostgreSQL and Firestore fields)
        if (data.xeroContactId && !client?.xeroContactId && !client?.xero_contact_id) {
          try {
            const isFirestoreId = isFirestoreClient(client.id);

            if (isFirestoreId) {
              const clientRef = doc(db, "clients", client.id);
              await updateDoc(clientRef, {
                xeroContactId: data.xeroContactId
              });
              console.log(`✅ Saved xeroContactId to Firestore client ${client.name}`);
            } else {
              await callXeroFunction('updateClient', {
                xero_contact_id: data.xeroContactId
              }, { method: 'PUT', id: client.id });
              console.log(`✅ Saved xero_contact_id to PostgreSQL client ${client.name}`);
            }
          } catch (updateError) {
            console.error("Error updating client with xeroContactId:", updateError);
          }
        }

        // Save to Firestore
        try {
          await addDoc(collection(db, "xeroInvoices"), {
            xeroInvoiceId: data.invoiceId,
            xeroInvoiceNumber: data.invoiceNumber,
            jobId: selectedJob.id,
            clientId: client.id,
            clientName: client.name,
            xeroContactId: data.xeroContactId || getXeroContactId(client),
            date: Timestamp.fromDate(new Date(selectedJob.date)),
            description: invoiceFormData.description,
            amount: invoiceFormData.amount,
            status: data.status,
            total: data.total,
            sentAt: Timestamp.now(),
            sentBy: user.email
          });

          // Mark job as invoiced
          await updateDoc(doc(db, "jobs", selectedJob.id), {
            invoiceSent: true,
            invoiceSentAt: Timestamp.now(),
            invoiceSentBy: user.email
          });
        } catch (firestoreError) {
          console.error("Error saving to Firestore:", firestoreError);
        }

        alert(`${language === "en" ? "Invoice sent successfully!" : "Invoice enviada com sucesso!"}\n\n${language === "en" ? "Invoice #" : "Invoice #"}${data.invoiceNumber}`);
        setShowInvoiceModal(false);

        // Reload jobs to show invoice sent indicator
        await reloadJobs();
      } else {
        alert(`${language === "en" ? "Error sending invoice:" : "Erro ao enviar invoice:"}\n${data.message}`);
      }
    } catch (error) {
      console.error("Error sending invoice:", error);
      alert(language === "en" ? "Error sending invoice" : "Erro ao enviar invoice");
    } finally {
      setSendingInvoices(false);
    }
  };

  // Update invoice data in review modal
  const handleReviewInvoiceChange = (index, field, value) => {
    setReviewInvoices(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Cancel invoice review modal
  const handleCancelReview = () => {
    setShowInvoiceReviewModal(false);
    setReviewInvoices([]);
  };

  // Handle amount change - only this job
  const handleAmountChangeOnlyThisJob = async () => {
    if (!pendingAmountChange) return;

    try {
      // Save only to this job
      await updateDoc(doc(db, "jobs", selectedJob.id), pendingAmountChange.jobData);

      setShowAmountChangeModal(false);
      setShowModal(false);
      setPendingAmountChange(null);
      await reloadJobs();
    } catch (error) {
      console.error("Error updating job:", error);
      alert(language === "en" ? "Error saving job" : "Erro ao salvar job");
    }
  };

  // Handle amount change - update default for client
  const handleAmountChangeUpdateDefault = async () => {
    if (!pendingAmountChange) return;

    try {
      const client = pendingAmountChange.client;
      const isFirestoreId = isFirestoreClient(client.id);

      // Update client's default amount
      if (isFirestoreId) {
        await updateDoc(doc(db, "clients", client.id), {
          defaultInvoiceAmount: pendingAmountChange.newAmount
        });
        console.log("✅ Firestore client updated with defaultInvoiceAmount");
      } else {
        await callXeroFunction('updateClient', {
          default_price: pendingAmountChange.newAmount
        }, { method: 'PUT', id: client.id });
        console.log("✅ PostgreSQL client updated with default_price");
      }

      // Save to this job
      await updateDoc(doc(db, "jobs", selectedJob.id), pendingAmountChange.jobData);

      // Update invoiceAmount on all future uninvoiced jobs of the same client
      const today = new Date().toISOString().split('T')[0];
      const futureJobs = jobs.filter(j =>
        String(j.clientId) === String(selectedJob.clientId) &&
        j.id !== selectedJob.id &&
        j.date >= today &&
        !j.invoiceSent
      );

      if (futureJobs.length > 0) {
        // Firestore batch supports up to 500 operations
        for (let i = 0; i < futureJobs.length; i += 450) {
          const chunk = futureJobs.slice(i, i + 450);
          const batch = writeBatch(db);
          chunk.forEach(j => {
            batch.update(doc(db, "jobs", j.id), {
              invoiceAmount: pendingAmountChange.newAmount,
              updatedAt: Timestamp.now()
            });
          });
          await batch.commit();
        }
        console.log(`✅ Updated invoiceAmount on ${futureJobs.length} future jobs`);
      }

      setShowAmountChangeModal(false);
      setShowModal(false);
      setPendingAmountChange(null);
      await reloadJobs();

      // Reload clients to reflect the change
      const result = await callXeroFunction('getClients', {}, { method: 'GET' });
      setClients(result.clients || []);
    } catch (error) {
      console.error("Error updating client and job:", error);
      alert(language === "en" ? "Error saving changes" : "Erro ao salvar alterações");
    }
  };

  // Handle password/checklist change - only this job
  const handlePasswordChecklistOnlyThisJob = async () => {
    if (!pendingPasswordChecklistChange) return;

    try {
      // Save only to this job
      await updateDoc(doc(db, "jobs", selectedJob.id), pendingPasswordChecklistChange.jobData);

      setShowPasswordChecklistModal(false);
      setShowModal(false);
      setPendingPasswordChecklistChange(null);
      await reloadJobs();
    } catch (error) {
      console.error("Error updating job:", error);
      alert(language === "en" ? "Error saving job" : "Erro ao salvar job");
    }
  };

  // Handle password/checklist/notes change - update client database
  const handlePasswordChecklistUpdateClient = async () => {
    if (!pendingPasswordChecklistChange) return;

    try {
      const { client, jobData } = pendingPasswordChecklistChange;

      console.log("🔐 Updating client database...", {
        clientId: client.id,
        clientName: client.name,
        isPostgres: typeof client.id === 'number',
        password: jobData.password,
        checklist: jobData.checklist,
        notes: jobData.notes
      });

      // Check if client is from Firestore or PostgreSQL
      const isFirestoreId = isFirestoreClient(client.id);

      if (isFirestoreId) {
        // Update Firestore client
        await updateDoc(doc(db, "clients", client.id), {
          password: jobData.password || "",
          checklist: jobData.checklist || [],
          notes: jobData.notes || ""
        });
        console.log("✅ Firestore client updated successfully");
      } else {
        // Update PostgreSQL client via API
        await callXeroFunction('updateClient', {
          password: jobData.password || "",
          default_checklist: JSON.stringify(jobData.checklist || []),
          notes: jobData.notes || ""
        }, { method: 'PUT', id: client.id });
        console.log("✅ PostgreSQL client updated successfully");
      }

      // Update ALL jobs for this client (not just the current one)
      // Use ID-based matching only (safe) - avoid name matching to prevent affecting wrong clients
      const allClientJobs = jobs.filter(j => {
        // Must have clientId to be matched
        if (!j.clientId) return false;
        // Direct ID match (PostgreSQL numeric or string)
        if (String(j.clientId) === String(client.id)) return true;
        // Legacy Firestore ID match (job uses old Firestore document ID, client has it in firebase_id)
        if (client.firebase_id && String(j.clientId) === String(client.firebase_id)) return true;
        // NOTE: Removed name-based matching - too risky if two clients have same name
        return false;
      });
      console.log(`📋 Updating ${allClientJobs.length} jobs for client ${client.name}...`);

      // First, save the current job with ALL jobData (includes time changes, status, etc.)
      if (selectedJob?.id) {
        await updateDoc(doc(db, "jobs", selectedJob.id), jobData);
        console.log(`✅ Current job ${selectedJob.id} updated with full jobData`);
      }

      // Then, update OTHER jobs for this client (only password, checklist, notes)
      const otherClientJobs = allClientJobs.filter(job => job.id !== selectedJob?.id);
      const updatePromises = otherClientJobs.map(job => {
        // Jobs are always in Firestore (string IDs)
        if (typeof job.id === 'string') {
          return updateDoc(doc(db, "jobs", job.id), {
            password: jobData.password || "",
            checklist: jobData.checklist || [],
            notes: jobData.notes || ""
          });
        }
      }).filter(Boolean);

      await Promise.all(updatePromises);
      console.log(`✅ Updated ${otherClientJobs.length} other jobs for this client`);

      console.log("✅ All jobs updated successfully");

      setShowPasswordChecklistModal(false);
      setShowModal(false);
      setPendingPasswordChecklistChange(null);
      await reloadJobs();

      // Reload clients to reflect the change
      const result = await callXeroFunction('getClients', {}, { method: 'GET' });
      setClients(result.clients || []);

      console.log("✅ Calendar page reloaded successfully");
    } catch (error) {
      console.error("❌ Error updating client and job:", error);
      alert(language === "en" ? "Error saving changes" : "Erro ao salvar alterações");
    }
  };

  // Handle search result click
  const handleSearchResultClick = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    if (!job) {
      console.error("❌ Job not found:", jobId);
      return;
    }

    // Auto-switch to the job's team
    const jobTeam = job.team || "team1";
    if (jobTeam !== activeTeam) {
      setActiveTeam(jobTeam);
    }

    // Navigate to the job's date on mobile
    if (isMobile && job.date) {
      setSelectedMobileDate(job.date);
    }

    const client = findClientById(job.clientId, job.clientName);

    // Get checklist with JSONB parse support for PostgreSQL
    const clientChecklist = (() => {
      // PostgreSQL returns JSONB as already-parsed array
      if (Array.isArray(client?.default_checklist)) {
        return client.default_checklist;
      }
      if (client?.default_checklist && typeof client.default_checklist === 'string') {
        try {
          return JSON.parse(client.default_checklist);
        } catch (e) {
          console.warn('Failed to parse default_checklist:', e);
        }
      }
      return client?.checklist || [];
    })();

    setSelectedJob(job);
    setSelectedEvent(null); // No calendar event when opening from search
    setFormData({
      clientId: job.clientId || "",
      date: job.date || "",
      startTime: job.startTime || "",
      endTime: job.endTime || "",
      notes: job.notes || client?.notes || "",
      password: job.password || client?.password || "",
      checklist: job.checklist || clientChecklist,
      invoiceAmount: job.invoiceAmount || client?.default_price || client?.defaultInvoiceAmount || 0,
      status: job.status || "scheduled",
      recurrence: job.recurrence || "none",
      uploadedFiles: job.uploadedFiles || [],
      timeModified: job.timeModified || false,
      timeModificationReason: job.timeModificationReason || "",
      originalEndTime: job.endTime || "",
      repeatUntil: "indefinite",
      repeatUntilDate: ""
    });
    setUploadedPhotos(job.uploadedFiles || []);
    setModalMode("edit");
    setShowModal(true);
    setShowSearchResults(false);
    setGlobalClientFilter(""); // Clear search
  };

  // === Calendar Notices CRUD ===
  const canEditNotices = user?.isAdmin || user?.isClientManager;

  const openNotice = (notice) => {
    setSelectedNotice(notice);
    setNoticeModalMode(canEditNotices ? "edit" : "view");
    setShowNoticeModal(true);
  };

  const handleSaveNotice = async (formData) => {
    try {
      if (noticeModalMode === "edit" && selectedNotice) {
        await updateDoc(doc(db, "calendarNotices", selectedNotice.id), {
          ...formData,
          updatedAt: Timestamp.now()
        });
      } else {
        await addDoc(collection(db, "calendarNotices"), {
          ...formData,
          createdBy: user?.email || "",
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      }
      setShowNoticeModal(false);
      setSelectedNotice(null);
    } catch (error) {
      console.error("Error saving notice:", error);
      alert(language === "en" ? "Error saving notice" : "Erro ao salvar aviso");
    }
  };

  const handleDeleteNotice = async () => {
    if (!selectedNotice) return;
    const msg = language === "en" ? "Delete this notice?" : "Excluir este aviso?";
    if (!window.confirm(msg)) return;
    try {
      await deleteDoc(doc(db, "calendarNotices", selectedNotice.id));
      setShowNoticeModal(false);
      setSelectedNotice(null);
    } catch (error) {
      console.error("Error deleting notice:", error);
      alert(language === "en" ? "Error deleting notice" : "Erro ao excluir aviso");
    }
  };

  // Handle event click
  const handleEventClick = (info) => {
    // Notice click: open modal (edit for admin/manager, view-only for staff)
    if (info.event.id.startsWith('notice-')) {
      const noticeId = info.event.extendedProps.noticeId;
      const notice = notices.find(n => n.id === noticeId);
      if (notice) openNotice(notice);
      return;
    }

    const job = jobs.find(j => j.id === info.event.id);
    const client = findClientById(job.clientId, job.clientName);

    console.log("📝 Opening job for edit:", {
      jobId: job.id,
      clientId: job.clientId,
      clientFound: !!client,
      jobPassword: job.password,
      clientPassword: client?.password,
      finalPassword: job.password || client?.password || "",
      jobRecurrence: job.recurrence,
      jobOriginalRecurrence: job.originalRecurrence
    });

    setSelectedJob(job);
    setSelectedEvent(info.event); // Track selected event for keyboard shortcuts
    setFormData({
      clientId: job.clientId || "",
      date: job.date || "",
      startTime: job.startTime || "",
      endTime: job.endTime || "",
      notes: job.notes || client?.notes || "",
      password: job.password || client?.password || "",
      // Use default_checklist (PostgreSQL) or checklist (Firestore fallback)
      checklist: (() => {
        if (job.checklist) return job.checklist;
        // PostgreSQL returns JSONB as already-parsed array
        if (Array.isArray(client?.default_checklist)) {
          return client.default_checklist;
        }
        if (client?.default_checklist && typeof client.default_checklist === 'string') {
          try {
            return JSON.parse(client.default_checklist);
          } catch (e) {
            console.warn('Failed to parse default_checklist:', e);
          }
        }
        return client?.checklist || [];
      })(),
      // Use default_price (PostgreSQL) or defaultInvoiceAmount (Firestore fallback)
      invoiceAmount: job.invoiceAmount || client?.default_price || client?.defaultInvoiceAmount || 0,
      status: job.status || "scheduled",
      visitType: job.visitType || "regular",
      firstCleanExtra: job.firstCleanExtra || 60,
      // Show originalRecurrence if recurrence was set to "none" (split occurrence)
      recurrence: job.recurrence === "none" && job.originalRecurrence ? job.originalRecurrence : (job.recurrence || "none"),
      uploadedFiles: job.uploadedFiles || [],
      timeModified: job.timeModified || false,
      timeModificationReason: job.timeModificationReason || "",
      originalEndTime: job.endTime || "", // Store original end time for comparison
      repeatUntil: "indefinite",
      repeatUntilDate: ""
    });
    setUploadedPhotos(job.uploadedFiles || []);
    setModalMode("edit");
    setShowModal(true);
  };

  // Handle date select (create new job)
  const handleDateSelect = (selectInfo) => {
    const start = selectInfo.start;
    // Format date in local timezone to avoid UTC conversion issues
    const year = start.getFullYear();
    const month = String(start.getMonth() + 1).padStart(2, "0");
    const day = String(start.getDate()).padStart(2, "0");
    const date = `${year}-${month}-${day}`;
    const startTime = `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`;
    const endDate = new Date(start.getTime() + 2 * 60 * 60 * 1000); // +2 hours
    const endTime = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}`;

    // Wait a tiny bit to ensure weeklyTeams is loaded
    setTimeout(() => {
      setFormData({
        clientId: "",
        date,
        startTime,
        endTime,
        notes: "",
        password: "",
        checklist: [],
        status: "scheduled",
        visitType: "regular",
        firstCleanExtra: 60,
        recurrence: "none",
        uploadedFiles: [],
        invoiceAmount: 0,
        timeModified: false,
        timeModificationReason: "",
        originalEndTime: "",
        repeatUntil: "indefinite",
        repeatUntilDate: ""
      });
      setUploadedPhotos([]);
      setClientSearchQuery(""); // Clear client search
      setSelectedJob(null);
      setSelectedEvent(null); // Clear selected event
      setModalMode("create");
      setShowModal(true);
    }, 100);
  };

  // Handle event drop (drag & drop)
  const handleEventDrop = async (info) => {
    // Notices are not draggable
    if (info.event.id.startsWith('notice-')) {
      info.revert();
      return;
    }

    // Check if it's a lunch break event
    if (info.event.id.startsWith('lunch-')) {
      // Extract date from event ID (format: "lunch-YYYY-MM-DD")
      const dateStr = info.event.id.replace('lunch-', '');

      // Get new time
      const newStart = info.event.start;
      const newStartTime = `${String(newStart.getHours()).padStart(2, "0")}:${String(newStart.getMinutes()).padStart(2, "0")}`;
      const newEnd = info.event.end;
      const newEndTime = `${String(newEnd.getHours()).padStart(2, "0")}:${String(newEnd.getMinutes()).padStart(2, "0")}`;

      // Find which client(s) have jobs on this date for this team
      const dayJobs = jobs.filter(job =>
        job.date === dateStr &&
        (job.team || "team1") === activeTeam
      );

      // Save custom lunch time keyed by date + team (independent per day)
      const updatedCustomTimes = { ...customLunchTimes };
      const key = `${dateStr}-${activeTeam}`;
      updatedCustomTimes[key] = {
        startTime: newStartTime,
        endTime: newEndTime
      };

      setCustomLunchTimes(updatedCustomTimes);

      try {
        localStorage.setItem('customLunchTimes', JSON.stringify(updatedCustomTimes));
      } catch (error) {
        console.error("Error saving custom lunch times:", error);
      }

      return; // Exit early for lunch breaks
    }

    const job = jobs.find(j => j.id === info.event.id);
    if (!job) return;

    // Check if it's a recurring event OR part of a series (even if recurrence was removed)
    if ((job.recurrence && job.recurrence !== "none") || job.seriesId) {
      // Store the change and show modal
      setPendingEventChange({
        type: "drop",
        info,
        job
      });
      setShowRecurrenceModal(true);
      info.revert(); // Revert temporarily until user chooses
      return;
    }

    // Get new date and times in local timezone
    const newStart = info.event.start;
    const newEnd = info.event.end;

    const year = newStart.getFullYear();
    const month = String(newStart.getMonth() + 1).padStart(2, "0");
    const day = String(newStart.getDate()).padStart(2, "0");
    const newDate = `${year}-${month}-${day}`;

    const newStartTime = `${String(newStart.getHours()).padStart(2, "0")}:${String(newStart.getMinutes()).padStart(2, "0")}`;
    let newEndTime = `${String(newEnd.getHours()).padStart(2, "0")}:${String(newEnd.getMinutes()).padStart(2, "0")}`;

    // Check if the SAME client is already scheduled for this day (regardless of team)
    // Use String() comparison to handle number vs string IDs (PostgreSQL vs Firestore)
    const existingJobOnDate = jobs.find(j =>
      j.date === newDate &&
      j.clientId && job.clientId &&
      String(j.clientId) === String(job.clientId) &&
      j.id !== job.id // Exclude current job
    );

    if (existingJobOnDate) {
      const currentClient = findClientById(job.clientId, job.clientName);

      alert(language === "en"
        ? `${currentClient?.name || "This client"} is already scheduled on ${newDate}.\n\nA client cannot be scheduled twice on the same day.`
        : `${currentClient?.name || "Este cliente"} já está agendado em ${newDate}.\n\nUm cliente não pode ser agendado duas vezes no mesmo dia.`
      );
      info.revert(); // Revert the drag
      return;
    }

    // Se timeModified=true, preservar a duração atual (usuário ajustou manualmente)
    // Caso contrário, recalcular baseado nas horas do cliente e tamanho do time
    if (job.timeModified) {
      // Preservar duração atual - calcular a partir do job original
      const [oldStartHour, oldStartMin] = job.startTime.split(':').map(Number);
      const [oldEndHour, oldEndMin] = job.endTime.split(':').map(Number);
      const durationMinutes = (oldEndHour * 60 + oldEndMin) - (oldStartHour * 60 + oldStartMin);

      const [newStartHour, newStartMin] = newStartTime.split(':').map(Number);
      const newEndMinutes = newStartHour * 60 + newStartMin + durationMinutes;
      const endHour = Math.floor(newEndMinutes / 60);
      const endMin = newEndMinutes % 60;

      newEndTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
    } else {
      // Recalcular end time baseado nas horas do cliente e tamanho do time
      const client = findClientById(job.clientId, job.clientName);
      const teamForNewDay = getTeamForDate(newDate);

      // Use service_duration (PostgreSQL) or hours (Firestore fallback)
      const clientDuration = getServiceDuration(client);
      if (clientDuration && teamForNewDay.length > 0) {
        // Parse client hours (format: "HH:MM")
        const [hours, minutes] = clientDuration.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes;

        // Divide by number of people
        const minutesPerPerson = Math.round(totalMinutes / teamForNewDay.length);

        // Calculate end time from start time
        const [startHour, startMin] = newStartTime.split(':').map(Number);
        const endTotalMinutes = startHour * 60 + startMin + minutesPerPerson;
        const endHour = Math.floor(endTotalMinutes / 60);
        const endMin = endTotalMinutes % 60;

        newEndTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
      }
    }

    try {
      // Update in Firestore
      await updateDoc(doc(db, "jobs", job.id), {
        date: newDate,
        startTime: newStartTime,
        endTime: newEndTime,
        updatedAt: Timestamp.now()
      });

      // Update local state without reloading
      setJobs(prevJobs =>
        prevJobs.map(j =>
          j.id === job.id
            ? { ...j, date: newDate, startTime: newStartTime, endTime: newEndTime }
            : j
        )
      );
    } catch (error) {
      console.error("Error updating job:", error);
      info.revert(); // Revert the change if error
      alert(language === "en" ? "Error moving job" : "Erro ao mover job");
    }
  };

  // Mobile drag and drop handlers using POINTER events
  const [dragGhostPos, setDragGhostPos] = useState({ x: 0, y: 0 });
  // dragOverSlot highlight is done via direct DOM manipulation (no state needed)
  const slotRectsRef = useRef([]); // cached slot rects + DOM elements
  const dragGhostRef = useRef({ x: 0, y: 0 }); // ref mirror to avoid setState every frame
  const dragOverSlotRef = useRef(null); // ref mirror for slot highlight
  const ghostDomRef = useRef(null); // direct DOM ref for ghost element
  const highlightedSlotDomRef = useRef(null); // currently highlighted slot DOM element

  // Cache all slot rects + DOM elements at drag start (avoids layout thrashing during drag)
  const cacheSlotRects = () => {
    const slots = document.querySelectorAll('[data-time-slot]');
    slotRectsRef.current = Array.from(slots).map(slot => ({
      time: slot.dataset.timeSlot,
      date: slot.dataset.date,
      rect: slot.getBoundingClientRect(),
      el: slot // keep DOM ref for direct highlight
    }));
  };

  // Find slot at point using cached rects (O(n) but no DOM access)
  const findSlotAtPointCached = (clientX, clientY) => {
    for (const s of slotRectsRef.current) {
      if (clientX >= s.rect.left && clientX <= s.rect.right && clientY >= s.rect.top && clientY <= s.rect.bottom) {
        return s;
      }
    }
    return null;
  };

  // Drag handle pointer down - starts drag immediately (no hold timer needed)
  const handleDragHandleDown = (e, job) => {
    e.stopPropagation();
    if (e.pointerType === 'touch' && e.isPrimary === false) return;

    e.currentTarget.setPointerCapture(e.pointerId);

    const pos = { x: e.clientX, y: e.clientY };
    dragStartPosRef.current = pos;
    draggedJobRef.current = job;
    isDraggingRef.current = false;
    dragGhostRef.current = pos;
    dragOverSlotRef.current = null;

    setDragGhostPos(pos);
    setDraggedJob(job);
    setIsDragging(false);

    // Pre-cache all slot rects while nothing is moving
    cacheSlotRects();
  };

  const handleDragHandleMove = (e) => {
    if (!draggedJobRef.current) return;

    const clientX = e.clientX;
    const clientY = e.clientY;

    // Start dragging after 8px movement
    if (!isDraggingRef.current) {
      const deltaX = Math.abs(clientX - dragStartPosRef.current.x);
      const deltaY = Math.abs(clientY - dragStartPosRef.current.y);
      if (deltaX > 8 || deltaY > 8) {
        isDraggingRef.current = true;
        setIsDragging(true); // single setState to show ghost + dim slots
      }
      return;
    }

    // ALL updates via direct DOM manipulation - ZERO React re-renders during drag
    // 1. Move ghost
    if (ghostDomRef.current) {
      ghostDomRef.current.style.transform = `translate(${clientX + 16}px, ${clientY - 40}px)`;
    }

    // 2. Find slot under finger and highlight via DOM
    const slotData = findSlotAtPointCached(clientX, clientY);
    const prevSlot = dragOverSlotRef.current;
    dragOverSlotRef.current = slotData;

    const slotChanged = (slotData?.time !== prevSlot?.time) || (slotData?.date !== prevSlot?.date);
    if (slotChanged) {
      // Remove old highlight
      if (highlightedSlotDomRef.current) {
        highlightedSlotDomRef.current.style.backgroundColor = '';
      }
      // Apply new highlight
      if (slotData?.el) {
        slotData.el.style.backgroundColor = '#3b82f6';
        highlightedSlotDomRef.current = slotData.el;
      } else {
        highlightedSlotDomRef.current = null;
      }
      // Update ghost text
      if (ghostDomRef.current) {
        ghostDomRef.current.textContent = slotData ? `🕐 ${slotData.time}` : '↕ Arraste';
      }
    }
  };

  // Shared cleanup for drag end/cancel
  const cleanupDrag = () => {
    if (highlightedSlotDomRef.current) {
      highlightedSlotDomRef.current.style.backgroundColor = '';
      highlightedSlotDomRef.current = null;
    }
    draggedJobRef.current = null;
    isDraggingRef.current = false;
    dragOverSlotRef.current = null;
    slotRectsRef.current = [];
    setDraggedJob(null);
    setIsDragging(false);
  };

  const handleDragHandleUp = (e) => {
    const currentDraggedJob = draggedJobRef.current;
    if (!currentDraggedJob) return;

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }

    if (isDraggingRef.current) {
      const slotData = findSlotAtPointCached(e.clientX, e.clientY);
      if (slotData) {
        if (slotData.date !== currentDraggedJob.date) {
          const confirmMove = window.confirm(
            `Mover "${findClientById(currentDraggedJob.clientId, currentDraggedJob.clientName)?.name || 'Cliente'}" para outro dia (${slotData.date})?`
          );
          if (!confirmMove) {
            cleanupDrag();
            return;
          }
        }
        cleanupDrag();
        handleDropOnTimeSlot(currentDraggedJob, slotData.date, slotData.time);
        return;
      }
    }

    cleanupDrag();
  };

  const handleDragHandleCancel = () => {
    cleanupDrag();
  };

  const handleDropOnTimeSlot = async (job, newDate, newStartTime) => {
    let newEndTime;
    const [newStartHour, newStartMin] = newStartTime.split(':').map(Number);

    // Se timeModified=true, preservar duração atual
    // Caso contrário, recalcular baseado nas horas do cliente e tamanho do time
    if (job.timeModified) {
      // Preservar duração atual
      const [oldStartHour, oldStartMin] = job.startTime.split(':').map(Number);
      const [oldEndHour, oldEndMin] = job.endTime.split(':').map(Number);
      const durationMinutes = (oldEndHour * 60 + oldEndMin) - (oldStartHour * 60 + oldStartMin);

      const newEndMinutes = newStartHour * 60 + newStartMin + durationMinutes;
      const newEndHour = Math.floor(newEndMinutes / 60);
      const newEndMin = newEndMinutes % 60;
      newEndTime = `${String(newEndHour).padStart(2, '0')}:${String(newEndMin).padStart(2, '0')}`;
    } else {
      // Recalcular baseado nas horas do cliente e tamanho do time para nova data
      const client = findClientById(job.clientId, job.clientName);
      const teamForNewDay = getTeamForDate(newDate);
      const clientDuration = getServiceDuration(client);

      if (clientDuration && teamForNewDay.length > 0) {
        const [hours, minutes] = clientDuration.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes;
        const minutesPerPerson = Math.round(totalMinutes / teamForNewDay.length);

        const newEndMinutes = newStartHour * 60 + newStartMin + minutesPerPerson;
        const newEndHour = Math.floor(newEndMinutes / 60);
        const newEndMin = newEndMinutes % 60;
        newEndTime = `${String(newEndHour).padStart(2, '0')}:${String(newEndMin).padStart(2, '0')}`;
      } else {
        // Fallback: preservar duração original se não conseguir calcular
        const [oldStartHour, oldStartMin] = job.startTime.split(':').map(Number);
        const [oldEndHour, oldEndMin] = job.endTime.split(':').map(Number);
        const durationMinutes = (oldEndHour * 60 + oldEndMin) - (oldStartHour * 60 + oldStartMin);

        const newEndMinutes = newStartHour * 60 + newStartMin + durationMinutes;
        const newEndHour = Math.floor(newEndMinutes / 60);
        const newEndMin = newEndMinutes % 60;
        newEndTime = `${String(newEndHour).padStart(2, '0')}:${String(newEndMin).padStart(2, '0')}`;
      }
    }

    // Check if the SAME client is already scheduled for this day
    // Use String() comparison to handle number vs string IDs (PostgreSQL vs Firestore)
    const existingJobOnDate = jobs.find(j =>
      j.date === newDate &&
      j.clientId && job.clientId &&
      String(j.clientId) === String(job.clientId) &&
      j.id !== job.id
    );

    if (existingJobOnDate) {
      const currentClient = findClientById(job.clientId, job.clientName);
      alert(language === "en"
        ? `${currentClient?.name || "This client"} is already scheduled on ${newDate}.\n\nA client cannot be scheduled twice on the same day.`
        : `${currentClient?.name || "Este cliente"} já está agendado em ${newDate}.\n\nUm cliente não pode ser agendado duas vezes no mesmo dia.`
      );
      setDraggedJob(null);
      setIsDragging(false);
      return;
    }

    // Check if it's a recurring event OR part of a series (even if recurrence was removed)
    if ((job.recurrence && job.recurrence !== "none") || job.seriesId) {
      // Store the pending change and show recurrence modal
      setPendingEventChange({
        type: "mobileDrop",
        job: job,
        newDate: newDate,
        newStartTime: newStartTime,
        newEndTime: newEndTime
      });
      setShowRecurrenceModal(true);

      // Reset drag state (modal will handle the update)
      setDraggedJob(null);
      setIsDragging(false);
      return;
    }

    try {
      // Update in Firestore (non-recurring job)
      await updateDoc(doc(db, "jobs", job.id), {
        date: newDate,
        startTime: newStartTime,
        endTime: newEndTime,
        updatedAt: Timestamp.now()
      });

      // Update local state
      setJobs(prevJobs =>
        prevJobs.map(j =>
          j.id === job.id
            ? { ...j, date: newDate, startTime: newStartTime, endTime: newEndTime }
            : j
        )
      );

      setDraggedJob(null);
      setIsDragging(false);
    } catch (error) {
      console.error("Error moving job:", error);
      alert(language === "en" ? "Error moving job" : "Erro ao mover job");
      setDraggedJob(null);
      setIsDragging(false);
    }
  };

  // Pinch-to-zoom handlers for timeline
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Pinch detected - calculate initial distance
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      setLastPinchDistance(distance);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && lastPinchDistance !== null) {
      e.preventDefault(); // Prevent default zoom behavior

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      // Calculate scale change
      const delta = distance - lastPinchDistance;
      const scaleChange = delta * 0.003; // Reduced sensitivity for smoother zoom

      // Update zoom (limit between 0.5 and 1.5)
      setZoomScale(prev => Math.max(0.5, Math.min(1.5, prev + scaleChange)));
      setLastPinchDistance(distance);
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      setLastPinchDistance(null);
    }
  };

  // Navigate mobile date by offset days (or weeks if in week mode)
  const navigateMobileDate = useCallback((offset) => {
    setSelectedMobileDate(prev => {
      const date = new Date(prev + 'T00:00:00');
      if (mobileViewType === "week") {
        date.setDate(date.getDate() + (offset * 7));
      } else {
        date.setDate(date.getDate() + offset);
      }
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    });
  }, [mobileViewType]);

  const handleMoveJob = async () => {
    const { job, newDate, newStartTime, newEndTime } = moveJobData;

    if (!newDate || !newStartTime || !newEndTime) {
      alert(language === "en" ? "Please fill all fields" : "Por favor preencha todos os campos");
      return;
    }

    // Check if the SAME client is already scheduled for this day
    // Use String() comparison to handle number vs string IDs (PostgreSQL vs Firestore)
    const existingJobOnDate = jobs.find(j =>
      j.date === newDate &&
      j.clientId && job.clientId &&
      String(j.clientId) === String(job.clientId) &&
      j.id !== job.id
    );

    if (existingJobOnDate) {
      const currentClient = findClientById(job.clientId, job.clientName);
      alert(language === "en"
        ? `${currentClient?.name || "This client"} is already scheduled on ${newDate}.\n\nA client cannot be scheduled twice on the same day.`
        : `${currentClient?.name || "Este cliente"} já está agendado em ${newDate}.\n\nUm cliente não pode ser agendado duas vezes no mesmo dia.`
      );
      return;
    }

    try {
      // Update in Firestore
      await updateDoc(doc(db, "jobs", job.id), {
        date: newDate,
        startTime: newStartTime,
        endTime: newEndTime,
        updatedAt: Timestamp.now()
      });

      // Update local state
      setJobs(prevJobs =>
        prevJobs.map(j =>
          j.id === job.id
            ? { ...j, date: newDate, startTime: newStartTime, endTime: newEndTime }
            : j
        )
      );

      setShowMoveJobModal(false);
      setDraggedJob(null);
      setMoveJobData({ job: null, newDate: "", newStartTime: "", newEndTime: "" });
    } catch (error) {
      console.error("Error moving job:", error);
      alert(language === "en" ? "Error moving job" : "Erro ao mover job");
    }
  };

  // Handle event resize
  const handleEventResize = async (info) => {
    // Notices cannot be resized
    if (info.event.id.startsWith('notice-')) {
      info.revert();
      return;
    }

    const job = jobs.find(j => j.id === info.event.id);
    if (!job) return;

    // Check if it's a recurring event OR part of a series (even if recurrence was removed)
    if ((job.recurrence && job.recurrence !== "none") || job.seriesId) {
      // Store the change and show modal
      setPendingEventChange({
        type: "resize",
        info,
        job
      });
      setShowRecurrenceModal(true);
      info.revert(); // Revert temporarily until user chooses
      return;
    }

    const newEnd = info.event.end;
    const newEndTime = `${String(newEnd.getHours()).padStart(2, "0")}:${String(newEnd.getMinutes()).padStart(2, "0")}`;

    try {
      // Update in Firestore - marcar timeModified para preservar duração ao arrastar
      await updateDoc(doc(db, "jobs", job.id), {
        endTime: newEndTime,
        timeModified: true, // Flag para indicar que duração foi ajustada manualmente
        updatedAt: Timestamp.now()
      });

      // Update local state without reloading
      setJobs(prevJobs =>
        prevJobs.map(j =>
          j.id === job.id ? { ...j, endTime: newEndTime, timeModified: true } : j
        )
      );
    } catch (error) {
      console.error("Error resizing job:", error);
      info.revert(); // Revert the change if error
      alert(language === "en" ? "Error resizing job" : "Erro ao redimensionar job");
    }
  };

  // Handle form submit
  // Generate recurring job occurrences - MELHORADO: usa data limite ao invés de número fixo
  const generateRecurringJobs = async (baseJobData, seriesId = null) => {
    const { date, recurrence, repeatUntil, repeatUntilDate } = baseJobData;

    if (recurrence === "none") return;

    // Generate a unique series ID if not provided
    if (!seriesId) {
      seriesId = `series_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    const baseDate = new Date(date + "T00:00:00");

    // Calcular data limite baseado em repeatUntil
    let endDate;
    if (repeatUntil === "date" && repeatUntilDate) {
      endDate = new Date(repeatUntilDate + "T23:59:59");
    } else {
      // Indefinido = 2 anos por padrão
      endDate = new Date(baseDate);
      endDate.setFullYear(endDate.getFullYear() + 2);
    }

    const generatedJobs = [];
    let currentDate = new Date(baseDate);
    let iteracao = 0;
    const maxIteracoes = 200; // Limite de segurança

    while (iteracao < maxIteracoes) {
      iteracao++;

      // Calcular próxima data
      const newDate = new Date(currentDate);
      switch (recurrence) {
        case "weekly":
          newDate.setDate(newDate.getDate() + 7);
          break;
        case "fortnightly":
          newDate.setDate(newDate.getDate() + 14);
          break;
        case "3weekly":
          newDate.setDate(newDate.getDate() + 21);
          break;
        case "monthly":
          newDate.setMonth(newDate.getMonth() + 1);
          break;
        default:
          break;
      }

      currentDate = newDate;

      // Parar se passou da data limite
      if (currentDate > endDate) break;

      // Format date in local timezone
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, "0");
      const day = String(currentDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Create job data for this occurrence
      const occurrenceData = {
        ...baseJobData,
        date: formattedDate,
        seriesId: seriesId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      generatedJobs.push(occurrenceData);
    }

    if (generatedJobs.length === 0) {
      console.log("⚠️ Nenhum job gerado - data limite pode estar muito próxima");
      return seriesId;
    }

    // Usar BATCH para salvar todos de uma vez (muito mais rápido!)
    // Firestore suporta até 500 operações por batch
    const batchSize = 450; // Margem de segurança
    for (let i = 0; i < generatedJobs.length; i += batchSize) {
      const batch = writeBatch(db);
      const chunk = generatedJobs.slice(i, i + batchSize);

      chunk.forEach(jobData => {
        const newDocRef = doc(collection(db, "jobs"));
        batch.set(newDocRef, jobData);
      });

      await batch.commit();
      console.log(`✅ Batch ${Math.floor(i / batchSize) + 1}: ${chunk.length} jobs salvos`);
    }

    console.log(`✅ Gerados ${generatedJobs.length} jobs recorrentes até ${endDate.toISOString().split('T')[0]}`);

    return seriesId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.clientId || !formData.date || !formData.startTime || !formData.endTime) {
      alert("Please fill all required fields");
      return;
    }

    // Check if the SAME client is already scheduled for this day (regardless of team)
    // Use String() comparison to handle number vs string IDs (PostgreSQL vs Firestore)
    const existingJobOnDate = jobs.find(job =>
      job.date === formData.date &&
      job.clientId && formData.clientId &&
      String(job.clientId) === String(formData.clientId) &&
      (modalMode === "create" || job.id !== selectedJob?.id) // Exclude current job if editing
    );

    if (existingJobOnDate) {
      const currentClient = clients.find(c => String(c.id) === String(formData.clientId));

      alert(language === "en"
        ? `${currentClient?.name || "This client"} is already scheduled on ${formData.date}.\n\nA client cannot be scheduled twice on the same day.`
        : `${currentClient?.name || "Este cliente"} já está agendado em ${formData.date}.\n\nUm cliente não pode ser agendado duas vezes no mesmo dia.`
      );
      return;
    }

    // Check for restricted times
    const currentClient = clients.find(c => String(c.id) === String(formData.clientId));
    if (currentClient?.restrictedTimes?.enabled) {
      const jobStart = formData.startTime;
      const timeWindows = currentClient.restrictedTimes.timeWindows || [];

      // Check if job time falls within any allowed window
      const isWithinAllowedWindow = timeWindows.some(window => {
        return jobStart >= window.start && jobStart <= window.end;
      });

      if (!isWithinAllowedWindow && timeWindows.length > 0) {
        // Show warning modal
        setRestrictedTimeData({
          client: currentClient,
          timeWindows,
          attemptedTime: jobStart,
          formData
        });
        setShowRestrictedTimeModal(true);
        return; // Stop here, wait for user decision
      }
    }

    console.log("Submitting job with data:", formData);

    // Proceed with job save
    await performJobSave();
  };

  // Handle restricted time warning - user confirms anyway
  const handleConfirmRestrictedTime = async () => {
    setShowRestrictedTimeModal(false);
    // Proceed with the save
    await performJobSave();
    setRestrictedTimeData(null);
  };

  // Handle restricted time warning - user cancels
  const handleCancelRestrictedTime = () => {
    setShowRestrictedTimeModal(false);
    setRestrictedTimeData(null);
    // Modal stays open so user can change the time
  };

  // Save client restricted times from job modal
  const handleSaveClientRestrictedTimes = async () => {
    if (!formData.clientId || !editingRestrictedTimes) return;

    try {
      const isFirestoreId = isFirestoreClient(formData.clientId);

      if (isFirestoreId) {
        const clientRef = doc(db, "clients", formData.clientId);
        await updateDoc(clientRef, {
          restrictedTimes: editingRestrictedTimes
        });
        console.log("✅ Firestore client restrictedTimes updated");
      } else {
        await callXeroFunction('updateClient', {
          restricted_times: JSON.stringify(editingRestrictedTimes)
        }, { method: 'PUT', id: formData.clientId });
        console.log("✅ PostgreSQL client restricted_times updated");
      }

      // Update local clients state
      setClients(clients.map(c =>
        c.id === formData.clientId
          ? { ...c, restrictedTimes: editingRestrictedTimes }
          : c
      ));

      setEditingRestrictedTimes(null);

      alert(language === "en"
        ? "Client restricted times updated successfully!"
        : "Horários restritos do cliente atualizados com sucesso!");
    } catch (error) {
      console.error("Error updating client restricted times:", error);
      alert(language === "en"
        ? "Error updating restricted times"
        : "Erro ao atualizar horários restritos");
    }
  };

  // Handle delete from modal button
  const handleDelete = async () => {
    if (!selectedJob) return;

    // Check if it's a recurring event
    if (selectedJob.recurrence && selectedJob.recurrence !== "none") {
      // Show recurrence modal for recurring events
      setPendingEventChange({
        type: "delete",
        job: selectedJob
      });
      setShowRecurrenceModal(true);
      return;
    }

    // For non-recurring events, show confirmation and delete
    if (!window.confirm(language === "en"
      ? "Are you sure you want to delete this job?"
      : "Tem certeza que deseja deletar este job?")) return;

    try {
      await deleteDoc(doc(db, "jobs", selectedJob.id));
      setShowModal(false);
      setSelectedEvent(null);
      await reloadJobs(); // Only reload jobs, no loading spinner
    } catch (error) {
      console.error("Error deleting job:", error);
      alert(language === "en" ? "Error deleting job" : "Erro ao deletar job");
    }
  };

  // Helper function to get team name
  const getTeamName = (teamId) => {
    if (teamId === "team1") return "Team 1";
    if (teamId === "team2") return "Team 2";
    if (teamId === "team3") return "Team 3";
    return "Team 1";
  };

  // Helper function to get next team (cycles through available teams for user)
  const getNextTeam = (currentTeam) => {
    if (!user?.isAdmin) {
      // Non-admin users only toggle between team1 and team2
      return currentTeam === "team1" ? "team2" : "team1";
    }
    // Admin users cycle through all three teams
    if (currentTeam === "team1") return "team2";
    if (currentTeam === "team2") return "team3";
    return "team1";
  };

  // Handle team switch - open picker first to choose target team
  const handleInitiateTeamSwitch = () => {
    if (!selectedJob) return;

    const currentTeam = selectedJob.team || activeTeam;

    // Store pending switch data and open picker modal
    setPendingTeamSwitch({
      job: selectedJob,
      currentTeam,
      client: findClientById(selectedJob.clientId, selectedJob.clientName)
    });
    setShowTeamPickerModal(true);
  };

  // Handle team selection from picker modal
  const handleTeamSelected = (targetTeam) => {
    if (!pendingTeamSwitch) return;

    setTeamSwitchData({
      job: pendingTeamSwitch.job,
      currentTeam: pendingTeamSwitch.currentTeam,
      targetTeam,
      client: pendingTeamSwitch.client
    });
    setShowTeamPickerModal(false);
    setPendingTeamSwitch(null);
    setShowTeamSwitchModal(true);
  };

  // Move single job to different team
  const handleMoveSingleJob = async () => {
    if (!teamSwitchData) return;

    try {
      const job = teamSwitchData.job;

      // Recalculate end time based on the new team's size
      const newEndTime = calculateEndTime(
        job.clientId,
        job.date,
        job.startTime,
        null,
        null,
        teamSwitchData.targetTeam,
        job.clientName
      );

      const updateData = {
        team: teamSwitchData.targetTeam,
        updatedAt: Timestamp.now()
      };

      // Only update endTime if recalculation succeeded
      if (newEndTime) {
        updateData.endTime = newEndTime;
        console.log(`🔄 Recalculated endTime for team switch: ${job.startTime} → ${newEndTime}`);
      }

      await updateDoc(doc(db, "jobs", job.id), updateData);

      setShowTeamSwitchModal(false);
      setShowModal(false);
      await reloadJobs();

      alert(language === "en"
        ? `Job moved to ${getTeamName(teamSwitchData.targetTeam)}`
        : `Job movido para ${getTeamName(teamSwitchData.targetTeam)}`);
    } catch (error) {
      console.error("Error moving job:", error);
      alert(language === "en" ? "Error moving job" : "Erro ao mover job");
    }
  };

  // Move all jobs this week to different team
  const handleMoveWeekJobs = async () => {
    if (!teamSwitchData) return;

    try {
      const jobDate = parseISO(teamSwitchData.job.date);
      const weekStart = startOfWeek(jobDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(jobDate, { weekStartsOn: 1 });
      const targetClientId = teamSwitchData.job.clientId;

      // IMPORTANT: Validate clientId exists to prevent affecting wrong jobs
      if (!targetClientId) {
        console.error("❌ Cannot move week jobs: clientId is missing");
        alert(language === "en" ? "Error: Client ID is missing" : "Erro: ID do cliente está faltando");
        return;
      }

      // Find all jobs for this client in this week
      const weekJobs = jobs.filter(j =>
        j.clientId &&
        String(j.clientId) === String(targetClientId) &&
        j.team === teamSwitchData.currentTeam
      ).filter(j => {
        const jDate = parseISO(j.date);
        return jDate >= weekStart && jDate <= weekEnd;
      });

      console.log(`🔍 Moving week jobs for clientId: ${targetClientId}, found: ${weekJobs.length} jobs`);

      // Usar BATCH para atualizar todos de uma vez
      const batch = writeBatch(db);
      const now = Timestamp.now();

      weekJobs.forEach(job => {
        const newEndTime = calculateEndTime(
          job.clientId, job.date, job.startTime, null, null,
          teamSwitchData.targetTeam, job.clientName
        );

        const updateData = { team: teamSwitchData.targetTeam, updatedAt: now };
        if (newEndTime) updateData.endTime = newEndTime;

        batch.update(doc(db, "jobs", job.id), updateData);
      });

      await batch.commit();
      console.log(`✅ Batch: ${weekJobs.length} jobs movidos para ${teamSwitchData.targetTeam}`);

      setShowTeamSwitchModal(false);
      setShowModal(false);
      await reloadJobs();

      alert(language === "en"
        ? `${weekJobs.length} job(s) moved to ${getTeamName(teamSwitchData.targetTeam)}`
        : `${weekJobs.length} job(s) movido(s) para ${getTeamName(teamSwitchData.targetTeam)}`);
    } catch (error) {
      console.error("Error moving week jobs:", error);
      alert(language === "en" ? "Error moving jobs" : "Erro ao mover jobs");
    }
  };

  // Move all future jobs to different team
  const handleMoveFutureJobs = async () => {
    if (!teamSwitchData) return;

    try {
      const jobDate = parseISO(teamSwitchData.job.date);
      const targetClientId = teamSwitchData.job.clientId;

      // IMPORTANT: Validate clientId exists to prevent affecting wrong jobs
      if (!targetClientId) {
        console.error("❌ Cannot move jobs: clientId is missing");
        alert(language === "en" ? "Error: Client ID is missing" : "Erro: ID do cliente está faltando");
        return;
      }

      // Find all future jobs for this client in the current team
      const futureJobs = jobs.filter(j =>
        j.clientId &&
        String(j.clientId) === String(targetClientId) &&
        j.team === teamSwitchData.currentTeam &&
        parseISO(j.date) >= jobDate
      );

      console.log(`🔍 Moving future jobs for clientId: ${targetClientId}, found: ${futureJobs.length} jobs`);

      // Usar BATCH para atualizar todos de uma vez
      const batch = writeBatch(db);
      const now = Timestamp.now();

      futureJobs.forEach(job => {
        const newEndTime = calculateEndTime(
          job.clientId, job.date, job.startTime, null, null,
          teamSwitchData.targetTeam, job.clientName
        );

        const updateData = { team: teamSwitchData.targetTeam, updatedAt: now };
        if (newEndTime) updateData.endTime = newEndTime;

        batch.update(doc(db, "jobs", job.id), updateData);
      });

      await batch.commit();
      console.log(`✅ Batch: ${futureJobs.length} jobs movidos para ${teamSwitchData.targetTeam}`);

      setShowTeamSwitchModal(false);
      setShowModal(false);
      await reloadJobs();

      alert(language === "en"
        ? `${futureJobs.length} job(s) moved to ${getTeamName(teamSwitchData.targetTeam)}`
        : `${futureJobs.length} job(s) movido(s) para ${getTeamName(teamSwitchData.targetTeam)}`);
    } catch (error) {
      console.error("Error moving future jobs:", error);
      alert(language === "en" ? "Error moving jobs" : "Erro ao mover jobs");
    }
  };

  // Create alternating pattern (Week 1: Team 1, Week 2: Team 2, etc.)
  const handleCreateAlternatingPattern = async () => {
    if (!teamSwitchData) return;

    try {
      const jobDate = parseISO(teamSwitchData.job.date);
      const targetClientId = teamSwitchData.job.clientId;

      // IMPORTANT: Validate clientId exists to prevent affecting wrong jobs
      if (!targetClientId) {
        console.error("❌ Cannot create pattern: clientId is missing");
        alert(language === "en" ? "Error: Client ID is missing" : "Erro: ID do cliente está faltando");
        return;
      }

      // Find all future jobs for this client
      const futureJobs = jobs.filter(j =>
        j.clientId &&
        String(j.clientId) === String(targetClientId) &&
        parseISO(j.date) >= jobDate
      ).sort((a, b) => parseISO(a.date) - parseISO(b.date));

      console.log(`🔍 Creating alternating pattern for clientId: ${targetClientId}, found: ${futureJobs.length} jobs`);

      // Group jobs by week
      const jobsByWeek = {};
      futureJobs.forEach(job => {
        const jDate = parseISO(job.date);
        const weekKey = format(startOfWeek(jDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        if (!jobsByWeek[weekKey]) jobsByWeek[weekKey] = [];
        jobsByWeek[weekKey].push(job);
      });

      // Sort weeks
      const weeks = Object.keys(jobsByWeek).sort();

      // Usar BATCH para atualizar todos de uma vez
      const batch = writeBatch(db);
      const now = Timestamp.now();
      let updatedCount = 0;

      weeks.forEach((weekKey, index) => {
        const team = (index % 2 === 0) ? teamSwitchData.targetTeam : teamSwitchData.currentTeam;

        jobsByWeek[weekKey].forEach(job => {
          if (job.team !== team) {
            const newEndTime = calculateEndTime(
              job.clientId, job.date, job.startTime, null, null, team, job.clientName
            );

            const updateData = { team, updatedAt: now };
            if (newEndTime) updateData.endTime = newEndTime;

            batch.update(doc(db, "jobs", job.id), updateData);
            updatedCount++;
          }
        });
      });

      await batch.commit();
      console.log(`✅ Batch: ${updatedCount} jobs atualizados em padrão alternado`);

      setShowTeamSwitchModal(false);
      setShowModal(false);
      await reloadJobs();

      alert(language === "en"
        ? `Alternating pattern created! ${updatedCount} job(s) updated across ${weeks.length} week(s)`
        : `Padrão alternado criado! ${updatedCount} job(s) atualizado(s) em ${weeks.length} semana(s)`);
    } catch (error) {
      console.error("Error creating alternating pattern:", error);
      alert(language === "en" ? "Error creating pattern" : "Erro ao criar padrão");
    }
  };

  // Handle delete from keyboard shortcut
  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    const job = jobs.find(j => j.id === selectedEvent.id);
    if (!job) return;

    // Check if it's a recurring event
    if (job.recurrence && job.recurrence !== "none") {
      // Show recurrence modal for recurring events
      setSelectedJob(job);
      setPendingEventChange({
        type: "delete",
        job: job
      });
      setShowRecurrenceModal(true);
      return;
    }

    // For non-recurring events, show confirmation and delete
    if (!window.confirm(language === "en"
      ? "Are you sure you want to delete this job?"
      : "Tem certeza que deseja deletar este job?")) return;

    try {
      await deleteDoc(doc(db, "jobs", job.id));
      setSelectedEvent(null);
      await reloadJobs(); // Only reload jobs, no loading spinner
    } catch (error) {
      console.error("Error deleting job:", error);
      alert(language === "en" ? "Error deleting job" : "Erro ao deletar job");
    }
  };

  // Checklist templates - bilingual (recalculates when language changes)
  const checklistTemplates = useMemo(() => ({
    moveout: language === "en" ? [
      "Dusting all surfaces",
      "Dust blinds (if plastic, talk to Bianca/Michel before doing)",
      "Skirting boards",
      "Walls and ceiling (spot cleaning marks and stains where possible)",
      "Mould removal (where possible)",
      "Full kitchen clean",
      "Cupboards and wardrobes (inside if empty and outside)",
      "Rangehood",
      "Internal windows",
      "Rubbish removal",
      "Bathrooms and toilets",
      "Vacuuming",
      "Mopping"
    ] : [
      "Tirar pó de todas as superfícies",
      "Tirar pó de persianas (se for de plástico falar com Bianca/Michel antes de fazer)",
      "Rodapés",
      "Paredes e teto (limpeza de manchas e marcas quando possível)",
      "Remoção de mofo (quando possível)",
      "Limpeza completa da cozinha",
      "Armários e guarda-roupas (por dentro se vazio e por fora)",
      "Coifa",
      "Janelas internas",
      "Remoção de lixo",
      "Banheiros e toiletes",
      "Aspirar",
      "Passar pano"
    ],
    regular: language === "en" ? [
      "Dusting all surfaces",
      "Skirting boards",
      "Kitchen (benchtops, outside of cupboards, appliances, sink, microwave, toaster, and coffee machine)",
      "Bathrooms and toilets",
      "Vacuuming all floors",
      "Mopping hard floors",
      "Taking the rubbish out",
      "Removing cobwebs"
    ] : [
      "Tirar pó de todas as superfícies",
      "Rodapés",
      "Cozinha (bancadas, parte externa dos armários, eletrodomésticos, pia, micro-ondas, torradeira e cafeteira)",
      "Banheiros e toiletes",
      "Aspirar todos os pisos",
      "Passar pano nos pisos duros",
      "Tirar o lixo",
      "Remover teias de aranha"
    ],
    airbnb: language === "en" ? [
      // Before Starting
      "Check for anything broken/damaged and take photo before starting (upload to app)",
      // Entrance
      "Clean entrance (sweep/mop)",
      "Check outdoor area - if excess dirt or leaves, sweep/clean",
      // Bedroom
      "[BEDROOM] Replace used linens and towels",
      "[BEDROOM] Check under furniture for forgotten items or trash",
      "[BEDROOM] Clean mirrors and glass surfaces",
      "[BEDROOM] Check lighting fixtures",
      "[BEDROOM] Check and water plants (if any)",
      "[BEDROOM] Clean and empty closets",
      // Bathroom
      "[BATHROOM] Scrub shower/bathtub, sink, and toilet",
      "[BATHROOM] Clean mirrors",
      "[BATHROOM] Refill soap dispensers and toilet paper",
      "[BATHROOM] Replace towels and bath mats",
      "[BATHROOM] Check condition of appliances (hairdryer, etc)",
      "[BATHROOM] Clean surfaces and drawers",
      "[BATHROOM] Ensure plunger and toilet brush are clean and visible",
      "[BATHROOM] Disinfect faucets",
      // Kitchen
      "[KITCHEN] Clean and disinfect countertops, sink, and faucet",
      "[KITCHEN] Check appliances",
      "[KITCHEN] Clean and empty refrigerator",
      "[KITCHEN] Clean inside of oven and microwave",
      "[KITCHEN] Disinfect trash can",
      "[KITCHEN] Check utensils, dishes, and cookware",
      "[KITCHEN] Restock soap, dishwasher detergent, and paper towels",
      "[KITCHEN] Clean table and chairs",
      // Living Room
      "[LIVING ROOM] Fluff and arrange pillows and throws",
      // General (all rooms)
      "[GENERAL] Dust all surfaces",
      "[GENERAL] Vacuum or mop floors",
      "[GENERAL] Clean light switches, door handles, and remotes",
      "[GENERAL] Empty trash bins and replace liners",
      "[GENERAL] Clean windows and sills",
      "[GENERAL] Restock welcome amenities",
      "[GENERAL] Inspect for property damages or maintenance issues",
      // When Finished
      "Take photos of all rooms and upload to app"
    ] : [
      // Antes de Começar
      "Verificar se há algo quebrado/danificado e tirar foto antes de começar (colocar no app)",
      // Entrada
      "Limpar entrada (varrer/passar pano)",
      "Verificar área externa - se tiver excesso de sujeira ou folhas, varrer/limpar",
      // Quarto
      "[QUARTO] Trocar lençóis e toalhas usados",
      "[QUARTO] Verificar embaixo dos móveis por itens esquecidos ou lixo",
      "[QUARTO] Limpar espelhos e superfícies de vidro",
      "[QUARTO] Verificar luminárias",
      "[QUARTO] Verificar e regar plantas (se houver)",
      "[QUARTO] Limpar e esvaziar armários",
      // Banheiro
      "[BANHEIRO] Esfregar box/banheira, pia e vaso sanitário",
      "[BANHEIRO] Limpar espelhos",
      "[BANHEIRO] Reabastecer dispensers de sabonete e papel higiênico",
      "[BANHEIRO] Trocar toalhas e tapetes",
      "[BANHEIRO] Verificar condição dos eletrodomésticos (secador, etc)",
      "[BANHEIRO] Limpar superfícies e gavetas",
      "[BANHEIRO] Garantir que desentupidor e escova de vaso estejam limpos e visíveis",
      "[BANHEIRO] Desinfetar torneiras",
      // Cozinha
      "[COZINHA] Limpar e desinfetar bancadas, pia e torneira",
      "[COZINHA] Verificar eletrodomésticos",
      "[COZINHA] Limpar e esvaziar geladeira",
      "[COZINHA] Limpar interior do forno e micro-ondas",
      "[COZINHA] Desinfetar lixeira",
      "[COZINHA] Verificar utensílios, pratos e panelas",
      "[COZINHA] Reabastecer sabão, detergente para lava-louças e papel toalha",
      "[COZINHA] Limpar mesa e cadeiras",
      // Sala de Estar
      "[SALA] Afotar e arrumar almofadas e mantas",
      // Geral (todos os cômodos)
      "[GERAL] Tirar pó de todas as superfícies",
      "[GERAL] Aspirar ou passar pano no chão",
      "[GERAL] Limpar interruptores, maçanetas e controles remotos",
      "[GERAL] Esvaziar lixeiras e trocar sacos",
      "[GERAL] Limpar janelas e peitoris",
      "[GERAL] Reabastecer amenidades de boas-vindas",
      "[GERAL] Inspecionar por danos na propriedade ou problemas de manutenção",
      // Ao Finalizar
      "Tirar fotos de todos os cômodos e colocar no app"
    ],
    postconstruction: language === "en" ? [
      // General
      "[GENERAL] Remove all construction dust from surfaces, ceilings, walls and skirting boards",
      "[GENERAL] Clean and unblock drains and sinks",
      "[GENERAL] Clean window rails and frames",
      "[GENERAL] Clean light switches and power points",
      // Kitchen
      "[KITCHEN] Clean cupboards inside and out",
      "[KITCHEN] Clean appliances (oven, cooktop)",
      "[KITCHEN] Fridge cleaning (add-on $80+GST)",
      "[KITCHEN] Clean benchtops and drains",
      // Bathrooms
      "[BATHROOMS] Clean and disinfect toilets, sinks, shower screens and baths",
      "[BATHROOMS] Clean mirrors and glass",
      // Floors
      "[FLOORS] Sweep and vacuum all floors",
      "[FLOORS] Mop all floors (timber, tiles, carpet)",
      // Windows
      "[WINDOWS] Clean glass inside (outside optional/extra)",
      // Final
      "[FINAL] Clean garage if included"
    ] : [
      // Geral
      "[GERAL] Remover toda poeira de construção de superfícies, tetos, paredes e rodapés",
      "[GERAL] Limpar e desobstruir ralos e pias",
      "[GERAL] Limpar trilhos e molduras de janelas",
      "[GERAL] Limpar interruptores e tomadas",
      // Cozinha
      "[COZINHA] Limpar armários por dentro e por fora",
      "[COZINHA] Limpar eletrodomésticos (forno, cooktop)",
      "[COZINHA] Limpeza de geladeira (add-on $80+GST)",
      "[COZINHA] Limpar bancadas e ralos",
      // Banheiros
      "[BANHEIROS] Limpar e desinfetar vasos, pias, box e banheiras",
      "[BANHEIROS] Limpar espelhos e vidros",
      // Pisos
      "[PISOS] Varrer e aspirar todos os pisos",
      "[PISOS] Passar pano em todos os pisos (madeira, azulejo, carpete)",
      // Janelas
      "[JANELAS] Limpar vidros por dentro (por fora opcional/extra)",
      // Final
      "[FINAL] Limpar garagem se incluída"
    ]
  }), [language]);

  // Checklist functions
  const loadChecklistTemplate = (templateType) => {
    if (!templateType || templateType === "") return;

    const template = checklistTemplates[templateType];
    if (template) {
      const newChecklist = template.map(text => ({ text, done: false }));
      setFormData({
        ...formData,
        checklist: newChecklist
      });
      // Track which template is active for language switching
      setActiveChecklistTemplate(templateType);
    }
  };

  // Auto-reload checklist when language changes (if a template is active)
  useEffect(() => {
    if (activeChecklistTemplate && formData.checklist.length > 0) {
      const template = checklistTemplates[activeChecklistTemplate];
      if (template) {
        // Preserve the "done" status by index position (most reliable for translations)
        const currentChecklist = formData.checklist;

        // Template items: preserve done by index
        const newChecklist = template.map((text, index) => {
          // Use same index position to preserve "done" status
          const existingItem = currentChecklist[index];
          return { text, done: existingItem?.done || false };
        });

        // Keep any manually added items (beyond template length)
        if (currentChecklist.length > template.length) {
          const manualItems = currentChecklist.slice(template.length);
          newChecklist.push(...manualItems);
        }

        setFormData({
          ...formData,
          checklist: newChecklist
        });
      }
    }
  }, [language, checklistTemplates]);

  const addChecklistItem = (textOverride) => {
    const text = textOverride || newChecklistItem;
    if (!text.trim()) return;
    setFormData({
      ...formData,
      checklist: [...formData.checklist, { text: text.trim(), done: false }]
    });
    if (!textOverride) setNewChecklistItem("");
  };

  const toggleChecklistItem = (index) => {
    const updatedChecklist = [...formData.checklist];
    updatedChecklist[index].done = !updatedChecklist[index].done;
    setFormData({ ...formData, checklist: updatedChecklist });
  };

  const removeChecklistItem = (index) => {
    const updatedChecklist = formData.checklist.filter((_, i) => i !== index);
    setFormData({ ...formData, checklist: updatedChecklist });
  };

  // Helper function to merge uploaded files with existing ones in Firestore
  // This prevents race conditions where two users upload photos to the same job
  const mergeUploadedFiles = async (jobId, newFiles) => {
    if (!jobId || !newFiles || newFiles.length === 0) {
      return newFiles || [];
    }

    try {
      // Get current files from Firestore
      const jobDoc = await getDoc(doc(db, "jobs", jobId));
      if (!jobDoc.exists()) {
        return newFiles;
      }

      const existingFiles = jobDoc.data().uploadedFiles || [];

      // Create a map of existing files by URL to avoid duplicates
      const existingUrlMap = new Map(existingFiles.map(f => [f.url, f]));

      // Add new files that don't already exist
      newFiles.forEach(file => {
        if (!existingUrlMap.has(file.url)) {
          existingUrlMap.set(file.url, file);
        }
      });

      // Return merged array
      return Array.from(existingUrlMap.values());
    } catch (error) {
      console.error("Error merging uploaded files:", error);
      return newFiles;
    }
  };

  // Checklist functions - performJobSave (actual job save logic)
  const performJobSave = async () => {
    try {
      const jobData = {
        ...formData,
        team: activeTeam,
        updatedAt: Timestamp.now()
      };

      // Se a recorrência mudou para algo diferente de "none", limpar originalRecurrence
      // Isso garante que a cor seja atualizada corretamente
      if (jobData.recurrence && jobData.recurrence !== "none") {
        delete jobData.originalRecurrence;
      }

      // Add user info if there are uploaded files
      if (formData.uploadedFiles && formData.uploadedFiles.length > 0) {
        jobData.uid = user.uid;
        jobData.slug = user.displayName || user.email;
      }

      // For edit mode, merge uploaded files with existing ones in Firestore
      // This prevents race conditions where two users upload photos simultaneously
      if (modalMode === "edit" && selectedJob?.id && formData.uploadedFiles?.length > 0) {
        jobData.uploadedFiles = await mergeUploadedFiles(selectedJob.id, formData.uploadedFiles);
      }

      if (modalMode === "create") {
        jobData.createdAt = Timestamp.now();

        // If it's recurring, generate a unique series ID
        let seriesId = null;
        if (jobData.recurrence && jobData.recurrence !== "none") {
          seriesId = `series_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          jobData.seriesId = seriesId;
        }

        // Save password, checklist, and notes to client database on first save
        // Only if clientId is a Firestore document ID (string), not a PostgreSQL numeric ID
        if (formData.clientId && typeof formData.clientId === 'string' && !Number.isInteger(Number(formData.clientId)) && (formData.password || formData.checklist?.length > 0 || formData.notes)) {
          try {
            const clientRef = doc(db, "clients", formData.clientId);
            await updateDoc(clientRef, {
              password: formData.password || "",
              checklist: formData.checklist || [],
              notes: formData.notes || ""
            });
          } catch (error) {
            console.warn('Could not update Firestore client (probably PostgreSQL client):', error);
          }
        }

        // Save the base job
        await addDoc(collection(db, "jobs"), jobData);

        // If it's recurring, generate future occurrences
        if (jobData.recurrence && jobData.recurrence !== "none") {
          await generateRecurringJobs(jobData, seriesId);
        }

        setShowModal(false);
        await reloadJobs();
      } else if (modalMode === "edit") {
        // Check if password, checklist, or notes changed
        // Use findClientById to support legacy Firestore IDs (firebase_id field)
        const client = findClientById(formData.clientId, selectedJob?.clientName);

        console.log("🔍 Checking for changes...", {
          clientId: formData.clientId,
          clientName: selectedJob?.clientName,
          clientFound: !!client,
          formDataPassword: formData.password,
          clientPassword: client?.password,
          formDataNotes: formData.notes,
          clientNotes: client?.notes
        });

        // Get client's current checklist (PostgreSQL or Firestore)
        const clientChecklist = (() => {
          // PostgreSQL returns JSONB as already-parsed array
          if (Array.isArray(client?.default_checklist)) {
            return client.default_checklist;
          }
          // If it's a string, parse it
          if (client?.default_checklist && typeof client.default_checklist === 'string') {
            try {
              return JSON.parse(client.default_checklist);
            } catch (e) {
              console.warn('Failed to parse default_checklist:', e);
            }
          }
          // Firestore fallback
          return client?.checklist || [];
        })();

        const passwordChanged = formData.password !== (client?.password || "");
        const checklistChanged = JSON.stringify(formData.checklist || []) !== JSON.stringify(clientChecklist);
        const notesChanged = formData.notes !== (client?.notes || "");

        // Debug checklist comparison
        console.log("🔍 Checklist comparison debug:", {
          formDataChecklist: formData.checklist,
          formDataChecklistStr: JSON.stringify(formData.checklist || []),
          clientChecklist: clientChecklist,
          clientChecklistStr: JSON.stringify(clientChecklist),
          clientDefaultChecklist: client?.default_checklist,
          clientDefaultChecklistType: typeof client?.default_checklist,
          isArray: Array.isArray(client?.default_checklist),
          checklistChanged
        });

        if (client && (passwordChanged || checklistChanged || notesChanged)) {
          // Password, checklist, or notes changed - show confirmation modal
          console.log("🔔 Password/checklist/notes changed - showing modal", {
            passwordChanged,
            checklistChanged,
            notesChanged,
            currentPassword: formData.password,
            clientPassword: client?.password
          });
          setPendingPasswordChecklistChange({
            client: client,
            jobData: jobData,
            passwordChanged: passwordChanged,
            checklistChanged: checklistChanged,
            notesChanged: notesChanged
          });
          setShowPasswordChecklistModal(true);
          return;
        }

        // Check if invoice amount changed for Xero clients
        const clientPaymentMethod = getPaymentMethod(client);
        if (clientPaymentMethod === 'xero') {
          const originalAmount = selectedJob.invoiceAmount || client.default_price || client.defaultInvoiceAmount || 0;
          const newAmount = formData.invoiceAmount || 0;

          if (newAmount !== originalAmount && newAmount > 0) {
            // Amount changed - show confirmation modal
            setPendingAmountChange({
              client: client,
              originalAmount: originalAmount,
              newAmount: newAmount,
              jobData: jobData
            });
            setShowAmountChangeModal(true);
            return;
          }
        }

        // Check if recurrence changed from "none" to something recurring
        const wasNonRecurring = !selectedJob.recurrence || selectedJob.recurrence === "none";
        const isNowRecurring = jobData.recurrence && jobData.recurrence !== "none";

        if (wasNonRecurring && isNowRecurring) {
          const seriesId = `series_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          jobData.seriesId = seriesId;
          await updateDoc(doc(db, "jobs", selectedJob.id), jobData);
          await generateRecurringJobs(jobData, seriesId);
          setShowModal(false);
          await reloadJobs();
          return;
        }

        // Check if it's a recurring event and if critical fields changed
        if (selectedJob.recurrence && selectedJob.recurrence !== "none") {
          const dateChanged = formData.date !== selectedJob.date;
          const startTimeChanged = formData.startTime !== selectedJob.startTime;
          const endTimeChanged = formData.endTime !== selectedJob.endTime;
          const recurrenceChanged = formData.recurrence !== selectedJob.recurrence;
          const teamChanged = jobData.team !== (selectedJob.team || "team1");
          const changingToNone = formData.recurrence === "none";

          // Se mudou para "1 vez", salvar direto sem modal (só afeta este evento)
          if (changingToNone) {
            console.log("🔄 Mudando para '1 vez' - salvando apenas este evento");
            jobData.originalRecurrence = selectedJob.originalRecurrence || selectedJob.recurrence; // Manter cor
            jobData.seriesId = null; // Remover da série
            await updateDoc(doc(db, "jobs", selectedJob.id), jobData);
            setShowModal(false);
            await reloadJobs();
            return;
          }

          if (dateChanged || startTimeChanged || endTimeChanged || recurrenceChanged || teamChanged) {
            if (user?.isAdmin || user?.isClientManager) {
              console.log("🔄 Recurring job edited - showing recurrence choice modal");
              setPendingEventChange({
                type: "edit",
                job: selectedJob,
                newData: jobData
              });
              setShowRecurrenceModal(true);
              return;
            } else {
              console.log("👤 Collaborator editing recurring job - saving only this job");
              await updateDoc(doc(db, "jobs", selectedJob.id), jobData);
              setShowModal(false);
              await reloadJobs();
              return;
            }
          }
        }

        // For non-recurring or unchanged recurring jobs, save directly
        await updateDoc(doc(db, "jobs", selectedJob.id), jobData);
        setShowModal(false);
        await reloadJobs();
      }
    } catch (error) {
      console.error("Error saving job:", error);
      alert("Error saving job");
    }
  };

  // Detect and group related recurring jobs (for jobs created before seriesId was implemented)
  // Detecta jobs relacionados SEM fazer updates (retorna jobs para update em batch depois)
  const detectRelatedJobs = (job) => {
    console.log("🔍 Detecting related jobs for:", job.clientName || job.clientId);

    if (!job.recurrence || job.recurrence === "none") {
      console.log("❌ Job is not recurring");
      return null;
    }

    // Find jobs with same client and recurrence
    // Relaxado: não compara startTime porque pode ter sido movido
    const relatedJobs = jobs.filter(j =>
      j.clientId && job.clientId &&
      String(j.clientId) === String(job.clientId) &&
      j.recurrence === job.recurrence &&
      (!j.seriesId || j.seriesId === job.seriesId)
    );

    console.log(`📊 Found ${relatedJobs.length} jobs with same client and recurrence`);

    if (relatedJobs.length <= 1) {
      console.log("❌ Not enough related jobs found (need at least 2)");
      return null;
    }

    // Sort by date
    const sortedJobs = [...relatedJobs].sort((a, b) => a.date.localeCompare(b.date));

    // Verificação simplificada do padrão (mais rápida)
    let validIntervals = 0;
    for (let i = 1; i < Math.min(sortedJobs.length, 5); i++) { // Só verifica os 5 primeiros
      const prevDate = new Date(sortedJobs[i - 1].date + "T00:00:00");
      const currDate = new Date(sortedJobs[i].date + "T00:00:00");
      const daysDiff = Math.round((currDate - prevDate) / (1000 * 60 * 60 * 24));

      const expectedDiff = job.recurrence === "weekly" ? 7 :
                          job.recurrence === "fortnightly" ? 14 :
                          job.recurrence === "3weekly" ? 21 : 30;

      if (Math.abs(daysDiff - expectedDiff) <= 4) validIntervals++;
    }

    if (validIntervals === 0) {
      console.log("❌ Jobs don't follow recurrence pattern");
      return null;
    }

    // Gera seriesId
    const newSeriesId = `series_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log(`✅ Detected ${sortedJobs.length} related jobs, seriesId: ${newSeriesId}`);

    return {
      seriesId: newSeriesId,
      jobs: sortedJobs.map(j => ({ ...j, seriesId: newSeriesId }))
    };
  };

  // Handle recurrence choice for drag/resize/delete/edit
  const handleRecurrenceChoice = async (choice) => {
    if (!pendingEventChange) return;

    const { type, info, job, newData } = pendingEventChange;

    try {
      if (choice === "this") {
        // Only this event - convert to non-recurring
        if (type === "drop") {
          const newStart = info.event.start;
          const newEnd = info.event.end;
          const year = newStart.getFullYear();
          const month = String(newStart.getMonth() + 1).padStart(2, "0");
          const day = String(newStart.getDate()).padStart(2, "0");
          const newDate = `${year}-${month}-${day}`;
          const newStartTime = `${String(newStart.getHours()).padStart(2, "0")}:${String(newStart.getMinutes()).padStart(2, "0")}`;
          const newEndTime = `${String(newEnd.getHours()).padStart(2, "0")}:${String(newEnd.getMinutes()).padStart(2, "0")}`;

          await updateDoc(doc(db, "jobs", job.id), {
            date: newDate,
            startTime: newStartTime,
            endTime: newEndTime,
            originalRecurrence: job.originalRecurrence || job.recurrence, // Preserve original recurrence for color
            recurrence: "none", // Remove recurrence from this instance
            updatedAt: Timestamp.now()
          });

          setJobs(prevJobs =>
            prevJobs.map(j =>
              j.id === job.id
                ? { ...j, date: newDate, startTime: newStartTime, endTime: newEndTime, originalRecurrence: job.originalRecurrence || job.recurrence, recurrence: "none" }
                : j
            )
          );
        } else if (type === "mobileDrop") {
          // Mobile drag-and-drop - data is already calculated
          const { newDate, newStartTime, newEndTime } = pendingEventChange;

          await updateDoc(doc(db, "jobs", job.id), {
            date: newDate,
            startTime: newStartTime,
            endTime: newEndTime,
            originalRecurrence: job.originalRecurrence || job.recurrence, // Preserve original recurrence for color
            recurrence: "none", // Remove recurrence from this instance
            updatedAt: Timestamp.now()
          });

          setJobs(prevJobs =>
            prevJobs.map(j =>
              j.id === job.id
                ? { ...j, date: newDate, startTime: newStartTime, endTime: newEndTime, originalRecurrence: job.originalRecurrence || job.recurrence, recurrence: "none" }
                : j
            )
          );
        } else if (type === "resize") {
          const newEnd = info.event.end;
          const newEndTime = `${String(newEnd.getHours()).padStart(2, "0")}:${String(newEnd.getMinutes()).padStart(2, "0")}`;

          await updateDoc(doc(db, "jobs", job.id), {
            endTime: newEndTime,
            originalRecurrence: job.originalRecurrence || job.recurrence, // Preserve original recurrence for color
            recurrence: "none", // Remove recurrence from this instance
            updatedAt: Timestamp.now()
          });

          setJobs(prevJobs =>
            prevJobs.map(j =>
              j.id === job.id ? { ...j, endTime: newEndTime, originalRecurrence: job.originalRecurrence || job.recurrence, recurrence: "none" } : j
            )
          );
        } else if (type === "delete") {
          // Delete only this occurrence
          await deleteDoc(doc(db, "jobs", job.id));
          setShowModal(false);
          setSelectedEvent(null);
          await reloadJobs();
        } else if (type === "edit") {
          // Edit only this occurrence - remove recurrence from this instance
          await updateDoc(doc(db, "jobs", job.id), {
            ...newData,
            originalRecurrence: job.originalRecurrence || job.recurrence, // Preserve original recurrence for color
            recurrence: "none", // Remove recurrence from this instance
            updatedAt: Timestamp.now()
          });
          setShowModal(false);
          setSelectedEvent(null);
          await reloadJobs();
        }
      } else if (choice === "following") {
        // This and following events - update this and all future occurrences
        let seriesId = job.seriesId;
        let detectedSeriesJobs = null;

        // Se não tem seriesId, detecta jobs relacionados
        if (!seriesId) {
          const detectionResult = detectRelatedJobs(job);
          if (!detectionResult) {
            alert(language === "en"
              ? "Could not detect related jobs."
              : "Não foi possível detectar jobs relacionados.");
            setShowRecurrenceModal(false);
            setPendingEventChange(null);
            return;
          }
          seriesId = detectionResult.seriesId;
          detectedSeriesJobs = detectionResult.jobs;
          job.seriesId = seriesId;
        }

        // Pega jobs da série (futuros)
        const seriesJobs = detectedSeriesJobs
          ? detectedSeriesJobs.filter(j => j.date >= job.date)
          : jobs.filter(j => j.seriesId === job.seriesId && j.date >= job.date);

        // Usar BATCH para atualizar todos de uma vez (muito mais rápido!)
        const batch = writeBatch(db);
        const now = Timestamp.now();

        if (type === "drop" || type === "mobileDrop") {
          // Calcular novos valores
          const newDate = type === "drop"
            ? `${info.event.start.getFullYear()}-${String(info.event.start.getMonth() + 1).padStart(2, "0")}-${String(info.event.start.getDate()).padStart(2, "0")}`
            : pendingEventChange.newDate;
          const newStartTime = type === "drop"
            ? `${String(info.event.start.getHours()).padStart(2, "0")}:${String(info.event.start.getMinutes()).padStart(2, "0")}`
            : pendingEventChange.newStartTime;
          const newEndTime = type === "drop"
            ? `${String(info.event.end.getHours()).padStart(2, "0")}:${String(info.event.end.getMinutes()).padStart(2, "0")}`
            : pendingEventChange.newEndTime;

          // Diferença de tempo para aplicar aos outros jobs
          const oldDateTime = new Date(job.date + "T" + job.startTime);
          const newDateTime = new Date(newDate + "T" + newStartTime);
          const timeDiff = newDateTime - oldDateTime;

          seriesJobs.forEach(seriesJob => {
            const jobRef = doc(db, "jobs", seriesJob.id);
            let updatedDate, updatedStartTime, updatedEndTime;

            if (seriesJob.id === job.id) {
              updatedDate = newDate;
              updatedStartTime = newStartTime;
              updatedEndTime = newEndTime;
            } else {
              const adjustedDate = new Date(new Date(seriesJob.date + "T" + seriesJob.startTime).getTime() + timeDiff);
              updatedDate = `${adjustedDate.getFullYear()}-${String(adjustedDate.getMonth() + 1).padStart(2, "0")}-${String(adjustedDate.getDate()).padStart(2, "0")}`;
              updatedStartTime = `${String(adjustedDate.getHours()).padStart(2, "0")}:${String(adjustedDate.getMinutes()).padStart(2, "0")}`;
              // Manter duração original
              const duration = new Date(`2000-01-01T${seriesJob.endTime}`) - new Date(`2000-01-01T${seriesJob.startTime}`);
              const endTime = new Date(adjustedDate.getTime() + duration);
              updatedEndTime = `${String(endTime.getHours()).padStart(2, "0")}:${String(endTime.getMinutes()).padStart(2, "0")}`;
            }

            batch.update(jobRef, {
              date: updatedDate,
              startTime: updatedStartTime,
              endTime: updatedEndTime,
              seriesId: seriesId,
              updatedAt: now
            });
          });
          await batch.commit();
          console.log(`✅ Batch drop: ${seriesJobs.length} jobs atualizados`);
        } else if (type === "resize") {
          const newEndTime = `${String(info.event.end.getHours()).padStart(2, "0")}:${String(info.event.end.getMinutes()).padStart(2, "0")}`;
          seriesJobs.forEach(seriesJob => {
            batch.update(doc(db, "jobs", seriesJob.id), { endTime: newEndTime, seriesId, updatedAt: now });
          });
          await batch.commit();
          console.log(`✅ Batch resize: ${seriesJobs.length} jobs atualizados`);
        } else if (type === "delete") {
          seriesJobs.forEach(seriesJob => {
            batch.delete(doc(db, "jobs", seriesJob.id));
          });
          await batch.commit();
          console.log(`✅ Batch delete: ${seriesJobs.length} jobs deletados`);
        } else if (type === "edit") {
          // Edit this and all future occurrences
          const frequencyChanged = newData.recurrence && newData.recurrence !== job.recurrence && newData.recurrence !== "none";
          const changingToNone = newData.recurrence === "none" && job.recurrence !== "none";

          if (frequencyChanged) {
            // Frequência mudou para outra recorrência - deletar futuros e regenerar
            console.log(`🔄 Frequência mudou de ${job.recurrence} para ${newData.recurrence}`);

            // Separar: job atual vs jobs futuros
            const futureJobs = seriesJobs.filter(j => j.id !== job.id);

            // Deletar jobs futuros no batch
            futureJobs.forEach(futureJob => {
              batch.delete(doc(db, "jobs", futureJob.id));
            });

            // Atualizar o job atual com novos dados
            const updatedCurrentJob = { ...newData, seriesId, updatedAt: now };
            delete updatedCurrentJob.originalRecurrence; // Limpar cor antiga
            batch.update(doc(db, "jobs", job.id), updatedCurrentJob);

            // Commit do batch (deleta futuros + atualiza atual)
            await batch.commit();
            console.log(`✅ Deletados ${futureJobs.length} jobs futuros, job atual atualizado`);

            // Regenerar jobs com nova frequência
            const baseJobData = {
              ...newData,
              date: newData.date || job.date,
              seriesId: seriesId
            };
            delete baseJobData.originalRecurrence;

            await generateRecurringJobs(baseJobData, seriesId);
            console.log(`✅ Jobs regenerados com frequência ${newData.recurrence}`);

          } else if (changingToNone) {
            // Mudando para "none" (1 vez) - remover da série, tornar avulsos
            console.log(`🔄 Removendo ${seriesJobs.length} jobs da série (tornando avulsos)`);

            seriesJobs.forEach(seriesJob => {
              // Preservar dados do job, só remover da série
              const updatedJobData = {
                ...newData,
                date: seriesJob.date, // Manter data original de cada job
                originalRecurrence: seriesJob.originalRecurrence || seriesJob.recurrence, // Manter cor
                recurrence: "none",
                seriesId: null, // Remover da série
                updatedAt: now
              };
              batch.update(doc(db, "jobs", seriesJob.id), updatedJobData);
            });

            await batch.commit();
            console.log(`✅ ${seriesJobs.length} jobs removidos da série`);

          } else {
            // Frequência NÃO mudou - apenas atualiza os jobs existentes
            const daysDiff = newData.date !== job.date
              ? Math.round((new Date(newData.date + "T00:00:00") - new Date(job.date + "T00:00:00")) / (1000 * 60 * 60 * 24))
              : 0;

            seriesJobs.forEach(seriesJob => {
              const updatedJobData = { ...newData, seriesId, updatedAt: now };

              if (daysDiff !== 0) {
                const seriesJobDate = new Date(seriesJob.date + "T00:00:00");
                seriesJobDate.setDate(seriesJobDate.getDate() + daysDiff);
                updatedJobData.date = `${seriesJobDate.getFullYear()}-${String(seriesJobDate.getMonth() + 1).padStart(2, "0")}-${String(seriesJobDate.getDate()).padStart(2, "0")}`;
              } else {
                updatedJobData.date = seriesJob.date;
              }

              batch.update(doc(db, "jobs", seriesJob.id), updatedJobData);
            });

            // Commit do batch
            await batch.commit();
            console.log(`✅ Batch update: ${seriesJobs.length} jobs atualizados`);
          }
        }

        // Atualiza estado local
        setJobs(prevJobs => {
          const updatedIds = new Set(seriesJobs.map(j => j.id));
          return prevJobs.map(j => updatedIds.has(j.id) ? { ...j, seriesId } : j);
        });

        if (type === "delete") {
          setShowModal(false);
          setSelectedEvent(null);
        } else if (type === "edit") {
          setShowModal(false);
          setSelectedEvent(null);
        }

        await reloadJobs();
      } else if (choice === "all") {
        // All events - update ALL jobs in the series (including previous)
        let seriesId = job.seriesId;
        let detectedSeriesJobs = null;

        // Se não tem seriesId, detecta jobs relacionados
        if (!seriesId) {
          const detectionResult = detectRelatedJobs(job);
          if (!detectionResult) {
            alert(language === "en"
              ? "Could not detect related jobs."
              : "Não foi possível detectar jobs relacionados.");
            setShowRecurrenceModal(false);
            setPendingEventChange(null);
            return;
          }
          seriesId = detectionResult.seriesId;
          detectedSeriesJobs = detectionResult.jobs;
          job.seriesId = seriesId;
        }

        // Pega TODOS os jobs da série (incluindo anteriores)
        const seriesJobs = detectedSeriesJobs || jobs.filter(j => j.seriesId === job.seriesId);

        // Usar BATCH para atualizar todos de uma vez
        const batch = writeBatch(db);
        const now = Timestamp.now();

        if (type === "drop" || type === "mobileDrop") {
          const newDate = type === "drop"
            ? `${info.event.start.getFullYear()}-${String(info.event.start.getMonth() + 1).padStart(2, "0")}-${String(info.event.start.getDate()).padStart(2, "0")}`
            : pendingEventChange.newDate;
          const newStartTime = type === "drop"
            ? `${String(info.event.start.getHours()).padStart(2, "0")}:${String(info.event.start.getMinutes()).padStart(2, "0")}`
            : pendingEventChange.newStartTime;
          const newEndTime = type === "drop"
            ? `${String(info.event.end.getHours()).padStart(2, "0")}:${String(info.event.end.getMinutes()).padStart(2, "0")}`
            : pendingEventChange.newEndTime;

          const oldDateTime = new Date(job.date + "T" + job.startTime);
          const newDateTime = new Date(newDate + "T" + newStartTime);
          const timeDiff = newDateTime - oldDateTime;

          seriesJobs.forEach(seriesJob => {
            let updatedDate, updatedStartTime, updatedEndTime;

            if (seriesJob.id === job.id) {
              updatedDate = newDate;
              updatedStartTime = newStartTime;
              updatedEndTime = newEndTime;
            } else {
              const adjustedDate = new Date(new Date(seriesJob.date + "T" + seriesJob.startTime).getTime() + timeDiff);
              updatedDate = `${adjustedDate.getFullYear()}-${String(adjustedDate.getMonth() + 1).padStart(2, "0")}-${String(adjustedDate.getDate()).padStart(2, "0")}`;
              updatedStartTime = `${String(adjustedDate.getHours()).padStart(2, "0")}:${String(adjustedDate.getMinutes()).padStart(2, "0")}`;
              const duration = new Date(`2000-01-01T${seriesJob.endTime}`) - new Date(`2000-01-01T${seriesJob.startTime}`);
              const endTime = new Date(adjustedDate.getTime() + duration);
              updatedEndTime = `${String(endTime.getHours()).padStart(2, "0")}:${String(endTime.getMinutes()).padStart(2, "0")}`;
            }

            batch.update(doc(db, "jobs", seriesJob.id), {
              date: updatedDate,
              startTime: updatedStartTime,
              endTime: updatedEndTime,
              seriesId,
              updatedAt: now
            });
          });
          await batch.commit();
          console.log(`✅ Batch drop (all): ${seriesJobs.length} jobs atualizados`);
        } else if (type === "resize") {
          const newEndTime = `${String(info.event.end.getHours()).padStart(2, "0")}:${String(info.event.end.getMinutes()).padStart(2, "0")}`;
          seriesJobs.forEach(seriesJob => {
            batch.update(doc(db, "jobs", seriesJob.id), { endTime: newEndTime, seriesId, updatedAt: now });
          });
          await batch.commit();
          console.log(`✅ Batch resize (all): ${seriesJobs.length} jobs atualizados`);
        } else if (type === "delete") {
          seriesJobs.forEach(seriesJob => {
            batch.delete(doc(db, "jobs", seriesJob.id));
          });
          await batch.commit();
          console.log(`✅ Batch delete (all): ${seriesJobs.length} jobs deletados`);
        } else if (type === "edit") {
          const frequencyChanged = newData.recurrence && newData.recurrence !== job.recurrence && newData.recurrence !== "none";
          const changingToNone = newData.recurrence === "none" && job.recurrence !== "none";

          if (frequencyChanged) {
            // Frequência mudou - deletar TODOS e regenerar a partir do primeiro
            console.log(`🔄 Frequência mudou de ${job.recurrence} para ${newData.recurrence} (ALL)`);

            // Encontrar o primeiro job da série (mais antigo)
            const sortedJobs = [...seriesJobs].sort((a, b) => a.date.localeCompare(b.date));
            const firstJob = sortedJobs[0];

            // Deletar TODOS os jobs da série no batch
            seriesJobs.forEach(seriesJob => {
              batch.delete(doc(db, "jobs", seriesJob.id));
            });

            // Commit do batch (deleta todos)
            await batch.commit();
            console.log(`✅ Deletados ${seriesJobs.length} jobs da série`);

            // Criar novo job base (o primeiro) com nova frequência
            const baseJobData = {
              ...newData,
              date: firstJob.date,
              seriesId: seriesId
            };
            delete baseJobData.originalRecurrence;

            // Criar o primeiro job
            await addDoc(collection(db, "jobs"), {
              ...baseJobData,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now()
            });

            // Regenerar jobs futuros com nova frequência
            await generateRecurringJobs(baseJobData, seriesId);
            console.log(`✅ Série regenerada com frequência ${newData.recurrence}`);

          } else if (changingToNone) {
            // Mudando para "none" (1 vez) - remover TODOS da série, tornar avulsos
            console.log(`🔄 Removendo TODOS ${seriesJobs.length} jobs da série (tornando avulsos)`);

            seriesJobs.forEach(seriesJob => {
              const updatedJobData = {
                ...newData,
                date: seriesJob.date, // Manter data original de cada job
                originalRecurrence: seriesJob.originalRecurrence || seriesJob.recurrence, // Manter cor
                recurrence: "none",
                seriesId: null, // Remover da série
                updatedAt: now
              };
              batch.update(doc(db, "jobs", seriesJob.id), updatedJobData);
            });

            await batch.commit();
            console.log(`✅ ${seriesJobs.length} jobs removidos da série (all)`);

          } else {
            // Frequência NÃO mudou - apenas atualiza os jobs existentes
            const daysDiff = newData.date !== job.date
              ? Math.round((new Date(newData.date + "T00:00:00") - new Date(job.date + "T00:00:00")) / (1000 * 60 * 60 * 24))
              : 0;

            seriesJobs.forEach(seriesJob => {
              const updatedJobData = { ...newData, seriesId, updatedAt: now };
              if (daysDiff !== 0) {
                const seriesJobDate = new Date(seriesJob.date + "T00:00:00");
                seriesJobDate.setDate(seriesJobDate.getDate() + daysDiff);
                updatedJobData.date = `${seriesJobDate.getFullYear()}-${String(seriesJobDate.getMonth() + 1).padStart(2, "0")}-${String(seriesJobDate.getDate()).padStart(2, "0")}`;
              } else {
                updatedJobData.date = seriesJob.date;
              }
              batch.update(doc(db, "jobs", seriesJob.id), updatedJobData);
            });

            // Commit do batch
            await batch.commit();
            console.log(`✅ Batch update (all): ${seriesJobs.length} jobs atualizados`);
          }
        }

        setJobs(prevJobs => {
          const updatedIds = new Set(seriesJobs.map(j => j.id));
          return prevJobs.map(j => updatedIds.has(j.id) ? { ...j, seriesId } : j);
        });

        if (type === "delete" || type === "edit") {
          setShowModal(false);
          setSelectedEvent(null);
        }

        await reloadJobs();
      }

      setShowRecurrenceModal(false);
      setPendingEventChange(null);
      // No need to reload - state already updated above for "this" option
    } catch (error) {
      console.error("Error updating recurring job:", error);
      alert(language === "en" ? "Error updating job" : "Erro ao atualizar job");
    }
  };

  // Check for duplicate employees between all teams
  const checkForDuplicateEmployees = (dateStr) => {
    const team1Members = dailyTeamOverrides.team1?.[dateStr] || weeklyTeams.team1?.[getDayOfWeek(dateStr)] || [];
    const team2Members = dailyTeamOverrides.team2?.[dateStr] || weeklyTeams.team2?.[getDayOfWeek(dateStr)] || [];
    const team3Members = dailyTeamOverrides.team3?.[dateStr] || weeklyTeams.team3?.[getDayOfWeek(dateStr)] || [];

    // Check duplicates between team1 and team2
    const duplicates12 = team1Members.filter(uid => team2Members.includes(uid));
    // Check duplicates between team1 and team3
    const duplicates13 = team1Members.filter(uid => team3Members.includes(uid));
    // Check duplicates between team2 and team3
    const duplicates23 = team2Members.filter(uid => team3Members.includes(uid));

    // Combine all duplicates (using Set to avoid duplicate entries)
    const allDuplicates = [...new Set([...duplicates12, ...duplicates13, ...duplicates23])];

    if (allDuplicates.length > 0) {
      const duplicateNames = allDuplicates.map(uid => {
        // Check if it's a custom person (custom:Name)
        if (uid && uid.startsWith && uid.startsWith("custom:")) {
          return uid.replace("custom:", "");
        }
        // Find employee by ID or UID
        const emp = employees.find(e => e.id === uid || e.uid === uid);
        return emp?.name || emp?.displayName || uid;
      });
      return duplicateNames;
    }

    return [];
  };

  // Save a new custom member to the saved list
  const saveCustomMember = async (memberName) => {
    try {
      if (!memberName || !memberName.trim()) return;

      const trimmedName = memberName.trim();

      // Check if already saved
      if (savedCustomMembers.includes(trimmedName)) {
        return;
      }

      // Add to saved list
      const updatedMembers = [...savedCustomMembers, trimmedName].sort();

      // Get or create the document
      const customMembersSnap = await getDocs(collection(db, "savedCustomMembers"));

      if (customMembersSnap.empty) {
        // Create new document
        await addDoc(collection(db, "savedCustomMembers"), {
          members: updatedMembers,
          updatedAt: Timestamp.now()
        });
      } else {
        // Update existing document
        const docId = customMembersSnap.docs[0].id;
        await updateDoc(doc(db, "savedCustomMembers", docId), {
          members: updatedMembers,
          updatedAt: Timestamp.now()
        });
      }

      console.log("✅ Custom member saved:", trimmedName);
    } catch (error) {
      console.error("Error saving custom member:", error);
    }
  };

  // Remove a custom member from the saved list AND from all daily overrides
  const removeCustomMember = async (memberName) => {
    try {
      const customId = `custom:${memberName}`;

      // 1. Remove from savedCustomMembers list
      const updatedMembers = savedCustomMembers.filter(m => m !== memberName);

      const customMembersSnap = await getDocs(collection(db, "savedCustomMembers"));

      if (!customMembersSnap.empty) {
        const docId = customMembersSnap.docs[0].id;
        await updateDoc(doc(db, "savedCustomMembers", docId), {
          members: updatedMembers,
          updatedAt: Timestamp.now()
        });
      }

      // 2. Remove from ALL dailyTeamOverrides (all teams, all dates)
      const dailyTeamSnap = await getDocs(collection(db, "dailyTeamOverrides"));

      for (const docSnap of dailyTeamSnap.docs) {
        const data = docSnap.data();
        let needsUpdate = false;
        const updates = {};

        // Check each team's array for this custom member
        for (const teamKey of ['team1', 'team2', 'team3']) {
          if (Array.isArray(data[teamKey]) && data[teamKey].includes(customId)) {
            updates[teamKey] = data[teamKey].filter(uid => uid !== customId);
            needsUpdate = true;
          }
        }

        // Also check legacy format (direct array in document)
        if (Array.isArray(data.team) && data.team.includes(customId)) {
          updates.team = data.team.filter(uid => uid !== customId);
          needsUpdate = true;
        }

        if (needsUpdate) {
          await updateDoc(doc(db, "dailyTeamOverrides", docSnap.id), updates);
          console.log(`🧹 Removed ${memberName} from daily override: ${docSnap.id}`);
        }
      }

      console.log("🗑️ Custom member removed:", memberName);
    } catch (error) {
      console.error("Error removing custom member:", error);
    }
  };

  // Clean orphaned custom members from daily overrides (members that no longer exist in savedCustomMembers)
  const cleanOrphanedCustomMembers = async () => {
    try {
      const dailyTeamSnap = await getDocs(collection(db, "dailyTeamOverrides"));
      let totalCleaned = 0;

      for (const docSnap of dailyTeamSnap.docs) {
        const data = docSnap.data();
        let needsUpdate = false;
        const updates = {};

        // Check each team's array
        for (const teamKey of ['team1', 'team2', 'team3']) {
          if (Array.isArray(data[teamKey])) {
            const cleanedArray = data[teamKey].filter(uid => {
              // Keep if it's a real employee UID (not custom)
              if (!uid.startsWith || !uid.startsWith("custom:")) return true;
              // Keep if custom member exists in saved list
              const memberName = uid.substring(7);
              return savedCustomMembers.includes(memberName);
            });

            if (cleanedArray.length !== data[teamKey].length) {
              updates[teamKey] = cleanedArray;
              needsUpdate = true;
              totalCleaned += data[teamKey].length - cleanedArray.length;
            }
          }
        }

        // Also check legacy format
        if (Array.isArray(data.team)) {
          const cleanedArray = data.team.filter(uid => {
            if (!uid.startsWith || !uid.startsWith("custom:")) return true;
            const memberName = uid.substring(7);
            return savedCustomMembers.includes(memberName);
          });

          if (cleanedArray.length !== data.team.length) {
            updates.team = cleanedArray;
            needsUpdate = true;
            totalCleaned += data.team.length - cleanedArray.length;
          }
        }

        if (needsUpdate) {
          await updateDoc(doc(db, "dailyTeamOverrides", docSnap.id), updates);
        }
      }

      // Also clean weeklyTeams
      const weeklyTeamsSnap = await getDocs(collection(db, "weeklyTeams"));
      for (const docSnap of weeklyTeamsSnap.docs) {
        const data = docSnap.data();
        let wNeedsUpdate = false;
        const wUpdates = {};
        const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

        for (const day of allDays) {
          if (Array.isArray(data[day])) {
            const cleanedArray = data[day].filter(uid => {
              if (!uid.startsWith || !uid.startsWith("custom:")) return true;
              const memberName = uid.substring(7);
              return savedCustomMembers.includes(memberName);
            });
            if (cleanedArray.length !== data[day].length) {
              wUpdates[day] = cleanedArray;
              wNeedsUpdate = true;
              totalCleaned += data[day].length - cleanedArray.length;
            }
          }
        }

        if (wNeedsUpdate) {
          await updateDoc(doc(db, "weeklyTeams", docSnap.id), wUpdates);
        }
      }

      if (totalCleaned > 0) {
        console.log(`🧹 Cleaned ${totalCleaned} orphaned custom member(s) from daily overrides + weekly teams`);
      }
    } catch (error) {
      console.error("Error cleaning orphaned custom members:", error);
    }
  };

  // Run cleanup when savedCustomMembers loads (only for admin)
  // Only run AFTER data has loaded to prevent race condition
  useEffect(() => {
    if (user?.isAdmin && savedCustomMembersLoaded && dailyTeamOverrides && Object.keys(dailyTeamOverrides).length > 0) {
      cleanOrphanedCustomMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedCustomMembersLoaded, user?.isAdmin]);

  // Save daily team override
  const handleSaveDailyTeam = async (selectedTeam) => {
    try {
      const dateStr = selectedDate;

      // ⚡ PREPARE NEW VALUES before state update
      let newDailyOverrides = { ...dailyTeamOverrides };

      // Get the team-specific override object
      let teamOverrides = { ...newDailyOverrides[activeTeam] };

      if (selectedTeam.length === 0) {
        // Remove override for this team
        delete teamOverrides[dateStr];
      } else {
        // Add/update override for this team
        teamOverrides[dateStr] = selectedTeam;
      }

      // Update the nested structure temporarily to check for duplicates
      newDailyOverrides[activeTeam] = teamOverrides;

      // Check for duplicates with the new configuration across all other teams
      const otherTeams = ["team1", "team2", "team3"].filter(t => t !== activeTeam);
      let duplicates = [];

      for (const otherTeam of otherTeams) {
        const otherTeamMembers = newDailyOverrides[otherTeam]?.[dateStr] || weeklyTeams[otherTeam]?.[getDayOfWeek(dateStr)] || [];
        const teamDuplicates = selectedTeam.filter(uid => otherTeamMembers.includes(uid));
        duplicates = [...duplicates, ...teamDuplicates];
      }

      // Remove duplicate UIDs from the duplicates array
      duplicates = [...new Set(duplicates)];

      if (duplicates.length > 0) {
        const duplicateNames = duplicates.map(uid => {
          // Check if it's a custom person (custom:Name)
          if (uid && uid.startsWith && uid.startsWith("custom:")) {
            return uid.replace("custom:", "");
          }
          // Find employee by ID or UID
          const emp = employees.find(e => e.id === uid || e.uid === uid);
          console.log("🔍 Looking for employee:", uid, "Found:", emp);
          return emp?.name || emp?.displayName || uid;
        });

        console.log("⚠️ Duplicate employees found:", duplicateNames);

        // Show warning modal
        setDuplicateEmployees(duplicateNames);
        setPendingTeamSave({ type: "daily", selectedTeam, newDailyOverrides });
        setShowDuplicateWarning(true);
        return; // Stop here and wait for user confirmation
      }

      // Update the nested structure
      newDailyOverrides[activeTeam] = teamOverrides;

      // Update local state
      setDailyTeamOverrides(newDailyOverrides);

      // Prepare data for Firebase - store all teams in the document
      const firestoreData = {
        team1: newDailyOverrides.team1?.[dateStr] || null,
        team2: newDailyOverrides.team2?.[dateStr] || null,
        team3: newDailyOverrides.team3?.[dateStr] || null,
        updatedAt: Timestamp.now()
      };

      // Check if all teams have no override for this date
      if (!firestoreData.team1 && !firestoreData.team2 && !firestoreData.team3) {
        // Delete the document entirely
        await deleteDoc(doc(db, "dailyTeamOverrides", dateStr)).catch(() => {
          // Document might not exist, ignore error
        });
      } else {
        // Save or update override in Firestore
        await setDoc(doc(db, "dailyTeamOverrides", dateStr), firestoreData, { merge: true });
      }

      setShowDailyTeamModal(false);

      // 🧠 INTELLIGENT RECALCULATION: Update all jobs on this date - pass new values directly!
      await recalculateJobsForDates([dateStr], null, newDailyOverrides);

      alert(language === "en" ? "Daily team saved!" : "Equipe do dia salva!");
    } catch (error) {
      console.error("Error saving daily team:", error);
      alert(language === "en" ? "Error saving team" : "Erro ao salvar equipe");
    }
  };

  // Update weekly default team for this day of week
  const handleUpdateWeeklyDefault = async (selectedTeam) => {
    try {
      const dateStr = selectedDate;
      const dayOfWeek = getDayOfWeek(dateStr);

      // Update the weekly default for this day for the active team
      // ✅ Use the current team's config from weeklyTeams, NOT teamConfig
      const currentTeamConfig = weeklyTeams[activeTeam] || {
        Monday: [],
        Tuesday: [],
        Wednesday: [],
        Thursday: [],
        Friday: [],
        Saturday: [],
        Sunday: []
      };

      const updatedTeamConfig = {
        ...currentTeamConfig,
        [dayOfWeek]: selectedTeam
      };

      // Update the nested structure
      let newWeeklyTeams = { ...weeklyTeams };
      newWeeklyTeams[activeTeam] = updatedTeamConfig;

      // Check for duplicates with all other teams' weekly defaults for the same day
      const otherTeams = ["team1", "team2", "team3"].filter(t => t !== activeTeam);
      let duplicates = [];

      for (const otherTeam of otherTeams) {
        const otherTeamMembers = newWeeklyTeams[otherTeam]?.[dayOfWeek] || [];
        const teamDuplicates = selectedTeam.filter(uid => otherTeamMembers.includes(uid));
        duplicates = [...duplicates, ...teamDuplicates];
      }

      // Remove duplicate UIDs from the duplicates array
      duplicates = [...new Set(duplicates)];

      if (duplicates.length > 0) {
        const duplicateNames = duplicates.map(uid => {
          // Check if it's a custom person (custom:Name)
          if (uid && uid.startsWith && uid.startsWith("custom:")) {
            return uid.replace("custom:", "");
          }
          // Find employee by ID or UID
          const emp = employees.find(e => e.id === uid || e.uid === uid);
          return emp?.name || emp?.displayName || uid;
        });

        // Show warning modal
        setDuplicateEmployees(duplicateNames);
        setPendingTeamSave({ type: "weekly", selectedTeam, newWeeklyTeams, updatedTeamConfig });
        setShowDuplicateWarning(true);
        return; // Stop here and wait for user confirmation
      }

      // ⚡ PREPARE NEW VALUES
      // Update local state (DO NOT update teamConfig - it's only for Team Configuration Modal)
      setWeeklyTeams(newWeeklyTeams);

      // Prepare new daily overrides (remove this date's override if exists for this team)
      let newDailyOverrides = { ...dailyTeamOverrides };
      let teamDailyOverrides = { ...newDailyOverrides[activeTeam] };

      if (teamDailyOverrides[dateStr]) {
        delete teamDailyOverrides[dateStr];
        newDailyOverrides[activeTeam] = teamDailyOverrides;
        setDailyTeamOverrides(newDailyOverrides);
      }

      // Save to Firestore - save the nested structure
      const teamSnap = await getDocs(collection(db, "weeklyTeams"));
      if (!teamSnap.empty) {
        const teamDoc = teamSnap.docs[0];
        await updateDoc(doc(db, "weeklyTeams", teamDoc.id), newWeeklyTeams);
      } else {
        await addDoc(collection(db, "weeklyTeams"), newWeeklyTeams);
      }

      // Remove daily override for this specific date from Firestore if needed
      if (teamDailyOverrides[dateStr] === undefined) {
        // Update the Firebase document to reflect the removal
        const firestoreData = {
          team1: newDailyOverrides.team1?.[dateStr] || null,
          team2: newDailyOverrides.team2?.[dateStr] || null,
          team3: newDailyOverrides.team3?.[dateStr] || null,
          updatedAt: Timestamp.now()
        };

        if (!firestoreData.team1 && !firestoreData.team2 && !firestoreData.team3) {
          await deleteDoc(doc(db, "dailyTeamOverrides", dateStr)).catch(() => {});
        } else {
          await setDoc(doc(db, "dailyTeamOverrides", dateStr), firestoreData, { merge: true });
        }
      }

      setShowDailyTeamModal(false);

      // 🧠 INTELLIGENT RECALCULATION: Update all jobs on this day of week for this team - pass new values directly!
      const datesToRecalculate = jobs
        .filter(job =>
          (job.team || "team1") === activeTeam && // Only jobs for this team
          getDayOfWeek(job.date) === dayOfWeek &&
          !teamDailyOverrides[job.date] // No daily override for this date
        )
        .map(job => job.date);

      if (datesToRecalculate.length > 0) {
        await recalculateJobsForDates(datesToRecalculate, newWeeklyTeams, newDailyOverrides);
      }

      alert(language === "en"
        ? `Weekly default updated for all ${dayOfWeek}s!`
        : `Padrão semanal atualizado para todas as ${dayOfWeek === "Monday" ? "Segundas" : dayOfWeek === "Tuesday" ? "Terças" : dayOfWeek === "Wednesday" ? "Quartas" : dayOfWeek === "Thursday" ? "Quintas" : dayOfWeek === "Friday" ? "Sextas" : dayOfWeek === "Saturday" ? "Sábados" : "Domingos"}!`);
    } catch (error) {
      console.error("Error updating weekly default:", error);
      alert(language === "en" ? "Error updating weekly default" : "Erro ao atualizar padrão semanal");
    }
  };

  // Handle confirmation for duplicate employee warning
  const handleConfirmDuplicate = async () => {
    setShowDuplicateWarning(false);

    if (!pendingTeamSave) return;

    try {
      if (pendingTeamSave.type === "daily") {
        const { selectedTeam, newDailyOverrides } = pendingTeamSave;
        const dateStr = selectedDate;

        // Update the nested structure
        newDailyOverrides[activeTeam] = {
          ...(newDailyOverrides[activeTeam] || {}),
          [dateStr]: selectedTeam
        };

        // Update local state
        setDailyTeamOverrides(newDailyOverrides);

        // Prepare data for Firebase
        const firestoreData = {
          team1: newDailyOverrides.team1?.[dateStr] || null,
          team2: newDailyOverrides.team2?.[dateStr] || null,
          team3: newDailyOverrides.team3?.[dateStr] || null,
          updatedAt: Timestamp.now()
        };

        if (!firestoreData.team1 && !firestoreData.team2 && !firestoreData.team3) {
          await deleteDoc(doc(db, "dailyTeamOverrides", dateStr)).catch(() => {});
        } else {
          await setDoc(doc(db, "dailyTeamOverrides", dateStr), firestoreData, { merge: true });
        }

        setShowDailyTeamModal(false);
        await recalculateJobsForDates([dateStr], null, newDailyOverrides);
        alert(language === "en" ? "Daily team saved!" : "Equipe do dia salva!");

      } else if (pendingTeamSave.type === "weekly") {
        const { selectedTeam, newWeeklyTeams, updatedTeamConfig } = pendingTeamSave;
        const dateStr = selectedDate;
        const dayOfWeek = getDayOfWeek(dateStr);

        // Update local state (DO NOT update teamConfig - it's only for Team Configuration Modal)
        setWeeklyTeams(newWeeklyTeams);

        const teamSnap = await getDocs(collection(db, "weeklyTeams"));

        if (teamSnap.empty) {
          await addDoc(collection(db, "weeklyTeams"), newWeeklyTeams);
        } else {
          const teamDoc = teamSnap.docs[0];
          await updateDoc(doc(db, "weeklyTeams", teamDoc.id), newWeeklyTeams);
        }

        const teamDailyOverrides = dailyTeamOverrides[activeTeam] || {};

        if (teamDailyOverrides[dateStr] === undefined) {
          const firestoreData = {
            team1: dailyTeamOverrides.team1?.[dateStr] || null,
            team2: dailyTeamOverrides.team2?.[dateStr] || null,
            team3: dailyTeamOverrides.team3?.[dateStr] || null,
            updatedAt: Timestamp.now()
          };

          if (!firestoreData.team1 && !firestoreData.team2 && !firestoreData.team3) {
            await deleteDoc(doc(db, "dailyTeamOverrides", dateStr)).catch(() => {});
          } else {
            await setDoc(doc(db, "dailyTeamOverrides", dateStr), firestoreData, { merge: true });
          }
        }

        setShowDailyTeamModal(false);

        const datesToRecalculate = jobs
          .filter(job =>
            (job.team || "team1") === activeTeam &&
            getDayOfWeek(job.date) === dayOfWeek &&
            !teamDailyOverrides[job.date]
          )
          .map(job => job.date);

        if (datesToRecalculate.length > 0) {
          await recalculateJobsForDates(datesToRecalculate, newWeeklyTeams, dailyTeamOverrides);
        }

        alert(language === "en"
          ? `Weekly default updated for all ${dayOfWeek}s!`
          : `Padrão semanal atualizado para todas as ${dayOfWeek === "Monday" ? "Segundas" : dayOfWeek === "Tuesday" ? "Terças" : dayOfWeek === "Wednesday" ? "Quartas" : dayOfWeek === "Thursday" ? "Quintas" : dayOfWeek === "Friday" ? "Sextas" : dayOfWeek === "Saturday" ? "Sábados" : "Domingos"}!`);
      }

      setPendingTeamSave(null);
      setDuplicateEmployees([]);
    } catch (error) {
      console.error("Error saving team with duplicates:", error);
      alert(language === "en" ? "Error saving team" : "Erro ao salvar equipe");
    }
  };

  // Handle canceling duplicate employee warning
  const handleCancelDuplicate = () => {
    setShowDuplicateWarning(false);
    setPendingTeamSave(null);
    setDuplicateEmployees([]);
  };

  // Save team configuration
  const handleSaveTeamConfig = async () => {
    try {
      // ⚡ Update local state - update only the active team's configuration
      let newWeeklyTeams = { ...weeklyTeams };
      newWeeklyTeams[activeTeam] = teamConfig;
      setWeeklyTeams(newWeeklyTeams);

      const teamSnap = await getDocs(collection(db, "weeklyTeams"));

      if (teamSnap.empty) {
        // Create new document with nested structure
        await addDoc(collection(db, "weeklyTeams"), newWeeklyTeams);
      } else {
        // Update existing document with nested structure
        const teamDoc = teamSnap.docs[0];
        await updateDoc(doc(db, "weeklyTeams", teamDoc.id), newWeeklyTeams);
      }

      setShowTeamModal(false);

      // 🧠 INTELLIGENT RECALCULATION: Update all jobs that use weekly defaults for this team - pass new values directly!
      const teamDailyOverrides = dailyTeamOverrides[activeTeam] || {};
      const datesToRecalculate = jobs
        .filter(job =>
          (job.team || "team1") === activeTeam && // Only jobs for this team
          !teamDailyOverrides[job.date] // No daily override for this date
        )
        .map(job => job.date);

      if (datesToRecalculate.length > 0) {
        await recalculateJobsForDates(datesToRecalculate, newWeeklyTeams, dailyTeamOverrides);
      }

      alert(language === "en" ? "Team configuration saved!" : "Configuração de equipes salva!");

      // Real-time listeners will update automatically, no need to reload
      // loadData();
    } catch (error) {
      console.error("Error saving team config:", error);
      alert(language === "en" ? "Error saving configuration" : "Erro ao salvar configuração");
    }
  };

  // Copy Week functionality
  const handleCopyWeek = () => {
    setShowCopyWeekModal(true);
  };

  // Fetch available clients when dates change
  const fetchAvailableClients = async () => {
    if (!whatsAppData.startDate || !whatsAppData.endDate) {
      setAvailableClients([]);
      setSelectedClients([]);
      return;
    }

    try {
      console.log('🔍 Fetching clients...', {
        startDate: whatsAppData.startDate,
        endDate: whatsAppData.endDate,
        activeTeam,
        totalJobs: jobs.length,
        totalClients: clients.length
      });

      // Filter jobs in the date range
      const jobsInRange = jobs.filter(job => {
        const jobDate = job.date;
        return jobDate >= whatsAppData.startDate &&
               jobDate <= whatsAppData.endDate &&
               job.team === activeTeam;
      });

      console.log(`📊 Found ${jobsInRange.length} jobs in range`);

      // Group by client and get unique clients with phone numbers
      const clientMap = new Map();
      jobsInRange.forEach(job => {
        const client = findClientById(job.clientId, job.clientName);
        console.log('Checking job:', {
          clientId: job.clientId,
          clientFound: !!client,
          hasPhone: client?.phone,
          startTime: job.startTime,
          date: job.date
        });

        if (client && client.phone) {
          if (!clientMap.has(client.phone)) {
            clientMap.set(client.phone, {
              phone: client.phone,
              name: client.name,
              jobCount: 0
            });
          }
          clientMap.get(client.phone).jobCount++;
        } else {
          console.log(`❌ Skipping job - client not found or no phone`, { clientId: job.clientId });
        }
      });

      const clientsList = Array.from(clientMap.values());
      console.log(`✅ Found ${clientsList.length} clients with valid phones`, clientsList);

      // Check which clients already received messages in this period
      const startDateTime = new Date(whatsAppData.startDate);
      startDateTime.setHours(0, 0, 0, 0);
      const endDateTime = new Date(whatsAppData.endDate);
      endDateTime.setHours(23, 59, 59, 999);

      // Simplified query to avoid needing composite index
      const whatsappLogsSnap = await getDocs(
        query(
          collection(db, 'whatsappLogs'),
          where('timestamp', '>=', startDateTime),
          where('timestamp', '<=', endDateTime)
        )
      );

      const sentPhones = new Set();
      whatsappLogsSnap.forEach(doc => {
        const log = doc.data();
        // Filter by team and sent status in JavaScript
        if (log.team === activeTeam && log.sent === true) {
          sentPhones.add(log.phone);
        }
      });

      console.log(`📨 Found ${sentPhones.size} clients who already received messages`, Array.from(sentPhones));

      // Add 'alreadySent' flag to clients
      const clientsListWithStatus = clientsList.map(client => ({
        ...client,
        alreadySent: sentPhones.has(client.phone)
      }));

      setAvailableClients(clientsListWithStatus);
      // Select only clients who haven't received messages yet
      const notSentClients = clientsListWithStatus.filter(c => !c.alreadySent).map(c => c.phone);
      setSelectedClients(notSentClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setAvailableClients([]);
      setSelectedClients([]);
    }
  };

  // Auto-fetch clients when dates change
  useEffect(() => {
    if (whatsAppData.startDate && whatsAppData.endDate) {
      fetchAvailableClients();
    }
  }, [whatsAppData.startDate, whatsAppData.endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // WhatsApp Confirmations handler
  const handleSendWhatsApp = async () => {
    if (!whatsAppData.startDate || !whatsAppData.endDate) {
      alert(language === "en" ? "Please select both start and end dates" : "Por favor selecione as datas inicial e final");
      return;
    }

    if (selectedClients.length === 0) {
      alert(language === "en" ? "Please select at least one client" : "Por favor selecione pelo menos um cliente");
      return;
    }

    setSendingWhatsApp(true);
    try {
      console.log(`📤 Starting WhatsApp confirmations for ${selectedClients.length} clients`);

      // 1. Fetch jobs from Firestore for the date range
      console.log('🔍 Fetching jobs from Firestore...');
      const jobsQuery = query(
        collection(db, "jobs"),
        where("date", ">=", whatsAppData.startDate),
        where("date", "<=", whatsAppData.endDate),
        where("team", "==", activeTeam)
      );

      const jobsSnap = await getDocs(jobsQuery);
      console.log(`📊 Found ${jobsSnap.size} jobs`);

      if (jobsSnap.empty) {
        alert(language === "en" ? "No jobs found for this period" : "Nenhum job encontrado para este período");
        return;
      }

      // 2. Load all clients to get data
      const clientsResult = await callXeroFunction('getClients', {}, { method: 'GET' });
      const clientsList = clientsResult.clients || [];
      const clientsMapByName = {};
      const clientsMapById = {};
      clientsList.forEach((client) => {
        const clientData = {
          id: client.id,
          name: client.name,
          phone: client.phone,
          address: client.address,
        };
        clientsMapByName[client.name] = clientData;
        clientsMapById[client.id] = clientData;
      });

      // 3. Group jobs by client phone
      const clientGroups = {};
      jobsSnap.docs.forEach((doc) => {
        const job = doc.data();

        // Support both clientId (new) and clientName (old)
        let clientData = null;
        if (job.clientId) {
          clientData = clientsMapById[job.clientId];
        } else if (job.clientName) {
          clientData = clientsMapByName[job.clientName];
        }

        if (!clientData) {
          console.log(`⏭️  Skipping job - client not found`);
          return;
        }

        // Filter: only clients with valid phone
        if (!clientData.phone) {
          console.log(`⏭️  Skipping ${clientData.name} - No phone`);
          return;
        }

        const phone = clientData.phone;

        // Filter by selected phones
        if (!selectedClients.includes(phone)) {
          return;
        }

        if (!clientGroups[phone]) {
          clientGroups[phone] = {
            clientName: clientData.name,
            phone: phone,
            appointments: [],
          };
        }

        clientGroups[phone].appointments.push({
          date: job.date,
          time: job.startTime,
          address: clientData.address,
          team: job.team,
        });
      });

      const clientGroupsArray = Object.values(clientGroups);
      console.log(`👥 Grouped into ${clientGroupsArray.length} clients`);

      if (clientGroupsArray.length === 0) {
        alert(language === "en" ? "No clients matched the criteria" : "Nenhum cliente corresponde aos critérios");
        return;
      }

      // 4. Send to backend
      console.log(`📞 Sending to backend Easypanel... (messageType: ${whatsAppData.messageType})`);
      const result = await callXeroFunction('whatsappSendConfirmations', {
        clientGroups: clientGroupsArray,
        messageType: whatsAppData.messageType
      });

      alert(`✅ ${result.totalSent} ${language === "en" ? "messages sent successfully!" : "mensagens enviadas com sucesso!"}\n\n${language === "en" ? "Total clients:" : "Total de clientes:"} ${result.totalClients}`);
      setShowWhatsAppModal(false);
      setWhatsAppData({ startDate: "", endDate: "", messageType: "confirmation" }); // Reset form
      setAvailableClients([]);
      setSelectedClients([]);
    } catch (error) {
      console.error('Error sending WhatsApp confirmations:', error);
      alert(`❌ ${language === "en" ? "Error:" : "Erro:"} ${error.message}`);
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleCopyWeekSubmit = async () => {
    if (!copyWeekData.fromDate || !copyWeekData.toDate) {
      alert(language === "en" ? "Please select both weeks" : "Por favor selecione ambas as semanas");
      return;
    }

    try {
      setCopyingWeek(true);

      // Parse dates
      const fromDate = new Date(copyWeekData.fromDate);
      const toDate = new Date(copyWeekData.toDate);

      // Get start of week (Monday) for both dates
      const getMonday = (d) => {
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
      };

      const fromMonday = getMonday(new Date(fromDate));
      const toMonday = getMonday(new Date(toDate));

      // Calculate day difference
      const dayDiff = Math.floor((toMonday - fromMonday) / (1000 * 60 * 60 * 24));

      // Get all jobs from the source week
      const sourceJobs = jobs.filter(job => {
        const jobDate = new Date(job.date);
        const jobMonday = getMonday(new Date(jobDate));
        return jobMonday.getTime() === fromMonday.getTime();
      });

      // Create copies for the target week
      for (const job of sourceJobs) {
        const newDate = new Date(job.date);
        newDate.setDate(newDate.getDate() + dayDiff);

        const newJob = {
          clientId: job.clientId,
          date: newDate.toISOString().split('T')[0],
          startTime: job.startTime,
          endTime: job.endTime,
          notes: job.notes || "",
          password: job.password || "",
          employeeIds: job.employeeIds || [],
          status: "pending",
          invoiceAmount: job.invoiceAmount || 0,
          invoiceSent: false,
          checklist: job.checklist || [],
          photos: [],
          isTimeModified: job.isTimeModified || false,
          recurrenceType: "none",
          recurrenceId: null,
          team: job.team || activeTeam
        };

        await addDoc(collection(db, "jobs"), newJob);
      }

      await reloadJobs();
      setShowCopyWeekModal(false);
      setCopyWeekData({ fromDate: "", toDate: "" });
      alert(language === "en"
        ? `Successfully copied ${sourceJobs.length} jobs`
        : `${sourceJobs.length} jobs copiados com sucesso`);

    } catch (error) {
      console.error("Error copying week:", error);
      alert(language === "en" ? "Error copying week" : "Erro ao copiar semana");
    } finally {
      setCopyingWeek(false);
    }
  };

  // Plan Full Day functionality
  const handlePlanDay = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    setPlanDayData({
      date: today,
      startTime: currentTime,
      team: activeTeam,
      clients: []
    });
    setShowPlanDayModal(true);
  };

  const handleAddClientToPlan = (client) => {
    // Convert hours from "HH:MM" format to decimal hours
    let duration = 2; // Default to 2 hours
    // Use service_duration (PostgreSQL) or hours (Firestore fallback)
    const clientDuration = getServiceDuration(client);
    if (clientDuration) {
      if (typeof clientDuration === 'string' && clientDuration.includes(':')) {
        // Parse "HH:MM" format
        const [hours, minutes] = clientDuration.split(':').map(Number);
        duration = hours + (minutes / 60);
      } else {
        // If it's already a number, use it directly
        duration = parseFloat(clientDuration) || 2;
      }
    }

    const newClient = {
      clientId: client.id,
      clientName: client.name,
      duration: duration,
      intervalAfter: 15 // Default 15 minutes interval
    };

    setPlanDayData(prev => ({
      ...prev,
      clients: [...prev.clients, newClient]
    }));
  };

  const handleRemoveClientFromPlan = (index) => {
    setPlanDayData(prev => ({
      ...prev,
      clients: prev.clients.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateClientInterval = (index, intervalMinutes) => {
    setPlanDayData(prev => ({
      ...prev,
      clients: prev.clients.map((c, i) =>
        i === index ? { ...c, intervalAfter: parseInt(intervalMinutes) || 0 } : c
      )
    }));
  };

  const calculateJobTimes = () => {
    if (!planDayData.date || !planDayData.startTime || planDayData.clients.length === 0) {
      return [];
    }

    const jobs = [];
    let currentTime = new Date(`${planDayData.date}T${planDayData.startTime}`);

    planDayData.clients.forEach((client, index) => {
      const startTime = `${String(currentTime.getHours()).padStart(2, '0')}:${String(currentTime.getMinutes()).padStart(2, '0')}`;

      // Add duration to get end time
      const endTime = new Date(currentTime.getTime() + (client.duration * 60 * 60 * 1000));
      const endTimeStr = `${String(endTime.getHours()).padStart(2, '0')}:${String(endTime.getMinutes()).padStart(2, '0')}`;

      jobs.push({
        clientName: client.clientName,
        startTime: startTime,
        endTime: endTimeStr,
        duration: client.duration
      });

      // Add interval for next job (except for last one)
      if (index < planDayData.clients.length - 1) {
        currentTime = new Date(endTime.getTime() + (client.intervalAfter * 60 * 1000));
      }
    });

    return jobs;
  };

  const handleCreateDayPlan = async () => {
    if (!planDayData.date || !planDayData.startTime) {
      alert(language === "en" ? "Please select date and start time" : "Por favor selecione data e hora de início");
      return;
    }

    if (planDayData.clients.length === 0) {
      alert(language === "en" ? "Please add at least one client" : "Por favor adicione pelo menos um cliente");
      return;
    }

    try {
      setCreatingDayPlan(true);

      const jobTimes = calculateJobTimes();
      console.log("📅 Planning to create", planDayData.clients.length, "jobs");
      console.log("Job times calculated:", jobTimes);

      let createdCount = 0;
      const errors = [];

      // Create all jobs
      for (let i = 0; i < planDayData.clients.length; i++) {
        try {
          const client = planDayData.clients[i];
          const jobTime = jobTimes[i];

          if (!jobTime) {
            console.error(`❌ No job time calculated for client ${i}:`, client);
            errors.push(`${client.clientName}: Missing job time`);
            continue;
          }

          const newJob = {
            clientId: client.clientId,
            date: planDayData.date,
            startTime: jobTime.startTime,
            endTime: jobTime.endTime,
            notes: "",
            employeeIds: [],
            status: "pending",
            invoiceAmount: 0,
            invoiceSent: false,
            checklist: [],
            photos: [],
            isTimeModified: false,
            recurrenceType: "none",
            recurrenceId: null,
            team: planDayData.team
          };

          console.log(`Creating job ${i + 1}:`, newJob);
          await addDoc(collection(db, "jobs"), newJob);
          createdCount++;
          console.log(`✅ Job ${i + 1} created successfully`);

        } catch (jobError) {
          console.error(`❌ Error creating job ${i + 1}:`, jobError);
          errors.push(`${planDayData.clients[i].clientName}: ${jobError.message}`);
        }
      }

      await reloadJobs();
      setShowPlanDayModal(false);
      setPlanDayData({
        date: "",
        startTime: "",
        team: "team1",
        clients: []
      });

      if (errors.length > 0) {
        alert(language === "en"
          ? `Created ${createdCount} jobs. ${errors.length} failed:\n${errors.join('\n')}`
          : `${createdCount} jobs criados. ${errors.length} falharam:\n${errors.join('\n')}`);
      } else {
        alert(language === "en"
          ? `Successfully created ${createdCount} jobs`
          : `${createdCount} jobs criados com sucesso`);
      }

    } catch (error) {
      console.error("Error creating day plan:", error);
      alert(language === "en" ? "Error creating jobs" : "Erro ao criar jobs");
    } finally {
      setCreatingDayPlan(false);
    }
  };

  // Handle client selection in Plan Day modal
  const handlePlanDayClientSelect = (client) => {
    if (client.id === null) {
      // "Add New Client" was clicked
      setShowPlanDayNewClient(true);
      setPlanDayNewClient({
        name: planDayClientQuery,
        address: "",
        hours: "2:00",
        paymentMethod: "cash",
        defaultInvoiceAmount: ""
      });
    } else {
      // Existing client was selected
      handleAddClientToPlan(client);
      setPlanDayClientQuery("");
    }
  };

  // Save new client from Plan Day modal and add to plan
  const handleSavePlanDayNewClient = async () => {
    if (!planDayNewClient.name.trim()) {
      alert(language === "en" ? "Please enter client name" : "Por favor insira o nome do cliente");
      return;
    }

    if (!planDayNewClient.address.trim()) {
      alert(language === "en" ? "Please enter address" : "Por favor insira o endereço");
      return;
    }

    if (!planDayNewClient.hours.trim()) {
      alert(language === "en" ? "Please enter hours" : "Por favor insira as horas");
      return;
    }

    try {
      // Create new client via REST API
      const clientData = {
        name: planDayNewClient.name.trim(),
        address: planDayNewClient.address.trim(),
        service_duration: planDayNewClient.hours.trim(),
        payment_method: planDayNewClient.paymentMethod,
        default_price: parseFloat(planDayNewClient.defaultInvoiceAmount) || 0,
        default_checklist: JSON.stringify([]),
        restricted_times: null,
        notes: null
      };

      const result = await callXeroFunction('createClient', clientData, { method: 'POST' });
      const savedClient = result.client;

      // Reload clients list
      const clientsResult = await callXeroFunction('getClients', {}, { method: 'GET' });
      setClients(clientsResult.clients || []);

      // Add the new client to the plan
      const [hours, minutes] = planDayNewClient.hours.split(':').map(Number);
      const duration = hours + (minutes / 60);

      const newClient = {
        clientId: savedClient.id,
        clientName: planDayNewClient.name.trim(),
        duration: duration,
        intervalAfter: 15
      };

      setPlanDayData(prev => ({
        ...prev,
        clients: [...prev.clients, newClient]
      }));

      // Reset form
      setShowPlanDayNewClient(false);
      setPlanDayNewClient({
        name: "",
        address: "",
        hours: "2:00",
        paymentMethod: "cash",
        defaultInvoiceAmount: ""
      });
      setPlanDayClientQuery("");

      alert(language === "en" ? "Client saved successfully" : "Cliente salvo com sucesso");

    } catch (error) {
      console.error("Error saving client:", error);
      alert(language === "en" ? "Error saving client" : "Erro ao salvar cliente");
    }
  };

  // Toggle employee in team for a specific day
  const toggleEmployeeInTeam = (day, employeeId) => {
    setTeamConfig(prev => {
      const dayTeam = prev[day] || [];
      const isInTeam = dayTeam.includes(employeeId);

      return {
        ...prev,
        [day]: isInTeam
          ? dayTeam.filter(id => id !== employeeId)
          : [...dayTeam, employeeId]
      };
    });
  };

  // Custom event content renderer with checkbox
  const renderEventContent = (eventInfo) => {
    // Notice: banner render
    if (eventInfo.event.extendedProps?.isNotice) {
      return (
        <div className="calendar-notice-event" style={{
          padding: '2px 8px',
          fontWeight: '700',
          fontSize: '0.8rem',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {eventInfo.event.title}
        </div>
      );
    }

    // Lunch break: simplified render (no time, no extras)
    if (eventInfo.event.extendedProps?.isLunchBreak) {
      return (
        <div className="fc-event-main-frame" style={{ position: 'relative', width: '100%', height: '100%' }}>
          <div style={{ padding: '3px 6px', fontSize: '0.75rem', fontWeight: '600' }}>
            {eventInfo.event.title}
          </div>
        </div>
      );
    }

    const job = jobs.find(j => j.id === eventInfo.event.id);
    // Use findClientById with clientName fallback for legacy jobs
    const client = findClientById(job?.clientId, job?.clientName);

    const isXeroClient = getPaymentMethod(client) === 'xero';
    const isSelected = selectedInvoices.includes(eventInfo.event.id);
    const invoiceSent = job?.invoiceSent || false;
    const showInvoiceIndicator = invoiceSent && user?.isAdmin; // Only show to admins

    // Calculate event duration in minutes
    const start = eventInfo.event.start;
    const end = eventInfo.event.end;
    const durationMinutes = end ? (end - start) / (1000 * 60) : 60; // Default 60 if no end
    const isShortEvent = durationMinutes <= 20; // 20 minutes or less

    // Format duration string
    const durationStr = durationMinutes >= 60
      ? `${Math.floor(durationMinutes / 60)}h${durationMinutes % 60 > 0 ? (durationMinutes % 60) : ''}`
      : `${Math.round(durationMinutes)}m`;

    // Short address (first part before comma)
    const shortAddress = client?.address ? client.address.split(',')[0].trim() : '';

    // Payment method icon
    const paymentIcon = isXeroClient ? '💳' : '💵';

    // Rich tooltip with full details
    const tooltipLines = [`${client?.name || 'No Client'} — ${eventInfo.timeText} (${durationStr})`];
    if (job?.visitType === "first_clean") tooltipLines.push(`🆕 First Clean (+${job.firstCleanExtra || 60}min)`);
    if (job?.visitType === "touchup") tooltipLines.push('↩️ Touch-up / Return ($0)');
    if (client?.address) tooltipLines.push(`📍 ${client.address}`);
    tooltipLines.push(isXeroClient ? '💳 Xero Invoice' : '💵 Cash');
    if (showInvoiceIndicator) tooltipLines.push('✅ Invoice Sent');
    if (job?.checklist?.length > 0) {
      const done = job.checklist.filter(i => i.done).length;
      tooltipLines.push(`📋 ${done}/${job.checklist.length} checklist`);
    }
    if (job?.notes) tooltipLines.push(`📝 ${job.notes.substring(0, 100)}`);
    const tooltipText = tooltipLines.join('\n');

    return (
      <div
        className="fc-event-main-frame"
        style={{ position: 'relative', width: '100%', height: '100%' }}
        title={tooltipText}
      >
        {((isXeroClient && selectionMode === 'invoices') || selectionMode === 'confirmations') && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => handleInvoiceToggle(eventInfo.event.id, e)}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: '4px',
              top: '4px',
              width: '16px',
              height: '16px',
              cursor: 'pointer',
              zIndex: 10,
              backgroundColor: selectionMode === 'confirmations' ? '#3b82f6' : 'white'
            }}
          />
        )}
        {showInvoiceIndicator && (
          <div
            style={{
              position: 'absolute',
              right: '2px',
              top: '2px',
              fontSize: '14px',
              lineHeight: '1',
              color: '#22c55e',
              fontWeight: 'bold',
              textShadow: '0 0 2px white',
              zIndex: 10
            }}
          >
            ✓
          </div>
        )}
        <div style={{
          paddingLeft: isXeroClient ? '24px' : '6px',
          paddingRight: showInvoiceIndicator ? '18px' : '4px',
          paddingTop: '3px',
          fontSize: '0.75rem',
          lineHeight: '1.2',
          overflow: 'hidden',
          display: isShortEvent ? 'flex' : 'block',
          alignItems: 'center',
          gap: '4px'
        }}>
          <div className="fc-event-time" style={{
            fontSize: '0.65rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            opacity: 0.85,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            {eventInfo.timeText}
            <span style={{ opacity: 0.7, fontSize: '0.58rem' }}>• {durationStr}</span>
          </div>
          <div className="fc-event-title" style={{
            fontSize: '0.72rem',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: isShortEvent ? 1 : 'none',
            letterSpacing: '-0.01em'
          }}>
            {eventInfo.event.title}
          </div>
          {/* Short address - visible only on taller events */}
          {!isShortEvent && shortAddress && durationMinutes >= 40 && (
            <div style={{
              fontSize: '0.58rem',
              opacity: 0.65,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: '1px'
            }}>
              📍 {shortAddress}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render team members for a specific date
  const renderTeamMembers = (dateStr) => {
    const teamForDay = getTeamForDate(dateStr);
    const hasOverride = !!dailyTeamOverrides[activeTeam][dateStr];
    const isEditable = user?.isAdmin || user?.isClientManager; // Apenas admins e managers podem editar

    if (teamForDay.length === 0) {
      return (
        <div
          className="team-members-section mb-3 p-3"
          style={{
            backgroundColor: darkTheme ? '#1e2230' : '#f8fafc',
            borderRadius: '8px',
            border: `2px solid ${darkTheme ? '#334155' : '#e2e8f0'}`,
            cursor: isEditable ? 'pointer' : 'default'
          }}
          onClick={() => {
            if (isEditable) {
              setSelectedDate(dateStr);
              setShowDailyTeamModal(true);
            }
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                {language === "en" ? "Team for this day" : "Equipe para este dia"}
              </small>
              <div className="mt-1 text-muted" style={{ fontSize: '0.9rem' }}>
                {language === "en" ? "No team members assigned" : "Nenhum membro atribuído"}
              </div>
            </div>
            {isEditable && (
              <span style={{ fontSize: '1.2rem' }}>✏️</span>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        className="team-members-section mb-3 p-3"
        style={{
          backgroundColor: darkTheme ? '#1e2230' : '#f8fafc',
          borderRadius: '8px',
          border: `2px solid ${hasOverride ? '#3b82f6' : (darkTheme ? '#334155' : '#e2e8f0')}`,
          cursor: isEditable ? 'pointer' : 'default'
        }}
        onClick={() => {
          if (isEditable) {
            setSelectedDate(dateStr);
            setShowDailyTeamModal(true);
          }
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-2">
          <small className="text-muted" style={{ fontSize: '0.75rem' }}>
            {language === "en" ? "Team for this day" : "Equipe para este dia"}
            {hasOverride && (
              <Badge bg="primary" className="ms-2" style={{ fontSize: '0.65rem' }}>
                {language === "en" ? "Custom" : "Personalizado"}
              </Badge>
            )}
          </small>
          {isEditable && (
            <span style={{ fontSize: '1.2rem' }}>✏️</span>
          )}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {teamForDay.map((uid, idx) => {
            let displayName = "";
            let initials = "?";

            if (uid.startsWith("custom:")) {
              displayName = uid.substring(7);
              initials = displayName.substring(0, 2).toUpperCase();
            } else {
              const emp = employees.find(e => e.id === uid);
              displayName = emp?.displayName || "Unknown";
              const nameParts = displayName.split(" ");
              initials = nameParts.length > 1
                ? (nameParts[0][0] + nameParts[1][0]).toUpperCase()
                : displayName.substring(0, 2).toUpperCase();
            }

            return (
              <div
                key={idx}
                className="d-flex align-items-center gap-2 px-3 py-2"
                style={{
                  backgroundColor: darkTheme ? '#334155' : '#ffffff',
                  borderRadius: '20px',
                  border: `1px solid ${darkTheme ? '#64748b' : '#cbd5e1'}`,
                  fontSize: '0.85rem'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '0.75rem'
                  }}
                >
                  {initials}
                </div>
                <span>{displayName.split(" ")[0]}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Generate time slots for the day (7am to 6pm in 15min increments)
  // Memoized: time slots array is constant (07:00 to 18:00, every 15min)
  const generateTimeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 7; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        if (hour === 18 && minute > 0) break;
        const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        slots.push(timeStr);
      }
    }
    return slots;
  }, []);

  // Render mobile list view with cards
  const renderMobileListView = () => {
    const filteredJobs = getFilteredJobs();
    const locale = language === "pt" ? ptBR : enUS;

    if (filteredJobs.length === 0) {
      // Even with no jobs, render notices for the relevant date(s)
      const noJobsNotices = (() => {
        if (mobileViewType === "week") {
          const selectedDate = new Date(selectedMobileDate + 'T00:00:00');
          const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
          const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
          return notices.filter(n => {
            const nDate = new Date(n.date + 'T00:00:00');
            return nDate >= weekStart && nDate <= weekEnd && (n.team === "all" || n.team === activeTeam);
          });
        } else {
          return notices.filter(n => n.date === selectedMobileDate && (n.team === "all" || n.team === activeTeam));
        }
      })();

      return (
        <div>
          {noJobsNotices.map(n => (
            <div
              key={`notice-nojobs-${n.id}`}
              className="notice-mobile-card mb-2"
              onClick={() => openNotice(n)}
              style={{
                backgroundColor: NOTICE_COLORS[n.color] || NOTICE_COLORS.blue,
                color: "#fff",
                padding: '10px 16px',
                borderRadius: '10px',
                fontWeight: '700',
                fontSize: '0.9rem',
                textAlign: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
              }}
            >
              {n.title}
              {n.description && (
                <div style={{ fontWeight: '400', fontSize: '0.8rem', marginTop: '2px', opacity: 0.9 }}>
                  {n.description}
                </div>
              )}
            </div>
          ))}
          <div className="text-center py-5">
            <p className="text-muted">
              {language === "en" ? "No jobs scheduled for this period" : "Nenhum job agendado para este período"}
            </p>
          </div>
        </div>
      );
    }

    // Group by date when in week view mode
    const shouldGroupByDate = mobileViewType === "week";
    let groupedJobs = {};

    if (shouldGroupByDate) {
      filteredJobs.forEach(job => {
        if (!groupedJobs[job.date]) {
          groupedJobs[job.date] = [];
        }
        groupedJobs[job.date].push(job);
      });
    }

    const renderJobCard = (job, showDate = true) => {
      // Handle lunch break cards differently
      if (job.isLunchBreak) {
        return (
          <div
            key={job.id}
            className="mobile-job-card mb-3"
            style={{
              backgroundColor: darkTheme ? '#1d4ed8' : '#bee3f8',
              border: `2px solid ${darkTheme ? '#2563eb' : '#93c5fd'}`,
              borderRadius: '12px',
              padding: '16px',
              cursor: 'default',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1" style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: darkTheme ? '#fff' : '#1e3a5f'
                }}>
                  🍽️ {language === "en" ? "Lunch Break" : "Almoço"}
                </h5>
              </div>
              <div style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: darkTheme ? '#93c5fd' : '#1d4ed8'
              }}>
                {job.startTime} - {job.endTime}
              </div>
            </div>
          </div>
        );
      }

      // Regular job card - use findClientById with clientName fallback for legacy jobs
      const client = findClientById(job.clientId, job.clientName);

      // Proteção contra datas inválidas
      const jobDate = job.date ? parseISO(job.date) : null;
      const formattedDate = jobDate && !isNaN(jobDate.getTime())
        ? fnsFormat(jobDate, "EEE, dd MMM", { locale })
        : "Data inválida";
      const statusColors = {
        scheduled: 'primary',
        'in-progress': 'warning',
        completed: 'success',
        cancelled: 'danger'
      };
      const isXeroClient = getPaymentMethod(client) === 'xero';
      const showInvoiceIndicator = isXeroClient && job.invoiceSent && user?.isAdmin;

      // Duration for mobile card
      const [sH, sM] = (job.startTime || '0:0').split(':').map(Number);
      const [eH, eM] = (job.endTime || '0:0').split(':').map(Number);
      const jobDur = (eH * 60 + eM) - (sH * 60 + sM);
      const jobDurStr = jobDur >= 60 ? `${Math.floor(jobDur / 60)}h${jobDur % 60 > 0 ? (jobDur % 60) : ''}` : `${jobDur}m`;

      // Recurrence color for left border accent
      const recType = job.recurrence === "none" && job.originalRecurrence
        ? job.originalRecurrence : (job.recurrence || "none");
      const recColor = { weekly: '#f59e0b', fortnightly: '#8b5cf6', '3weekly': '#64748b', monthly: '#64748b' }[recType] || '#3b82f6';

      // Checklist progress
      const checklistItems = job.checklist || [];
      const checklistDone = checklistItems.filter(i => i.done).length;
      const checklistTotal = checklistItems.length;
      const checklistPct = checklistTotal > 0 ? Math.round((checklistDone / checklistTotal) * 100) : 0;

      return (
        <div
          key={job.id}
          className="mobile-job-card mb-3"
              onClick={(e) => {
                const fullJob = jobs.find(j => j.id === job.id);
                const client = findClientById(fullJob.clientId, fullJob.clientName);

                console.log("📱 Mobile job click:", {
                  jobId: fullJob.id,
                  clientId: fullJob.clientId,
                  clientFound: !!client,
                  jobPassword: fullJob.password,
                  clientPassword: client?.password,
                  finalPassword: fullJob.password || client?.password || ""
                });

                setSelectedJob(fullJob);
                setFormData({
                  clientId: fullJob.clientId || "",
                  date: fullJob.date || "",
                  startTime: fullJob.startTime || "",
                  endTime: fullJob.endTime || "",
                  notes: fullJob.notes || client?.notes || "",
                  password: fullJob.password || client?.password || "",
                  checklist: fullJob.checklist || client?.checklist || [],
                  status: fullJob.status || "scheduled",
                  recurrence: fullJob.recurrence || "none",
                  uploadedFiles: fullJob.uploadedFiles || [],
                  invoiceAmount: fullJob.invoiceAmount || 0,
                  timeModified: fullJob.timeModified || false,
                  timeModificationReason: fullJob.timeModificationReason || "",
                  originalEndTime: fullJob.originalEndTime || fullJob.endTime || "",
                  repeatUntil: "indefinite",
                  repeatUntilDate: ""
                });
                setUploadedPhotos(fullJob.uploadedFiles || []);
                // All users can edit jobs to upload photos
                setModalMode("edit");
                setShowModal(true);
              }}
              style={{
                position: 'relative',
                backgroundColor: darkTheme ? '#1e2230' : '#fff',
                border: `2px solid ${darkTheme ? '#334155' : '#e2e8f0'}`,
                borderLeft: `5px solid ${recColor}`,
                borderRadius: '12px',
                padding: '14px 14px 14px 16px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                userSelect: 'none',
                WebkitUserSelect: 'none'
              }}
            >
              {/* Header: Name + Status */}
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h5 className="mb-0" style={{
                    fontSize: '1.05rem',
                    fontWeight: '600',
                    color: darkTheme ? '#fff' : '#0f1117',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {client?.name || 'No Client'}
                  </h5>
                  {showDate && (
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                      {formattedDate}
                    </div>
                  )}
                </div>
                <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                  {showInvoiceIndicator && (
                    <Badge bg="success" style={{ fontSize: '0.7rem' }}>✓</Badge>
                  )}
                  <Badge bg={statusColors[job.status] || 'secondary'} style={{ fontSize: '0.7rem' }}>
                    {job.status}
                  </Badge>
                </div>
              </div>

              {/* Time + Duration */}
              <div className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: darkTheme ? '#60a5fa' : '#3b82f6'
                }}>
                  {job.startTime} - {job.endTime}
                </div>
                <span style={{
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  color: darkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: darkTheme ? '#0f1117' : '#f1f5f9',
                  padding: '2px 6px',
                  borderRadius: '6px'
                }}>
                  {jobDurStr}
                </span>
              </div>

              {/* Address */}
              {client?.address && (
                <div className="text-muted" style={{
                  fontSize: '0.8rem',
                  marginBottom: '6px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  📍 {client.address}
                </div>
              )}

              {/* Notes */}
              {job.notes && (
                <div style={{
                  fontSize: '0.85rem',
                  color: darkTheme ? '#94a3b8' : '#64748b',
                  marginTop: '6px',
                  paddingTop: '6px',
                  borderTop: `1px solid ${darkTheme ? '#2a2d35' : '#f1f5f9'}`,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  📝 {job.notes.length > 60 ? `${job.notes.substring(0, 60)}...` : job.notes}
                </div>
              )}

              {/* Checklist progress bar */}
              {checklistTotal > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div className="d-flex justify-content-between align-items-center" style={{ fontSize: '0.7rem', marginBottom: '3px' }}>
                    <span style={{ color: darkTheme ? '#94a3b8' : '#64748b' }}>
                      📋 {checklistDone}/{checklistTotal}
                    </span>
                    <span style={{ color: checklistPct === 100 ? '#22c55e' : (darkTheme ? '#64748b' : '#94a3b8') }}>
                      {checklistPct}%
                    </span>
                  </div>
                  <div style={{
                    height: '4px',
                    backgroundColor: darkTheme ? '#0f1117' : '#e2e8f0',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${checklistPct}%`,
                      height: '100%',
                      backgroundColor: checklistPct === 100 ? '#22c55e' : '#3b82f6',
                      borderRadius: '2px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}
            </div>
          );
    };

    // Render timeline view with time slots
    const renderTimelineDay = (date, jobsForDate) => {
      const timeSlots = generateTimeSlots;
      const dateObj = parseISO(date);
      const formattedDate = fnsFormat(dateObj, "EEEE, dd 'de' MMMM", { locale });

      return (
        <div key={date} className="mb-4">
          {/* Team Members for this day */}
          {renderTeamMembers(date)}

          {/* Date Header - Melhorado */}
          <div style={{
            background: darkTheme
              ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)'
              : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '16px',
            fontWeight: '600',
            fontSize: '1.05rem',
            color: '#fff',
            textTransform: 'capitalize',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <div>
              <div style={{ marginBottom: '4px' }}>
                {formattedDate}
              </div>
              <div style={{
                fontSize: '0.8rem',
                opacity: 0.9,
                fontWeight: '400'
              }}>
                {jobsForDate.filter(j => !j.isLunchBreak).length} job{jobsForDate.filter(j => !j.isLunchBreak).length !== 1 ? 's' : ''} agendado{jobsForDate.filter(j => !j.isLunchBreak).length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Mobile Notice Cards for this day */}
          {notices
            .filter(n => n.date === date && (n.team === "all" || n.team === activeTeam))
            .map(n => (
              <div
                key={`notice-mobile-${n.id}`}
                className="notice-mobile-card mb-2"
                onClick={() => openNotice(n)}
                style={{
                  backgroundColor: NOTICE_COLORS[n.color] || NOTICE_COLORS.blue,
                  color: "#fff",
                  padding: '10px 16px',
                  borderRadius: '10px',
                  fontWeight: '700',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                }}
              >
                {n.title}
                {n.description && (
                  <div style={{ fontWeight: '400', fontSize: '0.8rem', marginTop: '2px', opacity: 0.9 }}>
                    {n.description}
                  </div>
                )}
              </div>
            ))
          }

          {/* Timeline Grid */}
          <div
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              display: 'flex',
              overflow: 'auto',
              maxHeight: '70vh',
              touchAction: 'pan-y'
            }}>
            {/* Time labels column - Melhorado */}
            <div style={{
              width: '65px',
              flexShrink: 0,
              borderRight: `2px solid ${darkTheme ? '#334155' : '#cbd5e1'}`,
              background: darkTheme ? '#0f1117' : '#f8fafc'
            }}>
              {timeSlots.map(slot => {
                const isHourMark = slot.endsWith(':00');
                return (
                  <div key={`time-${slot}`} style={{
                    height: `${30 * zoomScale}px`,
                    padding: '2px',
                    fontSize: `${(isHourMark ? 0.75 : 0.65) * zoomScale}rem`,
                    fontWeight: isHourMark ? '600' : '400',
                    color: isHourMark
                      ? (darkTheme ? '#e2e8f0' : '#1e2230')
                      : (darkTheme ? '#64748b' : '#94a3b8'),
                    borderBottom: isHourMark
                      ? `2px solid ${darkTheme ? '#334155' : '#cbd5e1'}`
                      : `1px dashed ${darkTheme ? '#1e2230' : '#e2e8f0'}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    transition: 'height 0.1s ease-out, font-size 0.1s ease-out'
                  }}>
                    {slot}
                  </div>
                );
              })}
            </div>

            {/* Jobs column with drop zones */}
            <div style={{ flex: 1, position: 'relative', minHeight: `${timeSlots.length * 30 * zoomScale}px` }}>
              {/* Drop zones - Melhorado */}
              {timeSlots.map(slot => {
                const isHourMark = slot.endsWith(':00');
                return (
                  <div
                    key={`slot-${slot}`}
                    data-time-slot={slot}
                    data-date={date}
                    onClick={(user?.isAdmin || user?.isClientManager) && !isDragging ? () => {
                      // Click on empty time slot to create job at that time
                      const [slotH, slotM] = slot.split(':').map(Number);
                      const endH = slotH + 2;
                      const endTime = `${String(endH).padStart(2, '0')}:${String(slotM).padStart(2, '0')}`;
                      setFormData({
                        clientId: "",
                        date: date,
                        startTime: slot,
                        endTime: endTime,
                        notes: "",
                        password: "",
                        checklist: [],
                        status: "scheduled",
                        recurrence: "none",
                        uploadedFiles: [],
                        invoiceAmount: 0,
                        timeModified: false,
                        timeModificationReason: "",
                        originalEndTime: "",
                        repeatUntil: "indefinite",
                        repeatUntilDate: ""
                      });
                      setUploadedPhotos([]);
                      setClientSearchQuery("");
                      setSelectedJob(null);
                      setSelectedEvent(null);
                      setModalMode("create");
                      setShowModal(true);
                    } : undefined}
                    style={{
                      height: `${30 * zoomScale}px`,
                      borderBottom: isHourMark
                        ? `2px solid ${darkTheme ? '#334155' : '#cbd5e1'}`
                        : `1px dashed ${darkTheme ? '#1e2230' : '#e2e8f0'}`,
                      position: 'relative',
                      backgroundColor: isDragging ? (darkTheme ? 'rgba(66, 153, 225, 0.1)' : 'rgba(66, 153, 225, 0.05)') : 'transparent',
                      transition: 'background-color 0.15s, height 0.1s ease-out',
                      cursor: (user?.isAdmin || user?.isClientManager) ? 'pointer' : 'default'
                    }}
                  />
                );
              })}

              {/* Jobs positioned absolutely */}
              {jobsForDate.filter(j => !j.isLunchBreak).map(job => {
                // Calculate position based on start time
                const [startHour, startMin] = job.startTime.split(':').map(Number);
                const [endHour, endMin] = job.endTime.split(':').map(Number);

                const startMinutes = (startHour - 7) * 60 + startMin;
                const duration = ((endHour * 60 + endMin) - (startHour * 60 + startMin));
                // Each time slot = 30px height for 15 minutes base, then multiply by zoom
                // So 2px per minute * zoomScale
                const top = startMinutes * 2 * zoomScale;
                const height = duration * 2 * zoomScale;

                const client = findClientById(job.clientId, job.clientName);
                const isBeingDragged = isDragging && draggedJob?.id === job.id;
                const canDrag = user?.isAdmin || user?.isClientManager;

                // Determine color based on status, time modification, and recurrence (same as web calendar)
                let backgroundColor, borderColor;
                if (job.timeModified) {
                  backgroundColor = "#ef4444"; borderColor = "#dc2626";
                } else if (job.status === "completed") {
                  backgroundColor = "#22c55e"; borderColor = "#16a34a";
                } else if (job.status === "cancelled") {
                  backgroundColor = "#ef4444"; borderColor = "#dc2626";
                } else {
                  const recurrenceType = job.recurrence === "none" && job.originalRecurrence
                    ? job.originalRecurrence
                    : job.recurrence;

                  switch (recurrenceType) {
                    case "weekly":
                      backgroundColor = "#f59e0b"; borderColor = "#d97706"; break;
                    case "fortnightly":
                      backgroundColor = "#8b5cf6"; borderColor = "#7c3aed"; break;
                    case "3weekly":
                      backgroundColor = "#64748b"; borderColor = "#475569"; break;
                    case "monthly":
                      backgroundColor = "#64748b"; borderColor = "#475569"; break;
                    default:
                      backgroundColor = "#3b82f6"; borderColor = "#2563eb";
                  }
                }

                // Duration and address for mobile card
                const [sh, sm] = job.startTime.split(':').map(Number);
                const [eh, em] = job.endTime.split(':').map(Number);
                const dur = (eh * 60 + em) - (sh * 60 + sm);
                const durStr = dur >= 60 ? `${Math.floor(dur / 60)}h${dur % 60 > 0 ? (dur % 60) : ''}` : `${dur}m`;
                const mobAddress = client?.address ? client.address.split(',')[0].trim() : '';

                const statusColor = { bg: backgroundColor, border: borderColor };

                return (
                  <div
                    key={job.id}
                    onClick={() => {
                      // Tap on block = open job details (unless dragging)
                      if (isDraggingRef.current) return;
                      const fullJob = jobs.find(j => j.id === job.id);
                      const client = findClientById(fullJob.clientId, fullJob.clientName);
                      const clientChecklist = (() => {
                        if (Array.isArray(client?.default_checklist)) return client.default_checklist;
                        if (client?.default_checklist && typeof client.default_checklist === 'string') {
                          try { return JSON.parse(client.default_checklist); } catch (e) { /* ignore */ }
                        }
                        return client?.checklist || [];
                      })();
                      setSelectedJob(fullJob);
                      setFormData({
                        clientId: fullJob.clientId || "",
                        date: fullJob.date || "",
                        startTime: fullJob.startTime || "",
                        endTime: fullJob.endTime || "",
                        notes: fullJob.notes || client?.notes || "",
                        password: fullJob.password || client?.password || "",
                        checklist: fullJob.checklist || clientChecklist,
                        status: fullJob.status || "scheduled",
                        recurrence: fullJob.recurrence || "none",
                        uploadedFiles: fullJob.uploadedFiles || [],
                        invoiceAmount: fullJob.invoiceAmount || client?.default_price || client?.defaultInvoiceAmount || 0,
                        timeModified: fullJob.timeModified || false,
                        timeModificationReason: fullJob.timeModificationReason || "",
                        originalEndTime: fullJob.originalEndTime || fullJob.endTime || "",
                        repeatUntil: "indefinite",
                        repeatUntilDate: ""
                      });
                      setUploadedPhotos(fullJob.uploadedFiles || []);
                      setModalMode("edit");
                      setShowModal(true);
                    }}
                    style={{
                      position: 'absolute',
                      top: `${top}px`,
                      left: `${8 * zoomScale}px`,
                      right: `${8 * zoomScale}px`,
                      height: `${Math.max(height, 50 * zoomScale)}px`,
                      background: `linear-gradient(135deg, ${statusColor.bg} 0%, ${statusColor.border} 100%)`,
                      border: `${2 * zoomScale}px solid ${statusColor.border}`,
                      borderRadius: `${8 * zoomScale}px`,
                      borderLeft: `${4 * zoomScale}px solid ${statusColor.border}`,
                      padding: `${6 * zoomScale}px ${6 * zoomScale}px ${6 * zoomScale}px ${8 * zoomScale}px`,
                      cursor: 'pointer',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      overflow: 'hidden',
                      boxShadow: isBeingDragged
                        ? '0 12px 24px rgba(0,0,0,0.4)'
                        : '0 4px 8px rgba(0,0,0,0.15)',
                      opacity: isBeingDragged ? 0.7 : 1,
                      zIndex: draggedJob?.id === job.id ? 100 : 1,
                      transition: isBeingDragged ? 'none' : 'all 0.1s ease-out',
                      transform: isBeingDragged ? `scale(1.05)` : 'scale(1)'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: `${2 * zoomScale}px`
                    }}>
                      {/* Drag handle - touch-action: none prevents browser from stealing touch for scroll */}
                      {canDrag && (
                        <div
                          onPointerDown={(e) => handleDragHandleDown(e, job)}
                          onPointerMove={handleDragHandleMove}
                          onPointerUp={handleDragHandleUp}
                          onPointerCancel={handleDragHandleCancel}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            touchAction: 'none',
                            cursor: 'grab',
                            padding: `${2 * zoomScale}px ${4 * zoomScale}px`,
                            marginRight: `${4 * zoomScale}px`,
                            fontSize: `${0.9 * zoomScale}rem`,
                            color: 'rgba(255,255,255,0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0
                          }}
                        >
                          ⣿
                        </div>
                      )}
                      <div style={{
                        fontSize: `${0.85 * zoomScale}rem`,
                        fontWeight: '700',
                        color: '#fff',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}>
                        {client?.name || 'No Client'}
                      </div>
                      <span style={{
                        fontSize: `${0.6 * zoomScale}rem`,
                        color: 'rgba(255,255,255,0.8)',
                        fontWeight: '600',
                        flexShrink: 0,
                        marginLeft: `${4 * zoomScale}px`
                      }}>
                        {durStr}
                      </span>
                    </div>
                    <div style={{
                      fontSize: `${0.7 * zoomScale}rem`,
                      color: 'rgba(255,255,255,0.95)',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: `${4 * zoomScale}px`
                    }}>
                      <span>🕐</span>
                      <span>{job.startTime} - {job.endTime}</span>
                    </div>
                    {height > 60 * zoomScale && mobAddress && (
                      <div style={{
                        fontSize: `${0.6 * zoomScale}rem`,
                        color: 'rgba(255,255,255,0.75)',
                        marginTop: `${3 * zoomScale}px`,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        📍 {mobAddress}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Drag Ghost - follows finger when dragging, shows target time */}
              {isDragging && draggedJob && (
                <div
                  ref={ghostDomRef}
                  style={{
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    transform: `translate(${dragGhostPos.x + 16}px, ${dragGhostPos.y - 40}px)`,
                    willChange: 'transform',
                    padding: '8px 14px',
                    backgroundColor: '#1e293b',
                    border: '2px solid #3b82f6',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    textAlign: 'center',
                    pointerEvents: 'none',
                    zIndex: 9999,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {`↕ ${language === "en" ? "Drag to time" : "Arraste"}`}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    // Check if user can use timeline (admin or client manager)
    const canUseTimeline = user?.isAdmin || user?.isClientManager;

    // Render grouped by date (for weekly views) or as simple list (for today)
    if (shouldGroupByDate) {
      // Include dates that have notices but no jobs (so employees see notices on all days)
      const selectedDate = new Date(selectedMobileDate + 'T00:00:00');
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
      const noticeDatesInWeek = notices
        .filter(n => {
          const nDate = new Date(n.date + 'T00:00:00');
          return nDate >= weekStart && nDate <= weekEnd && (n.team === "all" || n.team === activeTeam);
        })
        .map(n => n.date);
      const allDates = new Set([...Object.keys(groupedJobs), ...noticeDatesInWeek]);
      const sortedDates = [...allDates].sort();

      if (canUseTimeline) {
        // Timeline view for admins and managers
        return (
          <div className="mobile-job-list">
            {sortedDates.map(date => renderTimelineDay(date, groupedJobs[date] || []))}
          </div>
        );
      } else {
        // Card list view for regular users
        return (
          <div className="mobile-job-list">
            {sortedDates.map(date => (
              <div key={date} className="mb-4" style={{
                backgroundColor: darkTheme ? '#0f1117' : '#ffffff',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: `2px solid ${darkTheme ? '#1e2230' : '#e2e8f0'}`
              }}>
                {/* Team Members for this day */}
                {renderTeamMembers(date)}

                {/* Day Header - Destacado */}
                <div style={{
                  background: darkTheme
                    ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  color: '#fff',
                  textTransform: 'capitalize',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                }}>
                  {fnsFormat(parseISO(date), "EEEE, dd 'de' MMMM", { locale })}
                </div>

                {/* Notices for this day (non-admin week view) */}
                {notices
                  .filter(n => n.date === date && (n.team === "all" || n.team === activeTeam))
                  .map(n => (
                    <div key={`notice-card-${n.id}`} className="notice-mobile-card mb-2" onClick={() => openNotice(n)} style={{
                      backgroundColor: NOTICE_COLORS[n.color] || NOTICE_COLORS.blue,
                      color: "#fff",
                      padding: '8px 12px', borderRadius: '8px', fontWeight: '700', fontSize: '0.85rem', textAlign: 'center',
                      cursor: 'pointer'
                    }}>
                      {n.title}
                    </div>
                  ))
                }

                {/* Jobs for this day */}
                {(groupedJobs[date] || []).map(job => renderJobCard(job, false))}
              </div>
            ))}
          </div>
        );
      }
    }

    // For today view
    if (filteredJobs.length > 0) {
      const today = filteredJobs[0].date;

      if (canUseTimeline) {
        // Timeline view for admins and managers
        return (
          <div className="mobile-job-list">
            {renderTimelineDay(today, filteredJobs)}
          </div>
        );
      } else {
        // Card list view for regular users
        const todayNotices = notices.filter(n => n.date === today && (n.team === "all" || n.team === activeTeam));
        return (
          <div className="mobile-job-list">
            {todayNotices.map(n => (
              <div key={`notice-card-${n.id}`} className="notice-mobile-card mb-2" onClick={() => openNotice(n)} style={{
                backgroundColor: NOTICE_COLORS[n.color] || NOTICE_COLORS.blue,
                color: "#fff",
                padding: '10px 16px', borderRadius: '10px', fontWeight: '700', fontSize: '0.9rem', textAlign: 'center',
                cursor: 'pointer'
              }}>
                {n.title}
              </div>
            ))}
            {filteredJobs.map(job => renderJobCard(job))}
          </div>
        );
      }
    }

    return null;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">{t(language, "loading")}</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Alert for expiring recurrences */}
      {!expiringAlertDismissed && expiringRecurrences.length > 0 && (
        <Alert variant="warning" dismissible onClose={() => setExpiringAlertDismissed(true)} className="mb-3">
          <Alert.Heading style={{ fontSize: '1rem' }}>
            ⚠️ {language === "en" ? "Recurring jobs expiring soon" : "Jobs recorrentes expirando em breve"}
          </Alert.Heading>
          <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            {language === "en"
              ? `${expiringRecurrences.length} client(s) have recurring jobs ending within 30 days:`
              : `${expiringRecurrences.length} cliente(s) têm jobs recorrentes terminando em até 30 dias:`}
          </p>
          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
            {expiringRecurrences.map((item, idx) => (
              <div key={idx} className="d-flex justify-content-between align-items-center mb-2 p-2"
                   style={{ backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: '4px' }}>
                <span style={{ fontSize: '0.85rem' }}>
                  <strong>{item.clientName}</strong> - {language === "en" ? "last job" : "último job"}: {item.ultimaData}
                  {item.diasRestantes <= 7 && <Badge bg="danger" className="ms-2">
                    {item.diasRestantes} {language === "en" ? "days" : "dias"}
                  </Badge>}
                  {item.diasRestantes > 7 && <Badge bg="warning" text="dark" className="ms-2">
                    {item.diasRestantes} {language === "en" ? "days" : "dias"}
                  </Badge>}
                </span>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    // Abrir modal para editar o último job
                    const job = item.ultimoJob;
                    const client = findClientById(job.clientId, job.clientName);
                    setSelectedJob(job);
                    setFormData({
                      clientId: job.clientId || "",
                      date: job.date || "",
                      startTime: job.startTime || "",
                      endTime: job.endTime || "",
                      notes: job.notes || client?.notes || "",
                      password: job.password || client?.password || "",
                      checklist: job.checklist || client?.checklist || [],
                      status: job.status || "scheduled",
                      recurrence: job.recurrence || "none",
                      uploadedFiles: job.uploadedFiles || [],
                      invoiceAmount: job.invoiceAmount || client?.default_price || client?.defaultInvoiceAmount || 0,
                      timeModified: job.timeModified || false,
                      timeModificationReason: job.timeModificationReason || "",
                      originalEndTime: job.endTime || "",
                      repeatUntil: "indefinite",
                      repeatUntilDate: ""
                    });
                    setUploadedPhotos(job.uploadedFiles || []);
                    setModalMode("edit");
                    setShowModal(true);
                  }}
                >
                  ✏️ {language === "en" ? "Edit" : "Editar"}
                </Button>
              </div>
            ))}
          </div>
        </Alert>
      )}

      {/* Action buttons row - above title */}
      <div className={`d-flex gap-2 mb-3 ${isMobile ? 'flex-wrap' : 'flex-wrap justify-content-end'}`}>
        {/* Only admins can configure teams */}
        {user?.isAdmin && (
          <Button
            variant="outline-primary"
            onClick={() => setShowTeamModal(true)}
            size="sm"
          >
            👥 {language === "en" ? "Configure Teams" : "Configurar Equipes"}
          </Button>
        )}
        {/* Bulk Edit buttons */}
        {(user?.isAdmin || user?.isClientManager) && (
          <Button
            variant="success"
            onClick={handlePlanDay}
            size="sm"
          >
            ➕ {language === "en" ? "Plan Full Day" : "Planejar Dia Completo"}
          </Button>
        )}
        {/* Only admins and managers can create new jobs */}
        {(user?.isAdmin || user?.isClientManager) && (
          <Button
            variant="primary"
            onClick={() => {
              setFormData({
                clientId: "",
                date: "",
                startTime: "09:00",
                endTime: "11:00",
                notes: "",
                checklist: [],
                status: "scheduled",
                recurrence: "none",
                uploadedFiles: [],
                invoiceAmount: 0,
                timeModified: false,
                timeModificationReason: "",
                originalEndTime: "",
                repeatUntil: "indefinite",
                repeatUntilDate: ""
              });
              setUploadedPhotos([]);
              setClientSearchQuery(""); // Clear client search
              setSelectedJob(null);
              setModalMode("create");
              setShowModal(true);
            }}
            size="sm"
          >
            ➕ {language === "en" ? "New Job" : "Novo Job"}
          </Button>
        )}
      </div>

      <div className="mb-4">
        <div>
          <h2>📅 {language === "en" ? "Calendar" : "Calendário"}</h2>
          <p className="text-muted">
            {language === "en" ? "Manage jobs and schedules" : "Gerencie jobs e agendamentos"}
          </p>

          {/* Client Search Filter */}
          <div
            className="mb-3"
            style={{ maxWidth: isMobile ? '100%' : '600px', position: 'relative' }}
            data-search-container
          >
            <div className="input-group input-group-sm">
              <span className="input-group-text">
                🔍
              </span>
              <input
                type="text"
                className="form-control"
                placeholder={language === "en" ? "Search client..." : "Buscar cliente..."}
                value={globalClientFilter}
                onChange={(e) => {
                  setGlobalClientFilter(e.target.value);
                  setShowSearchResults(e.target.value.trim().length > 0);
                }}
                onFocus={() => setShowSearchResults(globalClientFilter.trim().length > 0)}
              />
              {globalClientFilter && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => {
                    setGlobalClientFilter("");
                    setShowSearchResults(false);
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Search Results Dropdown (Google Calendar style) */}
            {showSearchResults && searchResults.length > 0 && (
              <div
                ref={searchResultsRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: darkTheme ? '#2d2d2d' : 'white',
                  border: `1px solid ${darkTheme ? '#444' : '#dee2e6'}`,
                  borderRadius: '0.25rem',
                  marginTop: '4px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {searchResults.map((result, index) => {
                  const date = parseISO(result.date);
                  const dayOfMonth = fnsFormat(date, 'd');
                  const monthYearDay = fnsFormat(date, 'MMM. yyyy, EEE', {
                    locale: language === 'pt' ? ptBR : enUS
                  }).toUpperCase();

                  // Show separator before first future job
                  const showSeparator = index === firstFutureIndex && firstFutureIndex > 0;

                  return (
                    <React.Fragment key={`${result.id}-${index}`}>
                      {/* Separator between past and future jobs */}
                      {showSeparator && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 16px',
                            backgroundColor: darkTheme ? '#1e3a5f' : '#ebf8ff',
                            borderTop: `2px solid ${darkTheme ? '#2563eb' : '#60a5fa'}`,
                            borderBottom: `2px solid ${darkTheme ? '#2563eb' : '#60a5fa'}`
                          }}
                        >
                          <div
                            style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: darkTheme ? '#93c5fd' : '#2563eb',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {language === 'en' ? '↓ Upcoming' : '↓ Próximos'}
                          </div>
                        </div>
                      )}
                      <div
                        data-search-item
                        onClick={() => handleSearchResultClick(result.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: `1px solid ${darkTheme ? '#3a3a3a' : '#f0f0f0'}`,
                          transition: 'background-color 0.2s',
                          backgroundColor: result.isPast
                            ? (darkTheme ? '#252525' : '#fafafa')
                            : (darkTheme ? '#2d2d2d' : 'white')
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = darkTheme ? '#3a3a3a' : '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = result.isPast
                            ? (darkTheme ? '#252525' : '#fafafa')
                            : (darkTheme ? '#2d2d2d' : 'white');
                        }}
                      >
                      {/* Day Number (Big) */}
                      <div
                        style={{
                          fontSize: '32px',
                          fontWeight: '500',
                          width: '60px',
                          textAlign: 'center',
                          marginRight: '16px',
                          color: darkTheme ? '#e0e0e0' : '#333'
                        }}
                      >
                        {dayOfMonth}
                      </div>

                      {/* Date and Time */}
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: '13px',
                            color: darkTheme ? '#b0b0b0' : '#666',
                            marginBottom: '4px',
                            textTransform: 'uppercase'
                          }}
                        >
                          {monthYearDay}
                        </div>
                        <div
                          style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: darkTheme ? '#e0e0e0' : '#333',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <span style={{
                            display: 'inline-block',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: result.status === 'done' ? '#22c55e' :
                                           result.status === 'pending' ? '#f59e0b' : '#64748b'
                          }}></span>
                          <span>{result.startTime} – {result.endTime}</span>
                        </div>
                        <div
                          style={{
                            fontSize: '14px',
                            color: darkTheme ? '#d0d0d0' : '#555',
                            marginTop: '4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {result.clientName}
                          <span style={{
                            backgroundColor: (result.team || 'team1') === activeTeam ? '#3b82f6' : '#64748b',
                            color: '#fff',
                            fontSize: '10px',
                            fontWeight: '700',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            marginLeft: '8px',
                            verticalAlign: 'middle'
                          }}>
                            {result.teamLabel}
                          </span>
                          {result.address && (
                            <span style={{
                              color: darkTheme ? '#999' : '#888',
                              marginLeft: '8px',
                              fontSize: '13px'
                            }}>
                              {result.address}
                            </span>
                          )}
                        </div>
                      </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {showSearchResults && searchResults.length === 0 && globalClientFilter.trim() && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: darkTheme ? '#2d2d2d' : 'white',
                  border: `1px solid ${darkTheme ? '#444' : '#dee2e6'}`,
                  borderRadius: '0.25rem',
                  marginTop: '4px',
                  padding: '20px',
                  textAlign: 'center',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  color: darkTheme ? '#999' : '#666'
                }}
              >
                {language === "en" ? "No results found" : "Nenhum resultado encontrado"}
              </div>
            )}
          </div>

          {/* Mobile Controls */}
          {isMobile ? (
            <div className="mt-3" style={{ width: '100%' }}>
              {/* Row 1: View toggle + Team selector + Today */}
              <div className="d-flex align-items-center mb-2" style={{ width: '100%' }}>
                {/* Day/Week Toggle - left aligned */}
                <div style={{ flex: 1 }}>
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn ${mobileViewType === "day" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setMobileViewType("day")}
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                  >
                    {language === "en" ? "Day" : "Dia"}
                  </button>
                  <button
                    type="button"
                    className={`btn ${mobileViewType === "week" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setMobileViewType("week")}
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                  >
                    {language === "en" ? "Week" : "Sem"}
                  </button>
                </div>
                </div>

                {/* Team Selector */}
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn ${activeTeam === "team1" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setActiveTeam("team1")}
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                  >
                    T1
                  </button>
                  <button
                    type="button"
                    className={`btn ${activeTeam === "team2" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setActiveTeam("team2")}
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                  >
                    T2
                  </button>
                  <button
                    type="button"
                    className={`btn ${activeTeam === "team3" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setActiveTeam("team3")}
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                  >
                    T3
                  </button>
                </div>

                {/* Today Button - right aligned */}
                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                      const today = new Date();
                      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                      setSelectedMobileDate(todayStr);
                    }}
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                  >
                    {language === "en" ? "Today" : "Hoje"}
                  </button>
                </div>
              </div>

              {/* Row 2: Arrow + Day Strip + Arrow */}
              <div className="d-flex align-items-center gap-1" style={{ width: '100%' }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigateMobileDate(-1)}
                  style={{ padding: '0.3rem 0.5rem', fontSize: '0.85rem', flexShrink: 0 }}
                >
                  ‹
                </button>
                <div className="d-flex gap-1 justify-content-center" style={{ flex: 1 }}>
                  {(() => {
                    const selected = new Date(selectedMobileDate + 'T00:00:00');
                    const weekStart = startOfWeek(selected, { weekStartsOn: 1 });
                    const todayNow = new Date();
                    const todayStr = `${todayNow.getFullYear()}-${String(todayNow.getMonth() + 1).padStart(2, '0')}-${String(todayNow.getDate()).padStart(2, '0')}`;
                    const dayNames = language === "en"
                      ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
                      : ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

                    return Array.from({ length: 7 }, (_, i) => {
                      const day = new Date(weekStart);
                      day.setDate(day.getDate() + i);
                      const dayStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                      const isToday = dayStr === todayStr;
                      const isSelected = dayStr === selectedMobileDate;

                      return (
                        <button
                          key={dayStr}
                          onClick={() => setSelectedMobileDate(dayStr)}
                          style={{
                            flex: 1,
                            minWidth: '36px',
                            padding: '4px 2px',
                            borderRadius: '10px',
                            border: isToday && !isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                            backgroundColor: isSelected
                              ? '#3b82f6'
                              : (darkTheme ? '#1e2230' : '#f1f5f9'),
                            color: isSelected ? '#fff' : (darkTheme ? '#cbd5e1' : '#334155'),
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div style={{ fontSize: '0.55rem', textTransform: 'uppercase', fontWeight: '500', opacity: 0.8 }}>
                            {dayNames[i]}
                          </div>
                          <div style={{ fontSize: '0.85rem', fontWeight: '700', lineHeight: '1.2' }}>
                            {day.getDate()}
                          </div>
                        </button>
                      );
                    });
                  })()}
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigateMobileDate(1)}
                  style={{ padding: '0.3rem 0.5rem', fontSize: '0.85rem', flexShrink: 0 }}
                >
                  ›
                </button>
              </div>
            </div>
          ) : (
            /* Desktop Team Selector */
            <div className="btn-group mt-2" role="group">
              <button
                type="button"
                className={`btn ${activeTeam === "team1" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setActiveTeam("team1")}
              >
                TEAM 1
              </button>
              <button
                type="button"
                className={`btn ${activeTeam === "team2" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setActiveTeam("team2")}
              >
                TEAM 2
              </button>
              <button
                type="button"
                className={`btn ${activeTeam === "team3" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setActiveTeam("team3")}
              >
                TEAM 3
              </button>
            </div>
          )}
          {/* Add Notice button - Admin only */}
          {(user?.isAdmin || user?.isClientManager) && (
            <Button
              variant="warning"
              size="sm"
              className="ms-2 mt-2"
              onClick={() => {
                setSelectedNotice(null);
                setNoticeModalMode("create");
                setShowNoticeModal(true);
              }}
            >
              {language === "en" ? "Add Notice" : "Adicionar Aviso"}
            </Button>
          )}
        </div>
      </div>

      {/* Team Members Section - Only for Day mode, non-admin users (admins see it inside timeline) */}
      {isMobile && mobileViewType === "day" && !(user?.isAdmin || user?.isClientManager) && renderTeamMembers(selectedMobileDate)}

      {/* Action buttons - Fixed at bottom right */}
      {!isMobile && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            display: 'flex',
            gap: '12px',
            zIndex: 1000
          }}
        >
          {/* WhatsApp Confirmations Button - Admin and Manager only */}
          {(user?.isAdmin || user?.isClientManager) && (
          <Button
            variant={selectionMode === 'confirmations' ? 'warning' : 'primary'}
            onClick={() => {
              if (selectionMode === 'confirmations' && selectedInvoices.length > 0) {
                // Open modal to choose message type before sending
                setShowWhatsAppModal(true);
              } else if (selectionMode === 'confirmations') {
                // Exit confirmations mode
                setSelectionMode(null);
                setSelectedInvoices([]);
              } else {
                // Enter confirmations mode
                setSelectionMode('confirmations');
                setSelectedInvoices([]);
              }
            }}
            disabled={sendingWhatsApp}
            style={{
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            {sendingWhatsApp ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {language === "en" ? "Sending..." : "Enviando..."}
              </>
            ) : selectionMode === 'confirmations' && selectedInvoices.length > 0 ? (
              <>📱 {language === "en" ? `Send to ${selectedInvoices.length} Clients` : `Enviar para ${selectedInvoices.length} Clientes`}</>
            ) : selectionMode === 'confirmations' ? (
              <>❌ {language === "en" ? "Cancel" : "Cancelar"}</>
            ) : (
              <>💬 {language === "en" ? "WhatsApp Confirmations" : "Confirmações WhatsApp"}</>
            )}
          </Button>
          )}

          {/* Send Invoices Button (Admin only) */}
          {user?.isAdmin && (
            <Button
              variant={selectionMode === 'invoices' ? 'warning' : 'success'}
              onClick={() => {
                if (selectionMode === 'invoices' && selectedInvoices.length > 0) {
                  // Send invoices
                  handleSendInvoices();
                } else if (selectionMode === 'invoices') {
                  // Exit invoice mode
                  setSelectionMode(null);
                  setSelectedInvoices([]);
                } else {
                  // Enter invoice mode
                  setSelectionMode('invoices');
                  setSelectedInvoices([]);
                }
              }}
              disabled={sendingInvoices}
              style={{
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            >
              {sendingInvoices ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {language === "en" ? "Sending..." : "Enviando..."}
                </>
              ) : selectionMode === 'invoices' && selectedInvoices.length > 0 ? (
                <>📤 {language === "en" ? `Send ${selectedInvoices.length} Invoices` : `Enviar ${selectedInvoices.length} Invoices`}</>
              ) : selectionMode === 'invoices' ? (
                <>❌ {language === "en" ? "Cancel" : "Cancelar"}</>
              ) : (
                <>📧 {language === "en" ? "Send Invoices" : "Enviar Invoices"}</>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Mobile List View - always show list on mobile */}
      {isMobile ? (
        <div
          className="mobile-list-container"
          onTouchStart={(e) => {
            if (e.touches.length === 1) {
              swipeRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY };
            }
          }}
          onTouchEnd={(e) => {
            if (e.changedTouches.length === 1 && swipeRef.current.startX) {
              const deltaX = e.changedTouches[0].clientX - swipeRef.current.startX;
              const deltaY = e.changedTouches[0].clientY - swipeRef.current.startY;
              // Navigate only if horizontal swipe is dominant (prevents conflict with vertical scroll)
              if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY) * 2) {
                navigateMobileDate(deltaX > 0 ? -1 : 1);
              }
              swipeRef.current = { startX: 0, startY: 0 };
            }
          }}
          style={{
            backgroundColor: darkTheme ? '#0f1117' : '#f8fafc',
            padding: '16px',
            borderRadius: '12px',
            minHeight: '400px'
          }}
        >
          <div key={selectedMobileDate} className="mobile-slide-in">
            {renderMobileListView()}
          </div>
        </div>
      ) : (
        <div className={`calendar-container ${darkTheme ? 'dark-theme' : 'light-theme'}`}>
          <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView={isMobile ? "timeGridDay" : "timeGridWeek"}
          customButtons={{
            themeToggle: {
              text: darkTheme ? '☀️' : '🌙',
              click: toggleTheme
            }
          }}
          headerToolbar={
            isMobile
              ? {
                  left: "prev,next today",
                  center: "title",
                  right: "timeGridDay,timeGridWeek,listWeek"
                }
              : {
                  left: "themeToggle prev,next today",
                  center: "title",
                  right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek"
                }
          }
          firstDay={1}
          slotMinTime="06:00:00"
          slotMaxTime={calculateMaxTime()}
          snapDuration="00:10:00"
          allDaySlot={noticeEvents.length > 0}
          allDayText=""
          views={{
            timeGridWeek: { slotDuration: '00:30:00' },
            timeGridDay: { slotDuration: '00:15:00' }
          }}
          events={events}
          eventContent={renderEventContent}
          eventClick={handleEventClick}
          select={handleDateSelect}
          selectable={user?.isAdmin || user?.isClientManager}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={true}
          height="auto"
          editable={user?.isAdmin || user?.isClientManager}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          dateClick={() => setSelectedEvent(null)} // Clear selection when clicking empty space
          datesSet={(dateInfo) => {
            // Debounce to avoid rapid state updates during navigation
            if (datesSetTimeoutRef.current) {
              clearTimeout(datesSetTimeoutRef.current);
            }
            datesSetTimeoutRef.current = setTimeout(() => {
              // Update visible date range when calendar navigates
              // Add buffer of 1 week before and after for smooth scrolling
              const start = addDays(dateInfo.start, -7);
              const end = addDays(dateInfo.end, 7);
              setVisibleDateRange({ start, end });
            }, 150); // 150ms debounce
          }}
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
          }}
          slotLabelFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
          }}
          dayHeaderContent={(args) => {
            // Format date in local timezone to avoid UTC conversion issues
            const date = args.date;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            const dateStr = `${year}-${month}-${day}`;

            const teamForDay = getTeamForDate(dateStr);
            const hasOverride = !!dailyTeamOverrides[dateStr];
            const teamNames = teamForDay.map(uid => {
              // Check if it's a custom name (starts with "custom:")
              if (uid.startsWith("custom:")) {
                return uid.substring(7); // Remove "custom:" prefix
              }
              // Otherwise, it's an employee UID
              const emp = employees.find(e => e.id === uid);
              return emp?.displayName?.split(" ")[0] || "";
            }).filter(n => n).join(", ");

            const dayName = language === "en"
              ? fnsFormat(date, "EEE", { locale: enUS })  // Mon, Tue, Wed...
              : fnsFormat(date, "EEEE", { locale: ptBR }).replace("-feira", ""); // Segunda, Terça...

            return (
              <div style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{ fontWeight: 'bold' }}>
                  {dayName}
                </div>
                <div style={{ fontSize: '0.85rem', color: darkTheme ? '#94a3b8' : '#64748b' }}>
                  {fnsFormat(date, "dd/MM")}
                </div>
                {teamNames ? (
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: darkTheme ? '#94a3b8' : '#64748b',
                      fontWeight: 'normal',
                      cursor: (user?.isAdmin || user?.isClientManager) ? 'pointer' : 'default',
                      marginTop: '4px'
                    }}
                    onClick={(e) => {
                      if (user?.isAdmin || user?.isClientManager) {
                        e.stopPropagation();
                        setSelectedDate(dateStr);
                        setShowDailyTeamModal(true);
                      }
                    }}
                    title={
                      (user?.isAdmin || user?.isClientManager)
                        ? (language === "en"
                          ? "Click to customize team for this date"
                          : "Clique para personalizar equipe desta data")
                        : (language === "en"
                          ? "Team members for this date"
                          : "Membros da equipe nesta data")
                    }
                  >
                    👤 {teamNames}
                  </div>
                ) : (
                  (user?.isAdmin || user?.isClientManager) && (
                    <div
                      style={{
                        fontSize: '0.85rem',
                        color: darkTheme ? '#64748b' : '#94a3b8',
                        fontWeight: 'normal',
                        cursor: 'pointer',
                        marginTop: '4px'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDate(dateStr);
                        setShowDailyTeamModal(true);
                      }}
                      title={
                        language === "en"
                          ? "Click to add team members"
                          : "Clique para adicionar membros"
                      }
                    >
                      ➕
                    </div>
                  )
                )}
              </div>
            );
          }}
        />
        </div>
      )}

      {/* Job Modal */}
      <Modal
        show={showModal}
        animation={false}
        data-bs-theme={modalTheme}
        onHide={() => {
          setShowModal(false);
          setEditingClientData(null); // Clear client editing state
          setAddressSuggestions([]); // Clear address suggestions
          // Clear calendar selection when closing modal
          if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.unselect();
          }
        }}
        size="lg"
        enforceFocus={false}
        restoreFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "create" && (language === "en" ? "New Job" : "Novo Job")}
            {modalMode === "edit" && (language === "en" ? "Edit Job" : "Editar Job")}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{language === "en" ? "Client" : "Cliente"} *</Form.Label>

                  {modalMode === "view" || (modalMode === "edit" && !user?.isAdmin && !user?.isClientManager) ? (
                    <Form.Control
                      type="text"
                      value={findClientById(formData.clientId)?.name || ""}
                      disabled
                    />
                  ) : (
                    <Combobox
                      onChange={handleClientSelect}
                      value={null}
                      disabled={modalMode === "view" || (modalMode === "edit" && !user?.isAdmin && !user?.isClientManager)}
                    >
                      <div className="position-relative mb-2">
                        <Combobox.Input
                          id="client-search"
                          name="clientSearch"
                          data-testid="client-search"
                          autoComplete="off"
                          className="form-control"
                          placeholder={language === "en" ? "Search client..." : "Buscar cliente..."}
                          value={clientSearchQuery}
                          onChange={(e) => setClientSearchQuery(e.target.value)}
                          displayValue={() => ""}
                        />
                        <Combobox.Options
                          className="list-group position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                          style={{ zIndex: 1000, maxHeight: "20vh", overflowY: "auto" }}
                        >
                          {clients
                            .filter(c =>
                              c.name.toLowerCase().includes(clientSearchQuery.toLowerCase())
                            )
                            .map(c => (
                              <Combobox.Option
                                key={c.id}
                                value={c}
                                className="list-group-item list-group-item-action cursor-pointer"
                              >
                                {c.name}
                              </Combobox.Option>
                            ))}
                          {clientSearchQuery.trim() && (
                            <Combobox.Option
                              value={{ id: null }}
                              className="list-group-item list-group-item-action cursor-pointer text-primary fw-bold"
                            >
                              ➕ {language === "en" ? "Add" : "Adicionar"} "{clientSearchQuery}"
                            </Combobox.Option>
                          )}
                        </Combobox.Options>
                      </div>
                    </Combobox>
                  )}

                  {/* Selected client display */}
                  {formData.clientId && (
                    <div className="alert alert-info py-2 px-3 d-flex justify-content-between align-items-center">
                      <span>
                        ✓ {findClientById(formData.clientId)?.name}
                      </span>
                      {modalMode !== "view" && (user?.isAdmin || user?.isClientManager) && (
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0"
                          onClick={() => {
                            setFormData({ ...formData, clientId: "" });
                            setClientSearchQuery("");
                          }}
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                  )}

                  {/* New Client Form */}
                  {newClient.visible && (
                    <div className="p-3 mb-3 bg-light border rounded">
                      <h6 className="mb-3">
                        {language === "en" ? "Add New Client" : "Adicionar Novo Cliente"}
                      </h6>

                      <Form.Group className="mb-2">
                        <Form.Label>{language === "en" ? "Name" : "Nome"} *</Form.Label>
                        <Form.Control
                          id="newclient-name"
                          name="name"
                          data-testid="newclient-name"
                          autoComplete="off"
                          type="text"
                          value={newClient.name}
                          onChange={(e) =>
                            setNewClient(n => ({ ...n, name: e.target.value }))
                          }
                        />
                      </Form.Group>

                      <Form.Group className="mb-2 position-relative">
                        <Form.Label>{language === "en" ? "Address (Auckland)" : "Endereço (Auckland)"} *</Form.Label>
                        <Form.Control
                          id="newclient-address"
                          name="address"
                          data-testid="newclient-address"
                          autoComplete="off"
                          type="text"
                          value={newClient.address}
                          onChange={(e) => handleAddressChange(e.target.value)}
                        />
                        {addressSuggestions.length > 0 && (
                          <ul
                            className="list-group position-absolute w-100 mt-1 shadow-sm"
                            style={{ zIndex: 1001, maxHeight: "15vh", overflowY: "auto" }}
                          >
                            {addressSuggestions.map((pred) => (
                              <li
                                key={pred.place_id}
                                className="list-group-item list-group-item-action cursor-pointer"
                                onClick={() => {
                                  setNewClient(n => ({
                                    ...n,
                                    address: pred.description,
                                    addressValid: true
                                  }));
                                  setAddressSuggestions([]);
                                  addressSessionTokenRef.current = null; // Renova session token
                                }}
                              >
                                {pred.description}
                              </li>
                            ))}
                          </ul>
                        )}
                      </Form.Group>

                      <Form.Group className="mb-2">
                        <Form.Label>{language === "en" ? "Hours (HH:MM)" : "Horas (HH:MM)"} *</Form.Label>
                        <Form.Control
                          id="newclient-hours"
                          name="hours"
                          data-testid="newclient-hours"
                          autoComplete="off"
                          type="text"
                          value={newClient.hours}
                          onChange={(e) =>
                            setNewClient(n => ({ ...n, hours: e.target.value }))
                          }
                        />
                      </Form.Group>

                      <Form.Group className="mb-2">
                        <Form.Label>{language === "en" ? "Email" : "Email"}</Form.Label>
                        <Form.Control
                          id="newclient-email"
                          name="email"
                          data-testid="newclient-email"
                          autoComplete="off"
                          type="email"
                          value={newClient.email}
                          onChange={(e) =>
                            setNewClient(n => ({ ...n, email: e.target.value }))
                          }
                        />
                      </Form.Group>

                      <Form.Group className="mb-2">
                        <Form.Label>{language === "en" ? "Phone" : "Telefone"}</Form.Label>
                        <Form.Control
                          id="newclient-phone"
                          name="phone"
                          data-testid="newclient-phone"
                          autoComplete="off"
                          type="tel"
                          value={newClient.phone}
                          onChange={(e) =>
                            setNewClient(n => ({ ...n, phone: e.target.value }))
                          }
                        />
                      </Form.Group>

                      <Form.Group className="mb-2">
                        <Form.Label>{language === "en" ? "Payment Method" : "Método de Pagamento"} *</Form.Label>
                        <Form.Select
                          id="newclient-payment-method"
                          name="paymentMethod"
                          data-testid="newclient-payment-method"
                          value={newClient.paymentMethod}
                          onChange={(e) =>
                            setNewClient(n => ({ ...n, paymentMethod: e.target.value }))
                          }
                        >
                          <option value="cash">Cash</option>
                          <option value="xero">Xero</option>
                        </Form.Select>
                      </Form.Group>

                      {/* Only admins can set default invoice amount */}
                      {newClient.paymentMethod === "xero" && user?.isAdmin && (
                        <Form.Group className="mb-3">
                          <Form.Label>{language === "en" ? "Default Invoice Amount ($)" : "Valor Padrão da Invoice ($)"}</Form.Label>
                          <Form.Control
                            id="newclient-invoice-amount"
                            name="defaultInvoiceAmount"
                            data-testid="newclient-invoice-amount"
                            autoComplete="off"
                            type="number"
                            step="0.01"
                            value={newClient.defaultInvoiceAmount}
                            onChange={(e) =>
                              setNewClient(n => ({ ...n, defaultInvoiceAmount: e.target.value }))
                            }
                          />
                        </Form.Group>
                      )}

                      <div className="text-end">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          onClick={() => {
                            setNewClient({
                              name: "",
                              address: "",
                              hours: "",
                              email: "",
                              phone: "",
                              paymentMethod: "cash",
                              defaultInvoiceAmount: "",
                              addressValid: false,
                              visible: false
                            });
                            setAddressSuggestions([]);
                            setClientSearchQuery("");
                          }}
                        >
                          {language === "en" ? "Cancel" : "Cancelar"}
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleSaveNewClient}
                        >
                          {language === "en" ? "Save Client" : "Salvar Cliente"}
                        </Button>
                      </div>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Client Address and Time Info */}
            {formData.clientId && (() => {
              const selectedClient = findClientById(formData.clientId);
              if (!selectedClient) return null;

              const teamForDay = formData.date ? getTeamForDate(formData.date) : [];
              const isEditingClient = editingClientData && editingClientData.id === selectedClient.id;

              return (
                <Row className="mb-3">
                  <Col md={12}>
                    <div className="p-3 rounded" style={{ backgroundColor: '#e9ecef', color: '#212529' }}>
                      {/* Header with Edit button */}
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong style={{ color: '#212529' }}>👤 {language === "en" ? "Client Info" : "Dados do Cliente"}</strong>
                        <div className="d-flex gap-2">
                          {!isEditingClient && user?.isAdmin && modalMode !== "view" && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditingClientData({
                                id: selectedClient.id,
                                name: selectedClient.name || "",
                                address: selectedClient.address || "",
                                phone: selectedClient.phone || "",
                                email: selectedClient.email || "",
                                service_duration: getServiceDuration(selectedClient) || "2:00",
                                payment_method: getPaymentMethod(selectedClient) || "cash",
                                default_price: parseFloat(getDefaultPrice(selectedClient)) || 0
                              })}
                            >
                              ✏️ {language === "en" ? "Edit Client" : "Editar Cliente"}
                            </Button>
                          )}
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              const addr = isEditingClient ? editingClientData.address : selectedClient.address;
                              const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
                              window.open(mapsUrl, "_blank");
                            }}
                          >
                            📍 Maps
                          </Button>
                        </div>
                      </div>

                      {/* Edit Mode */}
                      {isEditingClient ? (
                        <div className="border rounded p-3 bg-white">
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small mb-1">{language === "en" ? "Name" : "Nome"} *</Form.Label>
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  value={editingClientData.name}
                                  onChange={(e) => setEditingClientData({...editingClientData, name: e.target.value})}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small mb-1">{language === "en" ? "Phone" : "Telefone"}</Form.Label>
                                <Form.Control
                                  type="tel"
                                  size="sm"
                                  value={editingClientData.phone}
                                  onChange={(e) => setEditingClientData({...editingClientData, phone: e.target.value})}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={12}>
                              <Form.Group className="mb-2 position-relative">
                                <Form.Label className="small mb-1">{language === "en" ? "Address" : "Endereço"} *</Form.Label>
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  value={editingClientData.address}
                                  onChange={(e) => {
                                    setEditingClientData({...editingClientData, address: e.target.value});
                                    handleAddressChange(e.target.value);
                                  }}
                                />
                                {addressSuggestions.length > 0 && (
                                  <ul
                                    className="list-group position-absolute w-100 mt-1 shadow-sm"
                                    style={{ zIndex: 1001, maxHeight: "15vh", overflowY: "auto" }}
                                  >
                                    {addressSuggestions.map((pred) => (
                                      <li
                                        key={pred.place_id}
                                        className="list-group-item list-group-item-action cursor-pointer small"
                                        onClick={() => {
                                          setEditingClientData({...editingClientData, address: pred.description});
                                          setAddressSuggestions([]);
                                        }}
                                      >
                                        {pred.description}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small mb-1">Email</Form.Label>
                                <Form.Control
                                  type="email"
                                  size="sm"
                                  value={editingClientData.email}
                                  onChange={(e) => setEditingClientData({...editingClientData, email: e.target.value})}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small mb-1">{language === "en" ? "Hours (HH:MM)" : "Horas (HH:MM)"} *</Form.Label>
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  value={editingClientData.service_duration}
                                  onChange={(e) => setEditingClientData({...editingClientData, service_duration: e.target.value})}
                                  placeholder="2:00"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small mb-1">{language === "en" ? "Payment Method" : "Método de Pagamento"}</Form.Label>
                                <Form.Select
                                  size="sm"
                                  value={editingClientData.payment_method}
                                  onChange={(e) => setEditingClientData({...editingClientData, payment_method: e.target.value})}
                                >
                                  <option value="cash">Cash</option>
                                  <option value="xero">Xero</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            {editingClientData.payment_method === "xero" && (
                              <Col md={6}>
                                <Form.Group className="mb-2">
                                  <Form.Label className="small mb-1">{language === "en" ? "Default Price ($)" : "Preço Padrão ($)"}</Form.Label>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    step="0.01"
                                    value={editingClientData.default_price}
                                    onChange={(e) => setEditingClientData({...editingClientData, default_price: e.target.value})}
                                  />
                                </Form.Group>
                              </Col>
                            )}
                          </Row>
                          <div className="d-flex justify-content-end gap-2 mt-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                setEditingClientData(null);
                                setAddressSuggestions([]);
                              }}
                              disabled={savingClientData}
                            >
                              {language === "en" ? "Cancel" : "Cancelar"}
                            </Button>
                            <Button
                              variant="success"
                              size="sm"
                              onClick={handleSaveClientData}
                              disabled={savingClientData}
                            >
                              {savingClientData ? (
                                <>{language === "en" ? "Saving..." : "Salvando..."}</>
                              ) : (
                                <>{language === "en" ? "Save Client" : "Salvar Cliente"}</>
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* View Mode */
                        <div style={{ color: '#212529' }}>
                          <div className="mb-2">
                            <strong style={{ color: '#0d6efd' }}>📍 {language === "en" ? "Address" : "Endereço"}:</strong>
                            <div className="mt-1" style={{ color: '#333' }}>{selectedClient.address}</div>
                          </div>
                          {selectedClient.phone && (
                            <div className="mb-2">
                              <strong style={{ color: '#0d6efd' }}>📞 {language === "en" ? "Phone" : "Telefone"}:</strong> <span style={{ color: '#333' }}>{selectedClient.phone}</span>
                            </div>
                          )}
                          {selectedClient.email && (
                            <div className="mb-2">
                              <strong style={{ color: '#0d6efd' }}>✉️ Email:</strong> <span style={{ color: '#333' }}>{selectedClient.email}</span>
                            </div>
                          )}
                          {/* Only admins can see and edit invoice amounts */}
                          {getPaymentMethod(selectedClient) === 'xero' && user?.isAdmin && (
                            <div className="mt-3">
                              <Form.Group>
                                <Form.Label>
                                  <strong>💰 {language === "en" ? "Invoice Amount" : "Valor da Invoice"}</strong>
                                  {(selectedClient.default_price > 0 || selectedClient.defaultInvoiceAmount > 0) && (
                                    <span className="text-muted ms-2" style={{ fontSize: '0.85em' }}>
                                      ({language === "en" ? "Default" : "Padrão"}: ${parseFloat(getDefaultPrice(selectedClient)).toFixed(2)})
                                    </span>
                                  )}
                                </Form.Label>
                                <div className="input-group">
                                  <span className="input-group-text">$</span>
                                  <Form.Control
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.invoiceAmount}
                                    onChange={(e) => setFormData({ ...formData, invoiceAmount: parseFloat(e.target.value) || 0 })}
                                    disabled={modalMode === "view"}
                                    placeholder="0.00"
                                  />
                                </div>
                                {formData.invoiceAmount !== parseFloat(getDefaultPrice(selectedClient)) && formData.invoiceAmount > 0 && modalMode === "edit" && (
                                  <div className="mt-2 p-2 bg-warning bg-opacity-10 rounded border border-warning">
                                    <small>
                                      ⚠️ {language === "en"
                                        ? "Value differs from client's default. When saving, you can choose to update the default or use it only for this job."
                                        : "Valor diferente do padrão do cliente. Ao salvar, você poderá escolher atualizar o padrão ou usar apenas para este job."}
                                    </small>
                                  </div>
                                )}
                              </Form.Group>
                            </div>
                          )}
                          {(() => {
                            const clientDuration = getServiceDuration(selectedClient);
                            if (!clientDuration || teamForDay.length === 0) return null;

                            const [hours, minutes] = clientDuration.split(':').map(Number);
                            const totalMinutes = hours * 60 + minutes;
                            const extraMin = formData.visitType === "first_clean" ? (parseInt(formData.firstCleanExtra) || 0) : 0;
                            const adjustedTotal = totalMinutes + extraMin;
                            const minutesPerPerson = Math.round(adjustedTotal / teamForDay.length);
                            const hoursPerPerson = Math.floor(minutesPerPerson / 60);
                            const remainingMinutes = minutesPerPerson % 60;
                            const perPersonStr = `${String(hoursPerPerson).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}`;

                            // Format adjusted total for display
                            const adjH = Math.floor(adjustedTotal / 60);
                            const adjM = adjustedTotal % 60;
                            const adjustedStr = `${adjH}:${String(adjM).padStart(2, '0')}`;

                            return (
                              <div className="mt-2 p-2 bg-white rounded border">
                                <strong>⏱️ {language === "en" ? "Time calculation" : "Cálculo de tempo"}:</strong>
                                <div className="mt-1">
                                  {extraMin > 0
                                    ? `${clientDuration} + ${extraMin}min = ${adjustedStr} ÷ ${teamForDay.length} = ${perPersonStr}`
                                    : `${clientDuration} ÷ ${teamForDay.length} = ${perPersonStr}`
                                  } {language === "en" ? "per person" : "por pessoa"}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              );
            })()}

            {/* Visit Type - only show when client is selected */}
            {formData.clientId && (
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>{language === "en" ? "Visit Type" : "Tipo de Visita"}</Form.Label>
                    <Form.Select
                      value={formData.visitType}
                      onChange={(e) => {
                        const newVisitType = e.target.value;
                        const client = findClientById(formData.clientId);
                        const defaultPrice = getDefaultPrice(client);

                        if (newVisitType === "touchup") {
                          setFormData(prev => ({ ...prev, visitType: newVisitType, invoiceAmount: 0 }));
                        } else {
                          // Regular or first_clean: restore default price + recalculate end time
                          const updates = { visitType: newVisitType, invoiceAmount: defaultPrice };

                          if (formData.startTime && formData.date) {
                            const editingJob = selectedEvent ? jobs.find(j => j.id === selectedEvent.id) : null;
                            const newEndTime = calculateEndTime(formData.clientId, formData.date, formData.startTime, null, null, null, editingJob?.clientName, newVisitType, formData.firstCleanExtra);
                            if (newEndTime) updates.endTime = newEndTime;
                          }

                          setFormData(prev => ({ ...prev, ...updates }));
                        }
                      }}
                      disabled={modalMode === "view" || (!user?.isAdmin && !user?.isClientManager)}
                    >
                      <option value="regular">{language === "en" ? "Regular" : "Regular"}</option>
                      <option value="first_clean">{language === "en" ? "First Clean" : "Primeira Limpeza"}</option>
                      <option value="touchup">{language === "en" ? "Touch-up / Return ($0)" : "Retorno / Reparo ($0)"}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {formData.visitType === "first_clean" && (
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{language === "en" ? "Extra time (min)" : "Tempo extra (min)"}</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="15"
                        value={formData.firstCleanExtra}
                        onChange={(e) => {
                          const extra = parseInt(e.target.value) || 0;
                          const updates = { firstCleanExtra: extra };

                          // Recalculate end time with new extra
                          if (formData.clientId && formData.startTime && formData.date) {
                            const editingJob = selectedEvent ? jobs.find(j => j.id === selectedEvent.id) : null;
                            const newEndTime = calculateEndTime(formData.clientId, formData.date, formData.startTime, null, null, null, editingJob?.clientName, "first_clean", extra);
                            if (newEndTime) updates.endTime = newEndTime;
                          }

                          setFormData(prev => ({ ...prev, ...updates }));
                        }}
                        disabled={modalMode === "view"}
                      />
                    </Form.Group>
                  </Col>
                )}
                {formData.visitType === "touchup" && (
                  <Col md={6} className="d-flex align-items-end">
                    <div className="p-2 rounded w-100 bg-warning bg-opacity-10 border border-warning">
                      <small>{language === "en" ? "↩️ No charge - set duration manually" : "↩️ Sem cobrança - defina a duração manualmente"}</small>
                    </div>
                  </Col>
                )}
              </Row>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{language === "en" ? "Team for this day" : "Equipe deste dia"}</Form.Label>
                  <div className="p-3 rounded" style={{ backgroundColor: '#e9ecef', color: '#212529' }}>
                    {formData.date ? (() => {
                      const teamForDay = getTeamForDate(formData.date);
                      const hasOverride = !!dailyTeamOverrides[formData.date];

                      if (teamForDay.length === 0) {
                        return (
                          <small style={{ color: '#666' }}>
                            {language === "en" ? "No team configured for this day" : "Nenhuma equipe configurada para este dia"}
                          </small>
                        );
                      }

                      return teamForDay.map(uid => {
                        // Check if it's a custom name (starts with "custom:")
                        if (uid.startsWith("custom:")) {
                          const customName = uid.substring(7);
                          return (
                            <div key={uid} className="mb-1" style={{ color: '#333' }}>👤 {customName}</div>
                          );
                        }
                        // Otherwise, it's an employee UID
                        const emp = employees.find(e => e.id === uid);
                        return emp ? (
                          <div key={uid} className="mb-1" style={{ color: '#333' }}>👤 {emp.displayName}</div>
                        ) : null;
                      });
                    })() : (
                      <small style={{ color: '#666' }}>
                        {language === "en" ? "Select a date first" : "Selecione uma data primeiro"}
                      </small>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{language === "en" ? "Date" : "Data"} *</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.date}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      setFormData(prev => ({ ...prev, date: newDate }));

                      // 🧠 INTELLIGENT RECALCULATION: Auto-adjust time when date changes (skip for touch-up)
                      if (formData.clientId && formData.startTime && newDate && formData.visitType !== "touchup") {
                        // Get clientName from selectedEvent's job if editing existing job
                        const editingJob = selectedEvent ? jobs.find(j => j.id === selectedEvent.id) : null;
                        const newEndTime = calculateEndTime(formData.clientId, newDate, formData.startTime, null, null, null, editingJob?.clientName, formData.visitType, formData.firstCleanExtra);
                        if (newEndTime) {
                          console.log(`🧠 Date changed to ${newDate}. Recalculating endTime: ${formData.startTime} → ${newEndTime}`);
                          setTimeout(() => {
                            setFormData(prev => ({ ...prev, date: newDate, endTime: newEndTime }));
                          }, 0);
                        }
                      }
                    }}
                    disabled={modalMode === "view" || (!user?.isAdmin && !user?.isClientManager)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{language === "en" ? "Start Time" : "Início"} *</Form.Label>
                  <Form.Control
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => {
                      const newStartTime = e.target.value;
                      setFormData(prev => ({ ...prev, startTime: newStartTime }));

                      // Auto-recalculate end time if client is selected (skip for touch-up)
                      if (formData.clientId && formData.date && formData.visitType !== "touchup") {
                        const selectedClient = clients.find(c => String(c.id) === String(formData.clientId));
                        const teamForDay = getTeamForDate(formData.date);

                        const clientDuration = getServiceDuration(selectedClient);
                        if (clientDuration && teamForDay.length > 0 && newStartTime) {
                          const [hours, minutes] = clientDuration.split(':').map(Number);
                          const totalMinutes = hours * 60 + minutes;
                          const adjustedMinutes = formData.visitType === "first_clean" ? totalMinutes + (parseInt(formData.firstCleanExtra) || 60) : totalMinutes;
                          const minutesPerPerson = Math.round(adjustedMinutes / teamForDay.length);

                          const [startHour, startMin] = newStartTime.split(':').map(Number);
                          const endTotalMinutes = startHour * 60 + startMin + minutesPerPerson;
                          const endHour = Math.floor(endTotalMinutes / 60);
                          const endMin = endTotalMinutes % 60;

                          setFormData(prev => ({
                            ...prev,
                            startTime: newStartTime,
                            endTime: `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`
                          }));
                        }
                      }
                    }}
                    disabled={modalMode === "view" || (!user?.isAdmin && !user?.isClientManager)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{language === "en" ? "End Time" : "Fim"} *</Form.Label>
                  <Form.Control
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    disabled={modalMode === "view" || (!user?.isAdmin && !user?.isClientManager)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{language === "en" ? "Recurrence" : "Recorrência"}</Form.Label>
                  <Form.Select
                    value={formData.recurrence}
                    onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                    disabled={modalMode === "view" || (!user?.isAdmin && !user?.isClientManager)}
                  >
                    <option value="none">{language === "en" ? "One-time" : "Uma vez"}</option>
                    <option value="weekly">{language === "en" ? "Weekly" : "Semanal"}</option>
                    <option value="fortnightly">{language === "en" ? "Fortnightly" : "Quinzenal"}</option>
                    <option value="3weekly">{language === "en" ? "Every 3 weeks" : "A cada 3 semanas"}</option>
                    <option value="monthly">{language === "en" ? "Monthly" : "Mensal"}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{language === "en" ? "Status" : "Status"}</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    disabled={modalMode === "view"}
                  >
                    <option value="scheduled">{language === "en" ? "Scheduled" : "Agendado"}</option>
                    <option value="completed">{language === "en" ? "Completed" : "Completo"}</option>
                    <option value="cancelled">{language === "en" ? "Cancelled" : "Cancelado"}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Campo "Repetir até" - só aparece quando recurrence não é "none" */}
            {formData.recurrence !== "none" && (
              <Row className="mb-3">
                {/* Info sobre última ocorrência da série (só no modo edit) */}
                {modalMode === "edit" && selectedJob && (
                  <Col xs={12} className="mb-3">
                    {(() => {
                      // Buscar o nome do cliente atual para agrupar
                      const currentClient = findClientById(formData.clientId, selectedJob?.clientName);
                      const currentClientName = (currentClient?.name || selectedJob?.clientName || "").toLowerCase().trim();

                      if (!currentClientName) return null;

                      // Buscar todos os jobs recorrentes deste cliente (por nome)
                      const clientJobs = jobs.filter(j => {
                        if (!j.recurrence || j.recurrence === "none") return false;

                        // Pegar nome do cliente do job
                        const jobClient = findClientById(j.clientId, j.clientName);
                        const jobClientName = (jobClient?.name || j.clientName || "").toLowerCase().trim();

                        return jobClientName === currentClientName;
                      });

                      if (clientJobs.length === 0) return null;

                      // Filtrar apenas jobs futuros
                      const hoje = new Date();
                      hoje.setHours(0, 0, 0, 0);
                      const futureJobs = clientJobs.filter(j => {
                        const jobDate = new Date(j.date + "T23:59:59");
                        return jobDate >= hoje;
                      });

                      if (futureJobs.length === 0) return null;

                      // Ordenar e pegar o último
                      const sorted = [...futureJobs].sort((a, b) => b.date.localeCompare(a.date));
                      const ultimoJob = sorted[0];
                      const ultimaDataObj = new Date(ultimoJob.date);
                      const dataFormatada = ultimaDataObj.toLocaleDateString(language === "en" ? "en-NZ" : "pt-BR");

                      return (
                        <Alert variant="info" className="py-2 mb-0">
                          <div className={`${(user?.isAdmin || user?.isClientManager) ? 'mb-2' : 'mb-0'}`} style={{ fontSize: '0.9rem' }}>
                            📅 {language === "en" ? "Last occurrence of series" : "Última ocorrência da série"}: <strong>{dataFormatada}</strong>
                            <span className="text-muted ms-2">({futureJobs.length} {language === "en" ? "future jobs" : "jobs futuros"})</span>
                          </div>
                          {(user?.isAdmin || user?.isClientManager) && (
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span style={{ fontSize: '0.85rem' }}>
                                {language === "en" ? "Extend until:" : "Estender até:"}
                              </span>
                              <Form.Control
                                type="date"
                                size="sm"
                                value={formData.repeatUntilDate}
                                min={ultimoJob.date}
                                onChange={(e) => setFormData({ ...formData, repeatUntilDate: e.target.value, repeatUntil: "date" })}
                                style={{ width: 'auto' }}
                              />
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={extendRecurringSeries}
                                disabled={extendingSeriesLoading || !formData.repeatUntilDate}
                              >
                                {extendingSeriesLoading ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-1" />
                                    {language === "en" ? "Extending..." : "Estendendo..."}
                                  </>
                                ) : (
                                  <>➕ {language === "en" ? "Extend" : "Estender"}</>
                                )}
                              </Button>
                            </div>
                          )}
                        </Alert>
                      );
                    })()}
                  </Col>
                )}
                {/* Preview de quantas ocorrências serão criadas - só no modo create */}
                {formData.date && modalMode === "create" && (
                  <Col xs={12} className="mt-2">
                    <small className="text-muted">
                      {(() => {
                        const ocorrencias = calcularOcorrencias(
                          formData.date,
                          formData.recurrence,
                          formData.repeatUntil,
                          formData.repeatUntilDate
                        );
                        const ultimaData = calcularUltimaData(
                          formData.date,
                          formData.recurrence,
                          formData.repeatUntil,
                          formData.repeatUntilDate
                        );
                        if (ocorrencias > 0 && ultimaData) {
                          const dataFormatada = ultimaData.toLocaleDateString(language === "en" ? "en-NZ" : "pt-BR");
                          return language === "en"
                            ? `📅 Will create ${ocorrencias} occurrences until ${dataFormatada}`
                            : `📅 Criará ${ocorrencias} ocorrências até ${dataFormatada}`;
                        }
                        return language === "en"
                          ? "Select a valid end date"
                          : "Selecione uma data final válida";
                      })()}
                    </small>
                  </Col>
                )}
              </Row>
            )}


            <Form.Group className="mb-3">
              <Form.Label>{language === "en" ? "Notes" : "Observações"}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                disabled={modalMode === "view"}
                readOnly={!user?.isAdmin && !user?.isClientManager}
              />
            </Form.Group>

            {/* Entry Password - Visible to all, editable only for Admin and Manager */}
            <Form.Group className="mb-3">
              <Form.Label>
                🔑 {language === "en" ? "Entry Password / Access Code" : "Senha de Entrada / Código de Acesso"}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={language === "en" ? "e.g., Gate code, door lock code, key location..." : "Ex: Código do portão, senha da fechadura, localização da chave..."}
                readOnly={modalMode === "view" || (!user?.isAdmin && !user?.isClientManager)}
                style={{
                  resize: 'none'
                }}
              />
              <Form.Text className="text-muted">
                {language === "en"
                  ? "Passwords, gate codes, or any access information for this location"
                  : "Senhas, códigos de portão ou qualquer informação de acesso para este local"}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{language === "en" ? "Checklist" : "Checklist"}</Form.Label>

              {modalMode !== "view" && (user?.isAdmin || user?.isClientManager) && (
                <>
                  {/* Template selector - Only for Admin and Manager */}
                  <div className="mb-3">
                    <Form.Label className="small text-muted">
                      {language === "en" ? "Load Template:" : "Carregar Template:"}
                    </Form.Label>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => loadChecklistTemplate("regular")}
                      >
                        {language === "en" ? "Regular Cleaning" : "Limpeza Regular"}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => loadChecklistTemplate("moveout")}
                      >
                        {language === "en" ? "Move Out" : "Mudança"}
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => loadChecklistTemplate("airbnb")}
                      >
                        Air Bnb
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => loadChecklistTemplate("postconstruction")}
                      >
                        {language === "en" ? "Post-Construction" : "Pós-Obra"}
                      </Button>
                    </div>
                    <small className="text-muted d-block mt-1">
                      {language === "en"
                        ? "Select a template to pre-fill the checklist. You can add or remove items after."
                        : "Selecione um template para preencher a checklist. Você pode adicionar ou remover itens depois."}
                    </small>
                  </div>

                </>
              )}
              <ChecklistGrouped
                checklist={formData.checklist}
                onToggle={toggleChecklistItem}
                onRemove={removeChecklistItem}
                onAdd={addChecklistItem}
                language={language}
                isEditable={modalMode !== "view"}
                canManage={modalMode !== "view" && (user?.isAdmin || user?.isClientManager)}
              />
            </Form.Group>

            {/* Photo/Video Upload */}
            <Form.Group className="mb-3">
              <Form.Label>{language === "en" ? "Photos/Videos" : "Fotos/Vídeos"}</Form.Label>
              <Form.Control
                type="file"
                multiple
                accept="image/*,video/*,.heic,.heif"
                disabled={uploading || modalMode === "view"}
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length === 0) return;

                  // Nota: Fotos > 10MB e vídeos > 50MB serão automaticamente comprimidos

                  setUploading(true);
                  setUploadError("");
                  setUploadProgress({ current: 0, total: files.length });

                  const uploaded = [];
                  const failed = [];

                  // Upload em lote com controle de concorrência (3 por vez)
                  const BATCH_SIZE = 3;
                  for (let i = 0; i < files.length; i += BATCH_SIZE) {
                    const batch = files.slice(i, i + BATCH_SIZE);

                    const results = await Promise.allSettled(
                      batch.map(async (file) => {
                        try {
                          const result = await uploadToCloudinary(file);
                          return {
                            success: true,
                            data: {
                              url: result.url,
                              name: result.fileName,
                              type: result.resourceType,
                              uploadedAt: new Date().toISOString(),
                              uploadedBy: user?.displayName || user?.email || 'unknown'
                            }
                          };
                        } catch (err) {
                          console.error(`Erro ao fazer upload de ${file.name}:`, err);
                          return {
                            success: false,
                            fileName: file.name,
                            error: err.message
                          };
                        }
                      })
                    );

                    // Processar resultados do lote
                    results.forEach((result, idx) => {
                      const fileIndex = i + idx;
                      setUploadProgress({ current: fileIndex + 1, total: files.length });

                      if (result.status === 'fulfilled' && result.value.success) {
                        uploaded.push(result.value.data);
                      } else {
                        const fileName = result.value?.fileName || batch[idx]?.name || 'unknown';
                        failed.push(fileName);
                      }
                    });

                    // Pequeno delay entre lotes para não sobrecarregar
                    if (i + BATCH_SIZE < files.length) {
                      await new Promise(resolve => setTimeout(resolve, 500));
                    }
                  }

                  // Mostrar resumo de erros
                  if (failed.length > 0) {
                    const failedList = failed.join(', ');
                    setUploadError(
                      language === "en"
                        ? `❌ Failed to upload ${failed.length} file(s): ${failedList}`
                        : `❌ Falha ao enviar ${failed.length} arquivo(s): ${failedList}`
                    );
                  }

                  // Add uploadedBy and uploadedAt to new photos
                  const uploadedWithMeta = uploaded.map(file => ({
                    ...file,
                    uploadedBy: user?.displayName || user?.email || 'unknown',
                    uploadedAt: new Date().toISOString()
                  }));

                  // Add to existing files instead of replacing
                  const newUploadedPhotos = [...uploadedPhotos, ...uploadedWithMeta];
                  setUploadedPhotos(newUploadedPhotos);
                  setFormData({ ...formData, uploadedFiles: newUploadedPhotos });
                  setUploading(false);
                  setUploadProgress({ current: 0, total: 0 });
                  e.target.value = ''; // Reset input para permitir upload do mesmo arquivo novamente
                }}
              />
              <small className="text-muted d-block mt-2">
                {uploading && uploadProgress.total > 0 && (
                  <span>
                    ⏳ {language === "en" ? "Uploading" : "Enviando"} {uploadProgress.current}/{uploadProgress.total} {language === "en" ? "files..." : "arquivos..."}
                  </span>
                )}
                {uploadError && <span className="text-danger d-block mt-1">{uploadError}</span>}
              </small>
            </Form.Group>

            {/* Upload status and action buttons */}
            {uploading && uploadProgress.total > 0 && (
              <div className="mb-3 p-3 bg-info bg-opacity-10 border border-info rounded">
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">{language === "en" ? "Loading..." : "Carregando..."}</span>
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold">
                      {language === "en" ? "Uploading files..." : "Enviando arquivos..."}
                    </div>
                    <small>
                      {uploadProgress.current} {language === "en" ? "of" : "de"} {uploadProgress.total} {language === "en" ? "completed" : "concluídos"}
                    </small>
                    <div className="progress mt-2" style={{ height: "8px" }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {uploadedPhotos.length > 0 && (
              <div className="mb-3 p-3 bg-success bg-opacity-10 border border-success rounded">
                <div className="mb-2">
                  <strong>
                    {selectedJob?.uploadedFiles?.length > 0 && uploadedPhotos.length > selectedJob.uploadedFiles.length
                      ? `✅ ${uploadedPhotos.length} ${uploadedPhotos.length === 1 ? (language === "en" ? "file" : "arquivo") : (language === "en" ? "files" : "arquivos")} (${uploadedPhotos.length - selectedJob.uploadedFiles.length} ${language === "en" ? "new" : "novos"}):`
                      : selectedJob?.uploadedFiles?.length > 0 && selectedJob.uploadedFiles.length === uploadedPhotos.length
                      ? `📁 ${uploadedPhotos.length} ${language === "en" ? "saved files" : "arquivos salvos"}:`
                      : `✅ ${uploadedPhotos.length} ${language === "en" ? "files ready" : "arquivos prontos"}:`
                    }
                  </strong>
                  <ul className="mb-0 mt-2">
                    {uploadedPhotos.map((file, i) => (
                      <li key={i} style={{ fontSize: "0.9rem" }}>
                        {file.type === "image" ? "🖼️" : "🎥"} {file.name}
                        {i >= (selectedJob?.uploadedFiles?.length || 0) && <span className="text-success ms-1">({language === "en" ? "new" : "novo"})</span>}
                      </li>
                    ))}
                  </ul>
                </div>
                <Button
                  variant="success"
                  size="sm"
                  onClick={async () => {
                    try {
                      const client = clients.find(c => String(c.id) === String(formData.clientId));
                      const hasNewFiles = uploadedPhotos.length > (selectedJob?.uploadedFiles?.length || 0);

                      let jobIdToUse = selectedJob?.id;

                      // If there are new files or it's a new job, save first
                      if (hasNewFiles || modalMode === "create") {
                        // Update formData with current uploadedFiles
                        const updatedFormData = { ...formData, uploadedFiles: uploadedPhotos };

                        const jobData = {
                          ...updatedFormData,
                          updatedAt: Timestamp.now()
                        };

                        // Add user info for uploaded files tracking
                        if (uploadedPhotos.length > 0) {
                          jobData.uid = user.uid;
                          jobData.slug = user.displayName || user.email;
                        }

                        if (modalMode === "create") {
                          // Create new job
                          jobData.createdAt = Timestamp.now();
                          const docRef = await addDoc(collection(db, "jobs"), jobData);
                          jobIdToUse = docRef.id;
                          await reloadJobs();
                        } else {
                          // Update existing job - merge with existing files to prevent race conditions
                          if (uploadedPhotos.length > 0) {
                            jobData.uploadedFiles = await mergeUploadedFiles(selectedJob.id, uploadedPhotos);
                          }
                          await updateDoc(doc(db, "jobs", selectedJob.id), jobData);
                          await reloadJobs();
                        }
                      }

                      // Navigate to gallery
                      const galleryUrl = `/gallery?jobId=${jobIdToUse}&client=${encodeURIComponent(client?.name || "")}&date=${formData.date}`;
                      navigate(galleryUrl);
                    } catch (err) {
                      console.error("Erro ao salvar e navegar:", err);
                      alert(language === "en" ? "Error saving files" : "Erro ao salvar arquivos");
                    }
                  }}
                  className="w-100"
                >
                  📸 {uploadedPhotos.length > (selectedJob?.uploadedFiles?.length || 0)
                    ? (language === "en" ? "Save and View" : "Salvar e Visualizar")
                    : (language === "en" ? "View Files" : "Visualizar Arquivos")}
                </Button>
              </div>
            )}

            {/* Edit Client Restricted Times - Admin/Manager Only */}
            {formData.clientId && (user?.isAdmin || user?.isClientManager) && (
              <div className="mt-4 pt-3 border-top">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">
                    ⏰ {language === "en" ? "Client Time Restrictions" : "Restrições de Horário do Cliente"}
                  </h6>
                  {!editingRestrictedTimes && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => {
                        const currentClient = clients.find(c => String(c.id) === String(formData.clientId));
                        setEditingRestrictedTimes(
                          currentClient?.restrictedTimes || { enabled: false, timeWindows: [] }
                        );
                      }}
                    >
                      {language === "en" ? "Edit" : "Editar"}
                    </Button>
                  )}
                </div>

                {!editingRestrictedTimes ? (
                  /* Display current settings */
                  (() => {
                    const currentClient = clients.find(c => String(c.id) === String(formData.clientId));
                    const restrictedTimes = currentClient?.restrictedTimes;

                    return (
                      <div className="alert alert-info mb-0">
                        {restrictedTimes?.enabled && restrictedTimes?.timeWindows?.length > 0 ? (
                          <>
                            <strong>{language === "en" ? "Allowed times:" : "Horários permitidos:"}</strong>
                            <div className="mt-2">
                              {restrictedTimes.timeWindows.map((window, idx) => (
                                <div key={idx} className="mb-1">
                                  🕐 {window.start} - {window.end}
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted">
                            {language === "en"
                              ? "No time restrictions set"
                              : "Nenhuma restrição de horário definida"}
                          </span>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  /* Edit form */
                  <div className="p-3 bg-light rounded">
                    <Form.Check
                      type="checkbox"
                      label={language === "en" ? "Restrict job times for this client" : "Restringir horários para este cliente"}
                      checked={editingRestrictedTimes.enabled}
                      onChange={e =>
                        setEditingRestrictedTimes({ ...editingRestrictedTimes, enabled: e.target.checked })
                      }
                      className="mb-3"
                    />

                    {editingRestrictedTimes.enabled && (
                      <>
                        <div className="mb-3">
                          <Form.Label className="small fw-bold">
                            {language === "en" ? "Allowed Time Windows" : "Janelas de Horário Permitidas"}
                          </Form.Label>

                          {editingRestrictedTimes.timeWindows?.map((window, idx) => (
                            <Row key={idx} className="mb-2 align-items-center">
                              <Col md={5}>
                                <Form.Control
                                  type="time"
                                  value={window.start}
                                  onChange={e => {
                                    const newWindows = [...editingRestrictedTimes.timeWindows];
                                    newWindows[idx].start = e.target.value;
                                    setEditingRestrictedTimes({
                                      ...editingRestrictedTimes,
                                      timeWindows: newWindows
                                    });
                                  }}
                                  placeholder={language === "en" ? "Start" : "Início"}
                                />
                              </Col>
                              <Col md={5}>
                                <Form.Control
                                  type="time"
                                  value={window.end}
                                  onChange={e => {
                                    const newWindows = [...editingRestrictedTimes.timeWindows];
                                    newWindows[idx].end = e.target.value;
                                    setEditingRestrictedTimes({
                                      ...editingRestrictedTimes,
                                      timeWindows: newWindows
                                    });
                                  }}
                                  placeholder={language === "en" ? "End" : "Fim"}
                                />
                              </Col>
                              <Col md={2}>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => {
                                    const newWindows = editingRestrictedTimes.timeWindows.filter((_, i) => i !== idx);
                                    setEditingRestrictedTimes({
                                      ...editingRestrictedTimes,
                                      timeWindows: newWindows
                                    });
                                  }}
                                >
                                  🗑️
                                </Button>
                              </Col>
                            </Row>
                          ))}

                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              const newWindows = [...(editingRestrictedTimes.timeWindows || []), { start: "", end: "" }];
                              setEditingRestrictedTimes({
                                ...editingRestrictedTimes,
                                timeWindows: newWindows
                              });
                            }}
                            className="mt-2"
                          >
                            ➕ {language === "en" ? "Add Time Window" : "Adicionar Janela de Horário"}
                          </Button>
                        </div>
                      </>
                    )}

                    <div className="d-flex gap-2">
                      <Button variant="primary" size="sm" onClick={handleSaveClientRestrictedTimes}>
                        {language === "en" ? "Save" : "Salvar"}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setEditingRestrictedTimes(null)}
                      >
                        {language === "en" ? "Cancel" : "Cancelar"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="flex-column p-2 gap-2">
            {/* Linha 1: Acoes do job (edit mode) */}
            {modalMode === "edit" && selectedJob && (user?.isAdmin || user?.isClientManager) && (
              <div className="w-100 d-flex gap-1 pb-1 border-bottom flex-wrap">
                <Button variant="outline-danger" size="sm" onClick={handleDelete}>
                  {language === "en" ? "Delete" : "Excluir"}
                </Button>
                {(() => {
                  const client = findClientById(selectedJob.clientId, selectedJob.clientName);
                  return getPaymentMethod(client) === 'xero' && user?.isAdmin && (
                    <Button variant="outline-success" size="sm" onClick={handleOpenInvoiceModal}>
                      💰 Invoice
                    </Button>
                  );
                })()}
                <Button variant="outline-warning" size="sm" onClick={handleInitiateTeamSwitch}>
                  🔄 {language === "en" ? "Team" : "Time"}
                </Button>
                {isMobile && (
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => {
                      setShowModal(false);
                      setMoveJobData({
                        job: selectedJob,
                        newDate: selectedJob.date || "",
                        newStartTime: selectedJob.startTime || "",
                        newEndTime: selectedJob.endTime || ""
                      });
                      setShowMoveJobModal(true);
                    }}
                  >
                    📦 {language === "en" ? "Move" : "Mover"}
                  </Button>
                )}
              </div>
            )}
            {/* Linha 2: Fechar + Salvar */}
            <div className="w-100 d-flex justify-content-between">
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowModal(false);
                }}
              >
                {language === "en" ? "Close" : "Fechar"}
              </Button>
              {(modalMode === "create" || modalMode === "edit") && (
                <Button variant="primary" type="submit" size="sm">
                  {language === "en" ? "Save" : "Salvar"}
                </Button>
              )}
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Team Configuration Modal */}
      <Modal show={showTeamModal} onHide={() => setShowTeamModal(false)} size="lg" data-bs-theme={modalTheme}>
        <Modal.Header closeButton>
          <Modal.Title>
            👥 {language === "en" ? "Configure Weekly Teams" : "Configurar Equipes Semanais"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            {language === "en"
              ? "Select which employees work on each day of the week:"
              : "Selecione quais colaboradoras trabalham em cada dia da semana:"}
          </p>
          {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(day => (
            <div key={day} className="mb-4">
              <h6 className="mb-2">
                {language === "en" ? day :
                  day === "Sunday" ? "Domingo" :
                  day === "Monday" ? "Segunda-feira" :
                  day === "Tuesday" ? "Terça-feira" :
                  day === "Wednesday" ? "Quarta-feira" :
                  day === "Thursday" ? "Quinta-feira" :
                  day === "Friday" ? "Sexta-feira" : "Sábado"}
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {employees.map(emp => {
                  const isSelected = (teamConfig[day] || []).includes(emp.id);
                  return (
                    <Button
                      key={emp.id}
                      variant={isSelected ? "primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => toggleEmployeeInTeam(day, emp.id)}
                    >
                      {isSelected ? "✓ " : ""}{emp.displayName}
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTeamModal(false)}>
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
          <Button variant="primary" onClick={handleSaveTeamConfig}>
            {language === "en" ? "Save Configuration" : "Salvar Configuração"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Daily Team Override Modal */}
      <Modal show={showDailyTeamModal} onHide={() => setShowDailyTeamModal(false)} size="md" data-bs-theme={modalTheme}>
        <Modal.Header closeButton>
          <Modal.Title>
            📅 {language === "en" ? "Team for" : "Equipe para"} {selectedDate && (() => {
              const [year, month, day] = selectedDate.split('-');
              return `${day}/${month}/${year}`;
            })()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedDate && (() => {
            const dayOfWeek = getDayOfWeek(selectedDate);
            const weeklyDefault = (weeklyTeams[activeTeam] || {})[dayOfWeek] || [];
            const currentOverride = (dailyTeamOverrides[activeTeam] || {})[selectedDate] || [];
            const isOverride = currentOverride.length > 0;

            const toggleEmployee = (empId) => {
              setTempDailyTeam(prev =>
                prev.includes(empId)
                  ? prev.filter(id => id !== empId)
                  : [...prev, empId]
              );
            };

            const resolveNames = (uids) => uids.map(uid => {
              if (uid.startsWith("custom:")) return uid.substring(7);
              const emp = employees.find(e => e.id === uid);
              return emp?.displayName?.split(" ")[0] || uid;
            }).filter(Boolean).join(", ");

            const addCustomPerson = () => {
              if (customPersonName.trim()) {
                const customId = `custom:${customPersonName.trim()}`;
                if (!tempDailyTeam.includes(customId)) {
                  setTempDailyTeam(prev => [...prev, customId]);
                  saveCustomMember(customPersonName.trim());
                }
                setCustomPersonName("");
              }
            };

            return (
              <>
                {/* Compact info badge */}
                <div className="mb-3 d-flex align-items-center gap-2">
                  <Badge bg={isOverride ? "warning" : "secondary"} text={isOverride ? "dark" : "light"}>
                    {isOverride
                      ? (language === "en" ? "Custom override" : "Personalização ativa")
                      : (language === "en" ? "Weekly default" : "Padrão semanal")}
                  </Badge>
                  {!isOverride && weeklyDefault.length > 0 && (
                    <small className="text-muted">{resolveNames(weeklyDefault)}</small>
                  )}
                </div>

                {/* Employees */}
                <div className="mb-3">
                  <small className="text-muted fw-bold d-block mb-2">
                    {language === "en" ? "Employees" : "Funcionários"}
                  </small>
                  <div className="d-flex flex-wrap gap-2">
                    {employees.map(emp => {
                      const isSelected = tempDailyTeam.includes(emp.id);
                      return (
                        <Button
                          key={emp.id}
                          variant={isSelected ? "primary" : "outline-secondary"}
                          size="sm"
                          onClick={() => toggleEmployee(emp.id)}
                          style={{ minWidth: '80px' }}
                        >
                          {isSelected ? "✓ " : ""}{emp.displayName}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Custom members */}
                {savedCustomMembers.length > 0 && (
                  <>
                    <hr className="my-2" />
                    <div className="mb-3">
                      <small className="text-muted fw-bold d-block mb-2">
                        {language === "en" ? "External" : "Externos"}
                      </small>
                      <div className="d-flex flex-wrap gap-2">
                        {savedCustomMembers.map(memberName => {
                          const customId = `custom:${memberName}`;
                          const isSelected = tempDailyTeam.includes(customId);
                          return (
                            <div key={customId} className="position-relative">
                              <Button
                                variant={isSelected ? "success" : "outline-secondary"}
                                size="sm"
                                onClick={() => toggleEmployee(customId)}
                                style={{ paddingRight: user?.isAdmin ? '1.5rem' : '0.5rem', minWidth: '80px' }}
                              >
                                {isSelected ? "✓ " : ""}{memberName}
                              </Button>
                              {user?.isAdmin && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(language === "en" ? `Remove "${memberName}"?` : `Remover "${memberName}"?`)) {
                                      removeCustomMember(memberName);
                                    }
                                  }}
                                  style={{
                                    position: 'absolute', right: '2px', top: '2px',
                                    background: 'transparent', border: 'none', color: '#dc3545',
                                    cursor: 'pointer', fontSize: '0.65rem', padding: '0 2px',
                                    fontWeight: 'bold', lineHeight: 1
                                  }}
                                  title={language === "en" ? "Remove" : "Remover"}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}

                {/* Inline add external */}
                <div className="d-flex gap-2 align-items-center mt-3">
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder={language === "en" ? "New member..." : "Novo membro..."}
                    value={customPersonName}
                    onChange={(e) => setCustomPersonName(e.target.value)}
                    onKeyPress={(e) => { if (e.key === "Enter") addCustomPerson(); }}
                    style={{ maxWidth: '200px' }}
                  />
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={addCustomPerson}
                    disabled={!customPersonName.trim()}
                  >
                    +
                  </Button>
                </div>
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <Button variant="secondary" size="sm" onClick={() => setShowDailyTeamModal(false)}>
              {language === "en" ? "Cancel" : "Cancelar"}
            </Button>
            <div className="d-flex gap-2">
              {/* Use Weekly Default - only when override exists */}
              {selectedDate && ((dailyTeamOverrides[activeTeam] || {})[selectedDate] || []).length > 0 && (
                <Button variant="outline-warning" size="sm" onClick={() => handleSaveDailyTeam([])}>
                  {language === "en" ? "Use Weekly Default" : "Usar Padrão Semanal"}
                </Button>
              )}
              {/* New Weekly Default - when team differs from weekly */}
              {selectedDate && (() => {
                const dayOfWeek = getDayOfWeek(selectedDate);
                const weeklyDefault = (weeklyTeams[activeTeam] || {})[dayOfWeek] || [];
                const isDifferent = JSON.stringify([...tempDailyTeam].sort()) !== JSON.stringify([...weeklyDefault].sort());
                return isDifferent ? (
                  <Button variant="outline-info" size="sm" onClick={() => handleUpdateWeeklyDefault(tempDailyTeam)}>
                    📅 {language === "en" ? "New Default" : "Novo Padrão"}
                  </Button>
                ) : null;
              })()}
              <Button variant="primary" size="sm" onClick={() => handleSaveDailyTeam(tempDailyTeam)}>
                {language === "en" ? "Save" : "Salvar"}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Duplicate Employee Warning Modal */}
      <Modal show={showDuplicateWarning} onHide={handleCancelDuplicate} centered data-bs-theme={modalTheme}>
        <Modal.Header closeButton>
          <Modal.Title>
            ⚠️ {language === "en" ? "Duplicate Employee Warning" : "Aviso de Colaborador Duplicado"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            {language === "en"
              ? "The following employee(s) are already assigned to the other team on this day:"
              : "O(s) seguinte(s) colaborador(es) já está(ão) designado(s) para a outra equipe neste dia:"}
          </p>
          <ul className="mb-3">
            {duplicateEmployees.map((name, idx) => (
              <li key={idx}><strong>{name}</strong></li>
            ))}
          </ul>
          <p className="mb-3">
            {language === "en"
              ? "An employee cannot be in multiple teams on the same day. Do you want to continue anyway?"
              : "Um colaborador não pode estar em múltiplos times no mesmo dia. Deseja continuar mesmo assim?"}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDuplicate}>
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
          <Button variant="warning" onClick={handleConfirmDuplicate}>
            {language === "en" ? "Continue Anyway" : "Continuar Mesmo Assim"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Recurrence Edit Modal */}
      <Modal
        show={showRecurrenceModal}
        onHide={() => { setShowRecurrenceModal(false); setPendingEventChange(null); }}
        centered
        animation={false}
        data-bs-theme={modalTheme}
        enforceFocus={false}
        restoreFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            🔁 {language === "en" ? "Edit Recurring Event" : "Editar Evento Recorrente"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            {language === "en"
              ? "This is a recurring event. How would you like to save the changes?"
              : "Este é um evento recorrente. Como você gostaria de salvar as alterações?"}
          </p>
          <div className="d-grid gap-2">
            <Button
              type="button"
              variant="outline-primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRecurrenceChoice("this");
              }}
              className="text-start"
            >
              <strong>{language === "en" ? "This event" : "Este evento"}</strong>
              <br />
              <small className="text-muted">
                {language === "en"
                  ? "Only this occurrence will be changed"
                  : "Apenas esta ocorrência será alterada"}
              </small>
            </Button>
            <Button
              type="button"
              variant="outline-primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRecurrenceChoice("following");
              }}
              className="text-start"
            >
              <strong>{language === "en" ? "This and following events" : "Este e os eventos seguintes"}</strong>
              <br />
              <small className="text-muted">
                {language === "en"
                  ? "This and all future occurrences will be changed"
                  : "Esta e todas as ocorrências futuras serão alteradas"}
              </small>
            </Button>
            <Button
              type="button"
              variant="outline-primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRecurrenceChoice("all");
              }}
              className="text-start"
            >
              <strong>{language === "en" ? "All events" : "Todos os eventos"}</strong>
              <br />
              <small className="text-muted">
                {language === "en"
                  ? "All occurrences to be changed, even the previous"
                  : "Todas as ocorrências serão alteradas, até as anteriores"}
              </small>
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowRecurrenceModal(false);
            setPendingEventChange(null);
          }}>
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Invoice Amount Change Modal */}
      <Modal show={showAmountChangeModal} onHide={() => setShowAmountChangeModal(false)} centered animation={false} data-bs-theme={modalTheme}>
        <Modal.Header closeButton>
          <Modal.Title>
            💰 {language === "en" ? "Update Invoice Amount?" : "Atualizar Valor da Invoice?"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingAmountChange && (
            <>
              <div className="mb-4 p-3 bg-light rounded">
                <div className="mb-2">
                  <strong>{language === "en" ? "Client" : "Cliente"}:</strong> {pendingAmountChange.client.name}
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted">{language === "en" ? "Current default" : "Padrão atual"}</small>
                    <div className="h5 mb-0">${parseFloat(getDefaultPrice(pendingAmountChange.client)).toFixed(2)}</div>
                  </div>
                  <div className="mx-3">→</div>
                  <div>
                    <small className="text-muted">{language === "en" ? "New amount" : "Novo valor"}</small>
                    <div className="h5 mb-0 text-primary">${pendingAmountChange.newAmount.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              <p className="mb-3">
                {language === "en"
                  ? "The invoice amount has changed. How would you like to apply this change?"
                  : "O valor da invoice foi alterado. Como você gostaria de aplicar esta mudança?"}
              </p>

              <div className="d-grid gap-2">
                <Button
                  variant="outline-primary"
                  onClick={handleAmountChangeOnlyThisJob}
                  className="text-start p-3"
                  style={{ borderWidth: '2px' }}
                >
                  <div className="d-flex align-items-start">
                    <div className="me-3" style={{ fontSize: '1.5rem' }}>📄</div>
                    <div>
                      <strong className="d-block mb-1">
                        {language === "en" ? "Only This Job" : "Apenas Este Job"}
                      </strong>
                      <small className="text-muted">
                        {language === "en"
                          ? `Use $${pendingAmountChange.newAmount.toFixed(2)} only for this specific job. The client's default amount ($${parseFloat(getDefaultPrice(pendingAmountChange.client)).toFixed(2)}) will remain unchanged.`
                          : `Usar $${pendingAmountChange.newAmount.toFixed(2)} apenas para este job específico. O valor padrão do cliente ($${parseFloat(getDefaultPrice(pendingAmountChange.client)).toFixed(2)}) permanecerá inalterado.`}
                      </small>
                    </div>
                  </div>
                </Button>

                <Button
                  variant="primary"
                  onClick={handleAmountChangeUpdateDefault}
                  className="text-start p-3"
                  style={{ borderWidth: '2px' }}
                >
                  <div className="d-flex align-items-start">
                    <div className="me-3" style={{ fontSize: '1.5rem' }}>👤</div>
                    <div>
                      <strong className="d-block mb-1">
                        {language === "en" ? "Update Client Default" : "Atualizar Padrão do Cliente"}
                      </strong>
                      <small className="text-white" style={{ opacity: 0.9 }}>
                        {language === "en"
                          ? `Change the client's default amount to $${pendingAmountChange.newAmount.toFixed(2)}. All future jobs will use this new amount by default.`
                          : `Alterar o valor padrão do cliente para $${pendingAmountChange.newAmount.toFixed(2)}. Todos os jobs futuros usarão este novo valor por padrão.`}
                      </small>
                    </div>
                  </div>
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowAmountChangeModal(false);
              setPendingAmountChange(null);
            }}
          >
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Password/Checklist/Notes Change Modal */}
      <Modal
        show={showPasswordChecklistModal}
        onHide={() => setShowPasswordChecklistModal(false)}
        centered
        animation={false}
        data-bs-theme={modalTheme}
        style={{ zIndex: 2000 }}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            🔐 {language === "en" ? "Update Password/Checklist/Notes?" : "Atualizar Senha/Checklist/Notas?"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingPasswordChecklistChange && pendingPasswordChecklistChange.client && (
            <>
              <div className="mb-4 p-3 bg-light rounded">
                <div className="mb-2">
                  <strong>{language === "en" ? "Client" : "Cliente"}:</strong> {pendingPasswordChecklistChange.client.name}
                </div>
                {pendingPasswordChecklistChange.passwordChanged && (
                  <div className="mb-2">
                    <small className="text-muted">🔑 {language === "en" ? "Password changed" : "Senha alterada"}</small>
                  </div>
                )}
                {pendingPasswordChecklistChange.checklistChanged && (
                  <div className="mb-2">
                    <small className="text-muted">✅ {language === "en" ? "Checklist changed" : "Checklist alterado"}</small>
                  </div>
                )}
                {pendingPasswordChecklistChange.notesChanged && (
                  <div>
                    <small className="text-muted">📝 {language === "en" ? "Notes changed" : "Notas alteradas"}</small>
                  </div>
                )}
              </div>

              <p className="mb-3">
                {language === "en"
                  ? "The password, checklist, or notes have changed. How would you like to apply this change?"
                  : "A senha, checklist ou notas foram alterados. Como você gostaria de aplicar esta mudança?"}
              </p>

              <div className="d-grid gap-2">
                <Button
                  variant="outline-primary"
                  onClick={handlePasswordChecklistOnlyThisJob}
                  className="text-start p-3"
                  style={{ borderWidth: '2px' }}
                >
                  <div className="d-flex align-items-start">
                    <div className="me-3" style={{ fontSize: '1.5rem' }}>📄</div>
                    <div>
                      <strong className="d-block mb-1">
                        {language === "en" ? "Only This Job" : "Apenas Este Job"}
                      </strong>
                      <small className="text-muted">
                        {language === "en"
                          ? "Update password/checklist/notes only for this specific job. The client's default data will remain unchanged."
                          : "Atualizar senha/checklist/notas apenas para este job específico. Os dados padrão do cliente permanecerão inalterados."}
                      </small>
                    </div>
                  </div>
                </Button>

                <Button
                  variant="primary"
                  onClick={handlePasswordChecklistUpdateClient}
                  className="text-start p-3"
                  style={{ borderWidth: '2px' }}
                >
                  <div className="d-flex align-items-start">
                    <div className="me-3" style={{ fontSize: '1.5rem' }}>👤</div>
                    <div>
                      <strong className="d-block mb-1">
                        {language === "en" ? "Update Client Database" : "Atualizar Base do Cliente"}
                      </strong>
                      <small className="text-white" style={{ opacity: 0.9 }}>
                        {language === "en"
                          ? "Update the client's default password/checklist/notes. All jobs for this client will be updated with this new data."
                          : "Atualizar a senha/checklist/notas padrão do cliente. Todos os jobs deste cliente serão atualizados com estes novos dados."}
                      </small>
                    </div>
                  </div>
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowPasswordChecklistModal(false);
              setPendingPasswordChecklistChange(null);
            }}
          >
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Invoice Review Modal - Bundle Sending */}
      <Modal
        show={showInvoiceReviewModal}
        onHide={handleCancelReview}
        size="xl"
        centered
        animation={false}
        data-bs-theme={modalTheme}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            📋 {language === "en" ? "Review Invoices Before Sending" : "Revisar Invoices Antes de Enviar"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <p className="text-muted mb-3">
            {language === "en"
              ? "Review and edit the invoice details below before sending to Xero:"
              : "Revise e edite os detalhes das invoices abaixo antes de enviar para Xero:"}
          </p>
          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead className="table-light sticky-top">
                <tr>
                  <th style={{ width: '12%' }}>{language === "en" ? "Client" : "Cliente"}</th>
                  <th style={{ width: '13%' }}>{language === "en" ? "Email" : "Email"}</th>
                  <th style={{ width: '22%' }}>{language === "en" ? "Reference" : "Referência"}</th>
                  <th style={{ width: '18%' }}>{language === "en" ? "Description" : "Descrição"}</th>
                  <th style={{ width: '10%' }}>{language === "en" ? "Amount" : "Valor"}</th>
                  <th style={{ width: '12%' }}>{language === "en" ? "Date" : "Data"}</th>
                  <th style={{ width: '13%' }} className="text-center">{language === "en" ? "Request Feedback" : "Pedir Feedback"}</th>
                </tr>
              </thead>
              <tbody>
                {reviewInvoices.map((invoice, index) => (
                  <tr key={invoice.jobId}>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={invoice.clientName}
                        onChange={(e) => handleReviewInvoiceChange(index, 'clientName', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={invoice.clientEmail}
                        onChange={(e) => handleReviewInvoiceChange(index, 'clientEmail', e.target.value)}
                      />
                    </td>
                    <td>
                      <textarea
                        className="form-control form-control-sm"
                        rows="2"
                        value={invoice.reference}
                        onChange={(e) => handleReviewInvoiceChange(index, 'reference', e.target.value)}
                      />
                    </td>
                    <td>
                      <textarea
                        className="form-control form-control-sm"
                        rows="2"
                        value={invoice.description}
                        onChange={(e) => handleReviewInvoiceChange(index, 'description', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control form-control-sm"
                        value={invoice.amount}
                        onChange={(e) => handleReviewInvoiceChange(index, 'amount', parseFloat(e.target.value) || 0)}
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={invoice.date}
                        onChange={(e) => handleReviewInvoiceChange(index, 'date', e.target.value)}
                      />
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="checkbox"
                        checked={invoice.requestFeedback !== false}
                        onChange={(e) => handleReviewInvoiceChange(index, 'requestFeedback', e.target.checked)}
                        title={language === "en" ? "Send feedback request via WhatsApp after invoice" : "Enviar pedido de feedback via WhatsApp após invoice"}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelReview}>
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmSendInvoices}
            disabled={sendingInvoices}
          >
            {sendingInvoices ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {language === "en" ? "Sending..." : "Enviando..."}
              </>
            ) : (
              <>📤 {language === "en" ? `Send ${reviewInvoices.length} Invoice(s)` : `Enviar ${reviewInvoices.length} Invoice(s)`}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Individual Invoice Modal */}
      <Modal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        size="md"
        centered
        animation={false}
        data-bs-theme={modalTheme}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            💰 {language === "en" ? "Send Invoice to Xero" : "Enviar Invoice para Xero"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedJob && (() => {
            const client = findClientById(selectedJob.clientId, selectedJob.clientName);
            return client && (
              <>
                <div className="mb-4 p-3 bg-light rounded">
                  <h6 className="mb-1">{client.name}</h6>
                  <small className="text-muted">
                    {fnsFormat(new Date(selectedJob.date), "PPP", {
                      locale: language === "pt" ? ptBR : enUS
                    })}
                  </small>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>{language === "en" ? "Description" : "Descrição"}</strong>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={invoiceFormData.description}
                    onChange={(e) => setInvoiceFormData({ ...invoiceFormData, description: e.target.value })}
                    placeholder={language === "en" ? "Invoice description..." : "Descrição da invoice..."}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>{language === "en" ? "Amount ($)" : "Valor ($)"}</strong>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={invoiceFormData.amount}
                    onChange={(e) => setInvoiceFormData({ ...invoiceFormData, amount: parseFloat(e.target.value) || 0 })}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>{language === "en" ? "Reference" : "Referência"}</strong>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={invoiceFormData.reference}
                    onChange={(e) => setInvoiceFormData({ ...invoiceFormData, reference: e.target.value })}
                    placeholder={language === "en" ? "Invoice reference..." : "Referência da invoice..."}
                  />
                  <Form.Text className="text-muted">
                    {language === "en" ? "This will appear on the invoice" : "Isso aparecerá na invoice"}
                  </Form.Text>
                </Form.Group>
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowInvoiceModal(false)}
            disabled={sendingInvoices}
          >
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
          <Button
            variant="success"
            onClick={handleSendIndividualInvoice}
            disabled={sendingInvoices}
          >
            {sendingInvoices ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {language === "en" ? "Sending..." : "Enviando..."}
              </>
            ) : (
              <>📤 {language === "en" ? "Send to Xero" : "Enviar para Xero"}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Restricted Time Warning Modal */}
      <Modal show={showRestrictedTimeModal} onHide={handleCancelRestrictedTime} centered data-bs-theme={modalTheme}>
        <Modal.Header closeButton>
          <Modal.Title>
            ⏰ {language === "en" ? "Time Restriction Warning" : "Aviso de Restrição de Horário"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {restrictedTimeData && (
            <>
              <div className="alert alert-warning mb-3">
                <h6 className="mb-2">
                  <strong>{restrictedTimeData.client.name}</strong>
                </h6>
                <p className="mb-0">
                  {language === "en"
                    ? "This client has time restrictions. Jobs can only be scheduled during these time windows:"
                    : "Este cliente tem restrições de horário. Jobs só podem ser agendados nestas janelas de horário:"}
                </p>
              </div>

              <div className="p-3 rounded mb-3">
                <div className="mb-3">
                  <small className="text-muted fw-bold">{language === "en" ? "Allowed time windows:" : "Janelas de horário permitidas:"}</small>
                  <div className="mt-2">
                    {restrictedTimeData.timeWindows?.map((window, idx) => (
                      <div key={idx} className="mb-1 text-success">
                        ✅ {window.start} - {window.end}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <small className="text-muted fw-bold">{language === "en" ? "Attempted time:" : "Horário tentado:"}</small>
                  <div className="h5 mb-0 text-danger mt-1">
                    ❌ {restrictedTimeData.attemptedTime}
                  </div>
                </div>
              </div>

              <p className="mb-0">
                {language === "en"
                  ? "Do you want to schedule anyway, or go back to change the time?"
                  : "Você quer agendar mesmo assim, ou voltar para mudar o horário?"}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelRestrictedTime}>
            {language === "en" ? "Go Back & Change Time" : "Voltar e Mudar Horário"}
          </Button>
          <Button variant="warning" onClick={handleConfirmRestrictedTime}>
            {language === "en" ? "Schedule Anyway" : "Agendar Mesmo Assim"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Copy Week Modal */}
      <Modal show={showCopyWeekModal} onHide={() => setShowCopyWeekModal(false)} centered data-bs-theme={modalTheme}>
        <Modal.Header closeButton>
          <Modal.Title>
            📅 {language === "en" ? "Copy Week" : "Copiar Semana"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              {language === "en" ? "From Week (select any day)" : "Da Semana (selecione qualquer dia)"}
            </Form.Label>
            <Form.Control
              type="date"
              value={copyWeekData.fromDate}
              onChange={(e) => setCopyWeekData(prev => ({ ...prev, fromDate: e.target.value }))}
            />
            <Form.Text className="text-muted">
              {language === "en"
                ? "All jobs from this week will be copied"
                : "Todos os jobs desta semana serão copiados"}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {language === "en" ? "To Week (select any day)" : "Para a Semana (selecione qualquer dia)"}
            </Form.Label>
            <Form.Control
              type="date"
              value={copyWeekData.toDate}
              onChange={(e) => setCopyWeekData(prev => ({ ...prev, toDate: e.target.value }))}
            />
            <Form.Text className="text-muted">
              {language === "en"
                ? "Jobs will be created in this week"
                : "Jobs serão criados nesta semana"}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCopyWeekModal(false)}>
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
          <Button variant="primary" onClick={handleCopyWeekSubmit} disabled={copyingWeek}>
            {copyingWeek ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {language === "en" ? "Copying..." : "Copiando..."}
              </>
            ) : (
              language === "en" ? "Copy Week" : "Copiar Semana"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* WhatsApp Confirmations Modal - Shows after selecting jobs */}
      <Modal show={showWhatsAppModal} onHide={() => setShowWhatsAppModal(false)} centered data-bs-theme={modalTheme}>
        <Modal.Header closeButton>
          <Modal.Title>
            📤 {language === "en" ? "Send WhatsApp Messages" : "Enviar Mensagens WhatsApp"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <strong>{selectedInvoices.length}</strong> {language === "en" ? "job(s) selected" : "job(s) selecionado(s)"}
          </Alert>

          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="fw-bold mb-0">
                📝 {language === "en" ? "Message Type" : "Tipo de Mensagem"}
              </Form.Label>
              {user?.isAdmin && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setEditingPrompt(null);
                    setShowPromptEditorModal(true);
                  }}
                >
                  ⚙️ {language === "en" ? "Manage Prompts" : "Gerenciar Prompts"}
                </Button>
              )}
            </div>
            <Form.Select
              value={whatsAppData.messageType}
              onChange={(e) => setWhatsAppData(prev => ({ ...prev, messageType: e.target.value }))}
            >
              {whatsAppMessageTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name} {!type.isDefault && '(Custom)'}
                </option>
              ))}
            </Form.Select>
            {whatsAppData.messageType === 'confirmation' && (
              <Form.Text className="text-muted">
                {language === "en"
                  ? "Standard confirmation asking clients to confirm their scheduled appointments."
                  : "Confirmação padrão pedindo aos clientes para confirmar seus agendamentos."}
              </Form.Text>
            )}
            {whatsAppData.messageType === 'end_of_year' && (
              <Form.Text className="text-muted">
                {language === "en"
                  ? "Notifies clients about holiday break and asks about pause/resume preferences."
                  : "Notifica clientes sobre férias e pergunta sobre pausar/retomar serviço."}
              </Form.Text>
            )}
            {whatsAppData.messageType === 'last_minute_cancel' && (
              <Form.Text className="text-muted">
                {language === "en"
                  ? "Offers available slot due to another client's cancellation."
                  : "Oferece horário disponível devido a cancelamento de outro cliente."}
              </Form.Text>
            )}
            {/* Show description for custom prompts */}
            {!['confirmation', 'end_of_year', 'last_minute_cancel'].includes(whatsAppData.messageType) && (
              <Form.Text className="text-muted">
                {language === "en" ? "Custom prompt" : "Prompt customizado"}
              </Form.Text>
            )}
          </Form.Group>

          <Alert variant="warning" className="mb-0">
            <strong>⚠️ {language === "en" ? "Important:" : "Importante:"}</strong><br />
            {language === "en"
              ? "Messages will be sent via WhatsApp and cannot be undone."
              : "As mensagens serão enviadas via WhatsApp e não podem ser desfeitas."}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWhatsAppModal(false)}>
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
          <Button
            variant="success"
            onClick={async () => {
              // Send confirmations from selected jobs
              setSendingWhatsApp(true);
              setShowWhatsAppModal(false);
              try {
                // Get selected jobs
                const selectedJobs = jobs.filter(j => selectedInvoices.includes(j.id));

                // Group by client phone using findClientById (handles legacy formats)
                const clientGroups = {};
                selectedJobs.forEach((job) => {
                  const client = findClientById(job.clientId, job.clientName);
                  if (!client || !client.phone) return;

                  const phone = client.phone;
                  if (!clientGroups[phone]) {
                    clientGroups[phone] = {
                      clientName: client.name,
                      phone: phone,
                      appointments: [],
                    };
                  }

                  clientGroups[phone].appointments.push({
                    date: job.date,
                    time: job.startTime,
                    address: client.address,
                    team: job.team,
                  });
                });

                const clientGroupsArray = Object.values(clientGroups);

                if (clientGroupsArray.length === 0) {
                  alert(language === "en" ? "No clients with valid phone numbers found" : "Nenhum cliente com telefone válido encontrado");
                  return;
                }

                // Send via backend with message type
                console.log(`📞 Sending to backend (messageType: ${whatsAppData.messageType})...`);
                const result = await callXeroFunction('whatsappSendConfirmations', {
                  clientGroups: clientGroupsArray,
                  messageType: whatsAppData.messageType
                });

                // Build message with details
                let message = language === "en"
                  ? `Sent ${result.totalSent || 0} out of ${clientGroupsArray.length} messages!`
                  : `Enviado ${result.totalSent || 0} de ${clientGroupsArray.length} mensagens!`;

                if (result.failedClients && result.failedClients.length > 0) {
                  message += "\n\n" + (language === "en" ? "Failed to send to:" : "Falhou ao enviar para:");
                  message += "\n" + result.failedClients.join("\n");
                }

                alert(message);

                // Exit selection mode and reset
                setSelectionMode(null);
                setSelectedInvoices([]);
                setWhatsAppData({ startDate: "", endDate: "", messageType: "confirmation" });
              } catch (error) {
                console.error('Error sending confirmations:', error);
                alert(language === "en" ? "Error sending confirmations" : "Erro ao enviar confirmações");
              } finally {
                setSendingWhatsApp(false);
              }
            }}
            disabled={sendingWhatsApp}
          >
            {sendingWhatsApp ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {language === "en" ? "Sending..." : "Enviando..."}
              </>
            ) : (
              <>
                📤 {language === "en" ? "Send Messages" : "Enviar Mensagens"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* WhatsApp Prompt Editor Modal */}
      <Modal show={showPromptEditorModal} onHide={() => setShowPromptEditorModal(false)} centered size="lg" data-bs-theme={modalTheme}>
        <Modal.Header closeButton>
          <Modal.Title>
            ⚙️ {language === "en" ? "Manage WhatsApp Prompts" : "Gerenciar Prompts WhatsApp"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Editable default prompts (end_of_year, last_minute_cancel) */}
          <h6 className="mb-3">{language === "en" ? "Editable Default Prompts" : "Prompts Padrão Editáveis"}</h6>
          <div className="mb-3">
            {defaultWhatsAppMessageTypes.filter(p => !p.isDefault).map(prompt => {
              // Check if there's a custom version in PostgreSQL
              const customVersion = activeCustomPrompts.find(c => c.id === prompt.id);
              const displayPrompt = customVersion || prompt;
              return (
                <div key={prompt.id} className="d-flex justify-content-between align-items-center p-2 mb-2" style={{ backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
                  <div>
                    <strong>{prompt.name}</strong>
                    {customVersion && <Badge bg="success" className="ms-2">Editado</Badge>}
                    <br />
                    <small className="text-muted">{displayPrompt.promptText?.substring(0, 100)}...</small>
                  </div>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setEditingPrompt({
                        id: prompt.id,
                        name: prompt.name,
                        promptText: displayPrompt.promptText,
                        mockMessage: displayPrompt.mockMessage || ''
                      })}
                    >
                      ✏️ {language === "en" ? "Edit" : "Editar"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <hr />

          {/* List existing custom prompts */}
          <h6 className="mb-3">{language === "en" ? "Custom Prompts" : "Prompts Customizados"}</h6>

          {activeCustomPrompts.filter(p => !['end_of_year', 'last_minute_cancel'].includes(p.id)).length === 0 ? (
            <Alert variant="info">
              {language === "en"
                ? "No custom prompts yet. Click 'Add New Prompt' to create one."
                : "Nenhum prompt customizado ainda. Clique em 'Adicionar Novo' para criar."}
            </Alert>
          ) : (
            <div className="mb-3">
              {activeCustomPrompts.filter(p => !['end_of_year', 'last_minute_cancel'].includes(p.id)).map(prompt => (
                <div key={prompt.id} className="d-flex justify-content-between align-items-center p-2 mb-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <div>
                    <strong>{prompt.name}</strong>
                    <br />
                    <small className="text-muted">{prompt.promptText?.substring(0, 100)}...</small>
                  </div>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => setEditingPrompt(prompt)}
                    >
                      ✏️ {language === "en" ? "Edit" : "Editar"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-2"
                      title={language === "en" ? "Archive" : "Arquivar"}
                      onClick={async () => {
                        try {
                          await callXeroFunction('archiveWhatsappPrompt', {}, { method: 'POST', id: prompt.id, action: 'archive' });
                          // Reload prompts from PostgreSQL after archiving
                          const result = await callXeroFunction('getWhatsappPrompts', {}, { method: 'GET' });
                          if (result.prompts) {
                            setCustomWhatsAppPrompts(result.prompts);
                          }
                        } catch (err) {
                          alert(language === "en" ? "Error archiving prompt" : "Erro ao arquivar prompt");
                        }
                      }}
                    >
                      📦
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      title={language === "en" ? "Delete" : "Excluir"}
                      onClick={async () => {
                        if (window.confirm(language === "en" ? "Delete this prompt permanently?" : "Excluir este prompt permanentemente?")) {
                          try {
                            await callXeroFunction('deleteWhatsappPrompt', {}, { method: 'DELETE', id: prompt.id });
                            // Reload prompts from PostgreSQL after deleting
                            const result = await callXeroFunction('getWhatsappPrompts', {}, { method: 'GET' });
                            if (result.prompts) {
                              setCustomWhatsAppPrompts(result.prompts);
                            }
                          } catch (err) {
                            alert(language === "en" ? "Error deleting prompt" : "Erro ao excluir prompt");
                          }
                        }
                      }}
                    >
                      🗑️
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Archived Prompts Section */}
          <div className="mt-3">
            <Button
              variant="link"
              size="sm"
              className="p-0 text-muted"
              onClick={async () => {
                if (!showArchivedPrompts) {
                  // Load archived prompts when expanding
                  try {
                    const result = await callXeroFunction('getArchivedWhatsappPrompts', { archived: 'true' }, { method: 'GET' });
                    if (result.prompts) {
                      setArchivedWhatsAppPrompts(result.prompts);
                    }
                  } catch (err) {
                    console.error('Error loading archived prompts:', err);
                  }
                }
                setShowArchivedPrompts(!showArchivedPrompts);
              }}
            >
              {showArchivedPrompts ? '▼' : '▶'} {language === "en" ? "Archived Prompts" : "Prompts Arquivados"}
              {archivedWhatsAppPrompts.length > 0 && ` (${archivedWhatsAppPrompts.length})`}
            </Button>

            {showArchivedPrompts && (
              <div className="mt-2">
                {archivedWhatsAppPrompts.length === 0 ? (
                  <p className="text-muted small mb-0">
                    {language === "en" ? "No archived prompts." : "Nenhum prompt arquivado."}
                  </p>
                ) : (
                  archivedWhatsAppPrompts.map(prompt => (
                    <div key={prompt.id} className="d-flex justify-content-between align-items-center p-2 mb-2" style={{ backgroundColor: '#f5f5f5', borderRadius: '4px', opacity: 0.8 }}>
                      <div>
                        <strong className="text-muted">{prompt.name}</strong>
                        <br />
                        <small className="text-muted">{prompt.promptText?.substring(0, 80)}...</small>
                      </div>
                      <div>
                        <Button
                          variant="outline-success"
                          size="sm"
                          title={language === "en" ? "Restore" : "Restaurar"}
                          onClick={async () => {
                            try {
                              await callXeroFunction('unarchiveWhatsappPrompt', {}, { method: 'POST', id: prompt.id, action: 'unarchive' });
                              // Reload both lists
                              const [activeResult, archivedResult] = await Promise.all([
                                callXeroFunction('getWhatsappPrompts', {}, { method: 'GET' }),
                                callXeroFunction('getArchivedWhatsappPrompts', { archived: 'true' }, { method: 'GET' })
                              ]);
                              if (activeResult.prompts) setCustomWhatsAppPrompts(activeResult.prompts);
                              if (archivedResult.prompts) setArchivedWhatsAppPrompts(archivedResult.prompts);
                            } catch (err) {
                              alert(language === "en" ? "Error restoring prompt" : "Erro ao restaurar prompt");
                            }
                          }}
                        >
                          ↩️ {language === "en" ? "Restore" : "Restaurar"}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <hr />

          {/* Add/Edit form */}
          <h6 className="mb-3">
            {editingPrompt
              ? (language === "en" ? "Edit Prompt" : "Editar Prompt")
              : (language === "en" ? "Add New Prompt" : "Adicionar Novo Prompt")}
          </h6>

          <Form.Group className="mb-3">
            <Form.Label>{language === "en" ? "Prompt Name" : "Nome do Prompt"}</Form.Label>
            <Form.Control
              type="text"
              placeholder={language === "en" ? "e.g., Holiday Greeting" : "ex: Saudação de Férias"}
              value={editingPrompt?.name || ''}
              onChange={(e) => setEditingPrompt(prev => ({ ...prev, name: e.target.value }))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {language === "en" ? "Prompt Text (for AI)" : "Texto do Prompt (para IA)"}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              placeholder={language === "en"
                ? "Write the prompt for AI. Use {firstName} for client name and {appointmentList} for dates/times."
                : "Escreva o prompt para IA. Use {firstName} para nome do cliente e {appointmentList} para datas/horários."}
              value={editingPrompt?.promptText || ''}
              onChange={(e) => setEditingPrompt(prev => ({ ...prev, promptText: e.target.value }))}
            />
            <Form.Text className="text-muted">
              {language === "en"
                ? "Available variables: {firstName}, {appointmentList}, {clientName}"
                : "Variáveis disponíveis: {firstName}, {appointmentList}, {clientName}"}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {language === "en" ? "Fallback Message (optional)" : "Mensagem de Fallback (opcional)"}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder={language === "en"
                ? "Message to use if AI is unavailable. Use same variables."
                : "Mensagem para usar se IA não estiver disponível. Use as mesmas variáveis."}
              value={editingPrompt?.mockMessage || ''}
              onChange={(e) => setEditingPrompt(prev => ({ ...prev, mockMessage: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowPromptEditorModal(false);
            setEditingPrompt(null);
          }}>
            {language === "en" ? "Close" : "Fechar"}
          </Button>
          <Button
            variant="primary"
            disabled={savingPrompt || !editingPrompt?.name || !editingPrompt?.promptText}
            onClick={async () => {
              setSavingPrompt(true);
              try {
                if (editingPrompt?.id && !editingPrompt?.isNew) {
                  // Update existing
                  await callXeroFunction('updateWhatsappPrompt', {
                    name: editingPrompt.name,
                    promptText: editingPrompt.promptText,
                    mockMessage: editingPrompt.mockMessage || ''
                  }, { method: 'PUT', id: editingPrompt.id });
                } else {
                  // Create new
                  await callXeroFunction('createWhatsappPrompt', {
                    name: editingPrompt.name,
                    promptText: editingPrompt.promptText,
                    mockMessage: editingPrompt.mockMessage || ''
                  }, { method: 'POST' });
                }
                // Reload prompts from PostgreSQL after saving
                const result = await callXeroFunction('getWhatsappPrompts', {}, { method: 'GET' });
                if (result.prompts) {
                  setCustomWhatsAppPrompts(result.prompts);
                }
                setEditingPrompt(null);
                alert(language === "en" ? "Prompt saved!" : "Prompt salvo!");
              } catch (err) {
                console.error('Error saving prompt:', err);
                alert(language === "en" ? "Error saving prompt" : "Erro ao salvar prompt");
              } finally {
                setSavingPrompt(false);
              }
            }}
          >
            {savingPrompt ? (
              <Spinner animation="border" size="sm" />
            ) : (
              language === "en" ? "Save Prompt" : "Salvar Prompt"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Plan Full Day Modal */}
      <Modal show={showPlanDayModal} onHide={() => setShowPlanDayModal(false)} size="xl" centered data-bs-theme={modalTheme}>
        <Modal.Header closeButton>
          <Modal.Title>
            ➕ {language === "en" ? "Plan Full Day" : "Planejar Dia Completo"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            {/* Left Column: Configuration */}
            <Col md={6}>
              <h5 className="mb-3">{language === "en" ? "Day Configuration" : "Configuração do Dia"}</h5>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  {language === "en" ? "Date" : "Data"}
                </Form.Label>
                <Form.Control
                  type="date"
                  value={planDayData.date}
                  onChange={(e) => setPlanDayData(prev => ({ ...prev, date: e.target.value }))}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  {language === "en" ? "Start Time" : "Hora de Início"}
                </Form.Label>
                <Form.Control
                  type="time"
                  value={planDayData.startTime}
                  onChange={(e) => setPlanDayData(prev => ({ ...prev, startTime: e.target.value }))}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  {language === "en" ? "Team" : "Time"}
                </Form.Label>
                <Form.Select
                  value={planDayData.team}
                  onChange={(e) => setPlanDayData(prev => ({ ...prev, team: e.target.value }))}
                >
                  <option value="team1">Team 1</option>
                  <option value="team2">Team 2</option>
                  {user?.isAdmin && <option value="team3">Team 3</option>}
                </Form.Select>
              </Form.Group>

              <hr />

              <h5 className="mb-3">{language === "en" ? "Add Clients" : "Adicionar Clientes"}</h5>
              <Form.Group className="mb-3">
                <Form.Label>
                  {language === "en" ? "Search and add clients" : "Buscar e adicionar clientes"}
                </Form.Label>
                <Combobox
                  onChange={handlePlanDayClientSelect}
                  value={null}
                >
                  <div className="position-relative mb-2">
                    <Combobox.Input
                      className="form-control"
                      placeholder={language === "en" ? "Search client..." : "Buscar cliente..."}
                      value={planDayClientQuery}
                      onChange={(e) => setPlanDayClientQuery(e.target.value)}
                      displayValue={() => ""}
                    />
                    <Combobox.Options
                      className="list-group position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                      style={{ zIndex: 1000, maxHeight: "20vh", overflowY: "auto" }}
                    >
                      {clients
                        .filter(c =>
                          !planDayData.clients.find(pc => pc.clientId === c.id) &&
                          c.name.toLowerCase().includes(planDayClientQuery.toLowerCase())
                        )
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(client => (
                          <Combobox.Option
                            key={client.id}
                            value={client}
                            className="list-group-item list-group-item-action cursor-pointer"
                          >
                            {client.name} ({getServiceDuration(client) || "2:00"})
                          </Combobox.Option>
                        ))}
                      {planDayClientQuery.trim() && (
                        <Combobox.Option
                          value={{ id: null }}
                          className="list-group-item list-group-item-action cursor-pointer text-primary fw-bold"
                        >
                          ➕ {language === "en" ? "Add" : "Adicionar"} "{planDayClientQuery}"
                        </Combobox.Option>
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>

                {/* New Client Form */}
                {showPlanDayNewClient && (
                  <div className="p-3 mb-3 bg-light border rounded">
                    <h6 className="mb-3">
                      {language === "en" ? "Add New Client" : "Adicionar Novo Cliente"}
                    </h6>

                    <Form.Group className="mb-2">
                      <Form.Label>{language === "en" ? "Name" : "Nome"} *</Form.Label>
                      <Form.Control
                        type="text"
                        value={planDayNewClient.name}
                        onChange={(e) =>
                          setPlanDayNewClient(n => ({ ...n, name: e.target.value }))
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>{language === "en" ? "Address (Auckland)" : "Endereço (Auckland)"} *</Form.Label>
                      <Form.Control
                        type="text"
                        value={planDayNewClient.address}
                        onChange={(e) =>
                          setPlanDayNewClient(n => ({ ...n, address: e.target.value }))
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>{language === "en" ? "Hours (HH:MM)" : "Horas (HH:MM)"} *</Form.Label>
                      <Form.Control
                        type="text"
                        value={planDayNewClient.hours}
                        onChange={(e) =>
                          setPlanDayNewClient(n => ({ ...n, hours: e.target.value }))
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>{language === "en" ? "Payment Method" : "Método de Pagamento"} *</Form.Label>
                      <Form.Select
                        value={planDayNewClient.paymentMethod}
                        onChange={(e) =>
                          setPlanDayNewClient(n => ({ ...n, paymentMethod: e.target.value }))
                        }
                      >
                        <option value="cash">Cash</option>
                        <option value="xero">Xero</option>
                      </Form.Select>
                    </Form.Group>

                    {planDayNewClient.paymentMethod === "xero" && user?.isAdmin && (
                      <Form.Group className="mb-3">
                        <Form.Label>{language === "en" ? "Default Invoice Amount ($)" : "Valor Padrão da Invoice ($)"}</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={planDayNewClient.defaultInvoiceAmount}
                          onChange={(e) =>
                            setPlanDayNewClient(n => ({ ...n, defaultInvoiceAmount: e.target.value }))
                          }
                        />
                      </Form.Group>
                    )}

                    <div className="text-end">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          setShowPlanDayNewClient(false);
                          setPlanDayNewClient({
                            name: "",
                            address: "",
                            hours: "2:00",
                            paymentMethod: "cash",
                            defaultInvoiceAmount: ""
                          });
                          setPlanDayClientQuery("");
                        }}
                      >
                        {language === "en" ? "Cancel" : "Cancelar"}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSavePlanDayNewClient}
                      >
                        {language === "en" ? "Save Client" : "Salvar Cliente"}
                      </Button>
                    </div>
                  </div>
                )}
              </Form.Group>

              {/* Client List */}
              {planDayData.clients.length > 0 && (
                <div>
                  <h6 className="mb-2">{language === "en" ? "Clients in order:" : "Clientes em ordem:"}</h6>
                  {planDayData.clients.map((client, index) => (
                    <div key={index} className="border rounded p-2 mb-2">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>{index + 1}. {client.clientName}</strong>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveClientFromPlan(index)}
                        >
                          ✕
                        </Button>
                      </div>
                      <div className="small text-muted">
                        {language === "en" ? "Duration" : "Duração"}: {client.duration}h
                      </div>
                      {index < planDayData.clients.length - 1 && (
                        <Form.Group className="mt-2">
                          <Form.Label className="small">
                            {language === "en" ? "Interval to next (minutes)" : "Intervalo até próximo (minutos)"}
                          </Form.Label>
                          <Form.Control
                            type="number"
                            size="sm"
                            min="0"
                            step="5"
                            value={client.intervalAfter}
                            onChange={(e) => handleUpdateClientInterval(index, e.target.value)}
                          />
                        </Form.Group>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Col>

            {/* Right Column: Preview */}
            <Col md={6}>
              <h5 className="mb-3">{language === "en" ? "Preview" : "Pré-visualização"}</h5>

              {planDayData.date && planDayData.startTime && planDayData.clients.length > 0 ? (
                <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="mb-3">
                    <strong>{language === "en" ? "Date:" : "Data:"}</strong> {planDayData.date}
                    <br />
                    <strong>{language === "en" ? "Team:" : "Time:"}</strong> {planDayData.team === "team1" ? "Team 1" : planDayData.team === "team2" ? "Team 2" : "Team 3"}
                  </div>
                  <hr />
                  {calculateJobTimes().map((job, index) => (
                    <div key={index} className="mb-3 p-2 bg-white rounded">
                      <div className="d-flex justify-content-between">
                        <strong>{index + 1}. {job.clientName}</strong>
                        <Badge bg="primary">{job.duration}h</Badge>
                      </div>
                      <div className="small text-muted mt-1">
                        ⏰ {job.startTime} - {job.endTime}
                      </div>
                    </div>
                  ))}
                  <hr />
                  <div className="text-end">
                    <strong>{language === "en" ? "Total jobs:" : "Total de jobs:"}</strong> {planDayData.clients.length}
                  </div>
                </div>
              ) : (
                <div className="text-center text-muted p-5">
                  <p>{language === "en"
                    ? "Add clients to see the preview"
                    : "Adicione clientes para ver a pré-visualização"}</p>
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPlanDayModal(false)}>
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
          <Button
            variant="success"
            onClick={handleCreateDayPlan}
            disabled={creatingDayPlan || planDayData.clients.length === 0}
          >
            {creatingDayPlan ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {language === "en" ? "Creating..." : "Criando..."}
              </>
            ) : (
              <>
                ➕ {language === "en" ? "Create Jobs" : "Criar Jobs"} ({planDayData.clients.length})
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Team Picker Modal - Choose which team to switch to */}
      <Modal
        show={showTeamPickerModal}
        onHide={() => {
          setShowTeamPickerModal(false);
          setPendingTeamSwitch(null);
        }}
        size="md"
        centered
        animation={false}
        data-bs-theme={modalTheme}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            🔄 {language === "en" ? "Select Target Team" : "Selecionar Time de Destino"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingTeamSwitch && (
            <>
              <Alert variant="info" className="mb-3">
                <strong>{language === "en" ? "Client:" : "Cliente:"}</strong> {pendingTeamSwitch.client?.name}
                <br />
                <strong>{language === "en" ? "Current Team:" : "Time Atual:"}</strong>{" "}
                {getTeamName(pendingTeamSwitch.currentTeam)}
              </Alert>

              <p className="mb-3">
                {language === "en"
                  ? "Select the team you want to move this job to:"
                  : "Selecione o time para onde deseja mover este job:"}
              </p>

              <div className="d-grid gap-2">
                {/* Team 1 - Always show if not current team */}
                {pendingTeamSwitch.currentTeam !== "team1" && (
                  <Button
                    variant="outline-primary"
                    onClick={() => handleTeamSelected("team1")}
                    className="p-3"
                  >
                    <strong>Team 1</strong>
                  </Button>
                )}

                {/* Team 2 - Always show if not current team */}
                {pendingTeamSwitch.currentTeam !== "team2" && (
                  <Button
                    variant="outline-success"
                    onClick={() => handleTeamSelected("team2")}
                    className="p-3"
                  >
                    <strong>Team 2</strong>
                  </Button>
                )}

                {/* Team 3 - Only show for admin */}
                {user?.isAdmin && pendingTeamSwitch.currentTeam !== "team3" && (
                  <Button
                    variant="outline-warning"
                    onClick={() => handleTeamSelected("team3")}
                    className="p-3"
                  >
                    <strong>Team 3</strong>
                  </Button>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowTeamPickerModal(false);
              setPendingTeamSwitch(null);
            }}
          >
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Team Switch Modal */}
      <Modal
        show={showTeamSwitchModal}
        onHide={() => setShowTeamSwitchModal(false)}
        size="lg"
        centered
        animation={false}
        data-bs-theme={modalTheme}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            🔄 {language === "en" ? "Switch Team" : "Mudar Time"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {teamSwitchData && (
            <>
              <Alert variant="info">
                <strong>{language === "en" ? "Client:" : "Cliente:"}</strong> {teamSwitchData.client?.name}
                <br />
                <strong>{language === "en" ? "Current Team:" : "Time Atual:"}</strong>{" "}
                {getTeamName(teamSwitchData.currentTeam)}
                <br />
                <strong>{language === "en" ? "Target Team:" : "Time de Destino:"}</strong>{" "}
                {getTeamName(teamSwitchData.targetTeam)}
              </Alert>

              <p className="mb-3">
                {language === "en"
                  ? "Choose how you want to move this client's jobs:"
                  : "Escolha como deseja mover os jobs deste cliente:"}
              </p>

              <div className="d-grid gap-3">
                {/* Option 1: Move single job */}
                <Button
                  variant="outline-primary"
                  onClick={handleMoveSingleJob}
                  className="text-start p-3"
                >
                  <strong>
                    {language === "en" ? "1. Move Only This Job" : "1. Mover Apenas Este Job"}
                  </strong>
                  <br />
                  <small className="text-muted">
                    {language === "en"
                      ? "Move only the selected job to the other team"
                      : "Mover apenas o job selecionado para o outro time"}
                  </small>
                </Button>

                {/* Option 2: Move all jobs this week */}
                <Button
                  variant="outline-primary"
                  onClick={handleMoveWeekJobs}
                  className="text-start p-3"
                >
                  <strong>
                    {language === "en" ? "2. Move All Jobs This Week" : "2. Mover Todos os Jobs Desta Semana"}
                  </strong>
                  <br />
                  <small className="text-muted">
                    {language === "en"
                      ? "Move all jobs for this client in the current week"
                      : "Mover todos os jobs deste cliente na semana atual"}
                  </small>
                </Button>

                {/* Option 3: Move all future jobs */}
                <Button
                  variant="outline-primary"
                  onClick={handleMoveFutureJobs}
                  className="text-start p-3"
                >
                  <strong>
                    {language === "en" ? "3. Move All Future Jobs" : "3. Mover Todos os Jobs Futuros"}
                  </strong>
                  <br />
                  <small className="text-muted">
                    {language === "en"
                      ? "Move all future jobs for this client (from this date forward)"
                      : "Mover todos os jobs futuros deste cliente (a partir desta data)"}
                  </small>
                </Button>

                {/* Option 4: Create alternating pattern */}
                <Button
                  variant="outline-success"
                  onClick={handleCreateAlternatingPattern}
                  className="text-start p-3"
                >
                  <strong>
                    {language === "en" ? "4. Create Alternating Pattern" : "4. Criar Padrão Alternado"}
                  </strong>
                  <br />
                  <small className="text-muted">
                    {language === "en"
                      ? `Week 1: ${getTeamName(teamSwitchData.targetTeam)}, Week 2: ${getTeamName(teamSwitchData.currentTeam)}, and so on...`
                      : `Semana 1: ${getTeamName(teamSwitchData.targetTeam)}, Semana 2: ${getTeamName(teamSwitchData.currentTeam)}, e assim por diante...`}
                  </small>
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTeamSwitchModal(false)}>
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Move Job Modal (Mobile Drag & Drop) */}
      <Modal show={showMoveJobModal} onHide={() => setShowMoveJobModal(false)} size="lg" data-bs-theme={modalTheme}>
        <Modal.Header closeButton>
          <Modal.Title>
            {language === "en" ? "Move Job" : "Mover Job"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {moveJobData.job && (
            <>
              <div className="mb-3 p-3" style={{
                backgroundColor: darkTheme ? '#1e2230' : '#f8fafc',
                borderRadius: '8px',
                border: `1px solid ${darkTheme ? '#334155' : '#e2e8f0'}`
              }}>
                <h5 className="mb-2">
                  {findClientById(moveJobData.job.clientId, moveJobData.job.clientName)?.name || 'No Client'}
                </h5>
                <div className="text-muted">
                  <small>{language === "en" ? "Current Schedule:" : "Horário Atual:"}</small>
                  <br />
                  {moveJobData.job.date} - {moveJobData.job.startTime} até {moveJobData.job.endTime}
                </div>
              </div>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>{language === "en" ? "New Date" : "Nova Data"}</Form.Label>
                  <Form.Control
                    type="date"
                    value={moveJobData.newDate}
                    onChange={(e) => setMoveJobData({ ...moveJobData, newDate: e.target.value })}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{language === "en" ? "Start Time" : "Horário Inicial"}</Form.Label>
                      <Form.Control
                        type="time"
                        value={moveJobData.newStartTime}
                        onChange={(e) => setMoveJobData({ ...moveJobData, newStartTime: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{language === "en" ? "End Time" : "Horário Final"}</Form.Label>
                      <Form.Control
                        type="time"
                        value={moveJobData.newEndTime}
                        onChange={(e) => setMoveJobData({ ...moveJobData, newEndTime: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMoveJobModal(false)}>
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
          <Button variant="primary" onClick={handleMoveJob}>
            {language === "en" ? "Move Job" : "Mover Job"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Calendar Notice Modal */}
      <NoticeModal
        show={showNoticeModal}
        onHide={() => { setShowNoticeModal(false); setSelectedNotice(null); }}
        mode={noticeModalMode}
        noticeData={selectedNotice}
        defaultTeam={activeTeam}
        onSave={handleSaveNotice}
        onDelete={handleDeleteNotice}
        language={language}
        darkTheme={darkTheme}
        modalTheme={modalTheme}
      />

    </Container>
  );
}
